import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import {
  Banknote,
  ShoppingCart,
  Clock,
  Lock,
  Plus,
  Minus,
  ArrowLeft,
  RefreshCw,
  Search,
  Sparkles,
  Info,
  Receipt,
  FileText,
  History,
  Calendar,
  UtensilsCrossed,
  ChefHat,
  Phone,
  ShoppingBag,
  Trash2,
  PauseCircle,
  PlayCircle,
  X,
  Eye,
  Printer,
  XCircle,
  Copy,
  CheckCircle,
  AlertTriangle,
  RotateCcw,
  LayoutGrid,
  List,
  ChevronDown,
} from 'lucide-react';
import apiService from '../services/api';
import { buildCategoryFilterChips, buildProduitCountMaps, normalizeRubriqueNom, rubriqueCountKey, sortProduitsForPos } from '../utils/rubriqueCounts';
import { matchSearchText } from '../utils/normalizeSearchText';
import { buildRubriqueCoverImages, getMenuTilePalette } from '../utils/menuNavVisuals';
import { formatMoney } from '../utils/money';
import { DEVISE_DEFAUT } from '../config/devises';
import { getProductImageUrl } from '../utils/productImage';
import CaissePaymentModal from '../components/caisse/CaissePaymentModal';
import CaisseProductModal from '../components/caisse/CaisseProductModal';
import CaisseOptionsModal from '../components/caisse/CaisseOptionsModal';
import CaissePoidsModal from '../components/caisse/CaissePoidsModal';
import CaissePanierOptions from '../components/caisse/CaissePanierOptions';
import CaisseClientPicker from '../components/caisse/CaisseClientPicker';
import CaisseRecompensePanel from '../components/caisse/CaisseRecompensePanel';
import CaisseRecompenseConfirmDialog from '../components/caisse/CaisseRecompenseConfirmDialog';
import FiscalDocumentsPanel from '../components/caisse/FiscalDocumentsPanel';
import FiscalClosuresPanel from '../components/caisse/FiscalClosuresPanel';
import PosOfflinePanel from '../components/caisse/PosOfflinePanel';
import PosPosteSessionBar from '../components/caisse/PosPosteSessionBar';
import PosCaisseSharedHeader from '../components/caisse/PosCaisseSharedHeader';
import PosOperationalAlertsBanner from '../components/caisse/PosOperationalAlertsBanner';
import CaisseConfirmModal from '../components/caisse/CaisseConfirmModal';
import CaisseDuplicataPreviewModal from '../components/caisse/CaisseDuplicataPreviewModal';
import CaisseCommandeFactureAvoirActions from '../components/caisse/CaisseCommandeFactureAvoirActions.jsx';
import { isFactureTelechargeable } from '../utils/commandeFactureDisponible.js';
import { POS_Z_CLASS } from '../utils/posZLayers';
import { usePosDisplayTimeZone } from '../hooks/usePosDisplayTimeZone';
import { usePosBarcodeScanner } from '../hooks/usePosBarcodeScanner';
import PosBarcodeManualEntry from '../components/caisse/PosBarcodeManualEntry';
import PosRetailStockHint from '../components/caisse/PosRetailStockHint';
import PosRetailReturnPanel from '../components/caisse/PosRetailReturnPanel';
import UnifiedReturnExchangePanel from '../components/caisse/UnifiedReturnExchangePanel';
import {
  buildClotureTiroirBreakdown,
  suggestEspecesCompteesInput,
} from '../utils/caisseSessionClotureDisplay.js';
import {
  aggregateRefundedQtyByLine,
  buildHistoryLineRefundDisplay,
  hasAnySavArticleReturnable,
  quantiteRetournablePourLigne,
} from '../utils/caisseSavRefundQty';
import { messageForBarcodeLookupError } from '../utils/posBarcodeCaisseMessages';
import PosCaisseDeviceEnrollment from '../components/caisse/PosCaisseDeviceEnrollment';
import { usePosCaisseRealtime } from '../hooks/usePosCaisseRealtime';
import { formatDeviceDisplayName, getDeviceFriendlyLabel } from '../utils/posDeviceDisplay';
import { getOrCreateDeviceId } from '../offline/deviceIdentity';
import {
  writePanierDraft,
  clearPanierDraftStorage,
  clearAllLocalPanierDrafts,
  reconcilePanierDraftForSession,
  adoptOrphanDraftIntoSession,
  clearOrphanPanierDraftStorage,
  isRestaurantPanierDraft,
  readPanierDraft,
  readOrphanPanierDraft,
  resolveCaisseTicketStorageKey,
  resetCaisseLocalStateForCommercantSwitch,
  purgeLegacyGlobalCaisseStorage,
  readActiveRetailCounterDraft,
  estimateRetailCounterDraftTotal,
} from '../utils/caisseLocalPanierStorage';
import { usePosImmersive } from '../context/PosImmersiveContext';
import { useCaisseFidelite } from '../hooks/useCaisseFidelite';
import {
  buildTicketFideliteFields,
  buildImmediateVenteFideliteFields,
  resolveCaisseImmediatePayGate,
  resolveCaisseConfirmedRewardPayGate,
  estimateCaissePayableTotal,
  isRewardCatalogueVenteError,
} from '../utils/caisseFidelite';
import { createCaisseIdempotencyKey } from '../utils/caisseIdempotency';
import { executeCaisseSavRefund } from '../utils/caisseSavRefundExecution';
import { validateWebEncaisseUnifiedHandoffTicketLoad } from '../utils/caisseWebEncaisseUnifiedHandoff';
import {
  buildCaisseFideliteDisplay,
} from '../utils/caisseTicketFideliteDisplay';
import {
  hasMenuOptions,
  productNeedsCustomization,
  mergeProduitAvecOptions,
  buildLineKey,
  formatOptionsTicketBlock,
  isVenteAuPoids,
  formatPrixProduitCaisse,
} from '../utils/caisseProduct';
import {
  buildModifierTicketPayload,
  resolveTicketSyncBeforePayGate,
  shouldSyncTicketBeforePay,
  mapTicketProduitToPanierLigne,
  isTicketRevisionConflict,
  resolveTicketRevisionConflictUi,
} from '../utils/caisseTicketSync';
import {
  formatPrepNothingToSendToast,
  formatPrepSendSuccessToast,
} from '../utils/restaurantPrepSendFeedback';
import {
  countPanierLinesForPrepSend,
  isPanierLineSelectedForPrepSend,
  panierLineNeedsKitchenSend,
  panierLinePrepSent,
  prepReleaseIntentFromPanierLine,
} from '../utils/restaurantPrepPanier';
import { isCaisseGuestClient } from '../utils/caisseGuest';
import {
  formatCaisseOpenTicketTitle,
  formatCaisseOpenTicketSubtitle,
  resolveCaisseOpenTicketRestaurantService,
} from '../utils/caisseOpenTicketDisplay';
import { canPosAction, getAdminUserFromStorage } from '../utils/posStaffAccess';
import { readPosHubAccueilFromSearchParams } from '../utils/posCaisseHubNav';
import usePermissions from '../hooks/usePermissions';
import { isU2Ux1StripeMesCommandesFromUnifiedEnabled } from '../utils/unifiedReturnU2Ux1';

function CaisseToast({ toasts, onDismiss }) {
  return (
    <div className={`fixed top-4 right-4 ${POS_Z_CLASS.modalConfirm} flex flex-col gap-2 pointer-events-none`}>
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`pointer-events-auto flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-2xl border backdrop-blur-sm animate-[slideIn_0.3s_ease-out] max-w-sm ${
            t.type === 'success'
              ? 'bg-emerald-600 border-emerald-500 text-white'
              : t.type === 'error'
                ? 'bg-red-600 border-red-500 text-white'
                : 'bg-slate-800 border-slate-700 text-white'
          }`}
        >
          {t.type === 'success' && <CheckCircle className="h-5 w-5 shrink-0" />}
          {t.type === 'error' && <XCircle className="h-5 w-5 shrink-0" />}
          {t.type === 'info' && <Info className="h-5 w-5 shrink-0" />}
          <span className="text-sm font-medium flex-1">{t.message}</span>
          <button type="button" onClick={() => onDismiss(t.id)} className="p-0.5 rounded hover:bg-white/20 shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

const MODES = {
  accueil: 'accueil',
  vendre: 'vendre',
  encaisser: 'encaisser',
  cloture: 'cloture',
  historique: 'historique',
  retourRetail: 'retourRetail',
  retoursUnifies: 'retoursUnifies',
  fiscalClosures: 'fiscalClosures',
  fiscalDocuments: 'fiscalDocuments',
  tresorerie: 'tresorerie',
};

const TYPE_ICONS = {
  COMMANDE: ShoppingCart,
  RENDEZ_VOUS: Calendar,
  RESERVATION_TABLE: UtensilsCrossed,
};

const CAISSE_COMMERCANT_KEY = 'clickorico_caisse_commercant_id';
const CAISSE_CONTACT_KEY = 'clickorico_caisse_contact';
const CAISSE_AFFICHAGE_KEY = 'clickorico_caisse_affichage';

const AFFICHAGE_CATALOGUE = {
  compact: 'compact',
  menu: 'menu',
};

const readAffichageCatalogue = () => {
  try {
    const saved = localStorage.getItem(CAISSE_AFFICHAGE_KEY);
    if (saved === AFFICHAGE_CATALOGUE.menu || saved === AFFICHAGE_CATALOGUE.compact) return saved;
  } catch { /* */ }
  return AFFICHAGE_CATALOGUE.compact;
};

const resolveCommercantId = (searchParams) => {
  const fromUrl = searchParams.get('commercantId');
  if (fromUrl) {
    try {
      sessionStorage.setItem(CAISSE_COMMERCANT_KEY, fromUrl);
    } catch {
      /* ignore */
    }
    return fromUrl;
  }
  try {
    const stored = sessionStorage.getItem(CAISSE_COMMERCANT_KEY);
    if (stored) return stored;
  } catch {
    /* ignore */
  }
  try {
    const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
    const id = user.commercantId || user.commercant?.id || null;
    if (id) {
      try {
        sessionStorage.setItem(CAISSE_COMMERCANT_KEY, id);
      } catch {
        /* ignore */
      }
    }
    return id;
  } catch {
    return null;
  }
};

function serializeLigneProduitPourVente(l) {
  const opts = l.options && Object.keys(l.options).length > 0 ? l.options : null;
  const line = {
    produitId: l.produitId,
    quantite: l.quantite,
    prixUnitaire: Number(l.prixUnitaire),
  };
  if (l.prepSelectedForSend != null || l.prepReleaseIntent != null) {
    line.prepReleaseIntent = prepReleaseIntentFromPanierLine(l);
  }
  const persistedId = l.commandeProduitId || l.id || null;
  if (persistedId) {
    line.id = persistedId;
  }
  if (l.poidsSouhaite != null && l.poidsSouhaite !== '') {
    line.poidsSouhaite = Number(l.poidsSouhaite);
  }
  if (l.typeDecoupe) {
    line.typeDecoupe = l.typeDecoupe;
  }
  if (opts) {
    line.options = opts;
    line.commentaire = formatOptionsTicketBlock(opts);
  }
  return line;
}

function ProductTile({ produit, fmt, qty, onTap, onInfo, loading }) {
  const imageUrl = getProductImageUrl(produit);
  const auPoids = isVenteAuPoids(produit);
  const prixLabel = formatPrixProduitCaisse(produit, fmt);
  const hasPromo =
    !auPoids && produit.prixPromo != null && produit.prixPromo < produit.prix;
  const disabled = produit.indisponible || produit.desactiveParIngredient;
  const avecOptions = hasMenuOptions(produit) || produit.hasOptions === true;
  const stockUi = produit.posStockDisplay;

  return (
    <div
      className={`relative bg-white rounded-2xl border-2 overflow-hidden transition-all duration-200 ${
        disabled
          ? 'opacity-50 border-slate-100'
          : loading
            ? 'opacity-70 border-emerald-300'
            : 'border-slate-100 hover:border-emerald-400 hover:shadow-lg hover:shadow-emerald-500/10 active:scale-[0.98]'
      }`}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={() => onTap(produit)}
        className="w-full text-left disabled:cursor-not-allowed"
      >
        <div className="aspect-square bg-slate-100 relative overflow-hidden">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={produit.nom}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
              <Sparkles className="h-10 w-10 text-slate-300" />
            </div>
          )}
          {qty > 0 && (
            <span className="absolute top-2 right-2 min-w-[1.75rem] h-7 px-1.5 flex items-center justify-center rounded-full bg-emerald-600 text-white text-sm font-bold shadow">
              {qty}
            </span>
          )}
          {hasPromo && (
            <span className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-rose-500 text-white text-[10px] font-bold uppercase">
              Promo
            </span>
          )}
          {auPoids && !disabled && (
            <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-amber-600 text-white text-[10px] font-bold">
              Poids / prix
            </span>
          )}
          {avecOptions && !disabled && !auPoids && (
            <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-violet-600 text-white text-[10px] font-bold">
              Options
            </span>
          )}
          {stockUi?.managed && stockUi?.status !== 'ok' && (
            <span className="absolute bottom-2 right-2 max-w-[85%]">
              <PosRetailStockHint display={stockUi} variant="badge" />
            </span>
          )}
        </div>
        <div className="p-3">
          {produit.categorie && (
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 truncate">
              {produit.sousCategorie
                ? `${produit.categorie} · ${produit.sousCategorie}`
                : produit.categorie}
            </p>
          )}
          <p className="font-semibold text-slate-900 text-sm leading-tight line-clamp-2 min-h-[2.5rem]">
            {produit.nom}
          </p>
          <div className="flex items-baseline gap-1.5 mt-1">
            <span className="text-emerald-600 font-bold">{prixLabel}</span>
            {hasPromo && (
              <span className="text-xs text-slate-400 line-through">{fmt(produit.prix)}</span>
            )}
          </div>
          {stockUi?.managed ? (
            <PosRetailStockHint display={stockUi} className="!mt-0.5" />
          ) : null}
        </div>
      </button>
      {!disabled && onInfo && qty === 0 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onInfo(produit);
          }}
          className="absolute top-2 left-2 p-1.5 rounded-full bg-white/90 shadow text-slate-500 hover:text-slate-800 z-10"
          title="Détail"
        >
          <Info className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

function MenuNavTile({ label, count, onClick, imageUrl, previewImages, paletteIndex = 0 }) {
  const palette = getMenuTilePalette(paletteIndex);
  const thumbUrl = imageUrl || previewImages?.[0] || null;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group flex items-stretch gap-3 w-full text-left rounded-2xl border-2 bg-white px-3 py-[1.125rem] min-h-[8.25rem] transition active:scale-[0.98] hover:bg-slate-50/80 ${palette.border}`}
    >
      <div className="flex-1 min-w-0 flex flex-col justify-center gap-1.5 self-center min-h-[5.25rem] pr-1">
        <p className="font-semibold text-[0.9375rem] leading-5 text-slate-900 line-clamp-2 min-h-[2.5rem]">
          {label}
        </p>
        {count != null && (
          <p className={`text-xs font-semibold leading-4 tabular-nums ${palette.accent}`}>
            {count} produit{count !== 1 ? 's' : ''}
          </p>
        )}
      </div>
      <div className={`relative shrink-0 self-center w-[5.25rem] h-[5.25rem] rounded-xl overflow-hidden ring-1 ring-inset ${palette.border}`}>
        {thumbUrl ? (
          <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${palette.gradient} flex items-center justify-center`}>
            <span className="text-xl font-bold text-white">{label.charAt(0).toUpperCase()}</span>
          </div>
        )}
      </div>
    </button>
  );
}

export default function Caisse() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const commercantId = useMemo(() => resolveCommercantId(searchParams), [searchParams]);
  const { hasPermission: hasCommercantPermission } = usePermissions(commercantId);
  const canOpenMesCommandesFromCaisse = hasCommercantPermission('GESTION_COMMANDES');
  const posClock = usePosDisplayTimeZone();
  const { active: posImmersiveActive } = usePosImmersive();
  const posStaffUser = useMemo(() => getAdminUserFromStorage(), []);
  const posCanSession = canPosAction(posStaffUser, 'caisse:session');
  const posCanSessionForce = canPosAction(posStaffUser, 'caisse:session:force');
  const posCanTresorerie = canPosAction(posStaffUser, 'caisse:tresorerie');
  const posCanRemboursement = canPosAction(posStaffUser, 'caisse:remboursement');
  const posCanFiscalRead = canPosAction(posStaffUser, 'caisse:fiscal:read');
  const posCanFiscalWrite = canPosAction(posStaffUser, 'caisse:fiscal');
  const posServiceSessionId = searchParams.get('posServiceSessionId');
  const posChannelMode = searchParams.get('posChannel') === 'telephone' ? 'telephone' : searchParams.get('posChannel') === 'emporter' ? 'emporter' : null;
  const restaurantReturn = searchParams.get('returnTo') === 'restaurant';
  const restaurantTableLabel = searchParams.get('tableLabel') || '';
  const restaurantNewAddition = searchParams.get('newAddition') === '1';
  const restaurantTicketIdFromUrl = searchParams.get('ticketId');
  const restaurantTableIdFromUrl = searchParams.get('posTableId');
  const [view, setView] = useState(() => {
    try {
      if (readPosHubAccueilFromSearchParams(new URLSearchParams(window.location.search))) {
        return MODES.accueil;
      }
    } catch { /* */ }
    return MODES.accueil;
  });
  /** U2-S1a — ticket POS ouvert depuis fiche Retours unifiés → retour R6 vers U1 */
  const [retailReturnUnifiedHandoff, setRetailReturnUnifiedHandoff] = useState(null);
  const [webEncaisseUnifiedHandoff, setWebEncaisseUnifiedHandoff] = useState(null);
  const webEncaisseHandoffLoadGenRef = useRef(0);
  const [session, setSession] = useState(null);
  const sessionOpenIdRef = useRef(null);
  const caisseViewRef = useRef(MODES.accueil);
  const [sessionStats, setSessionStats] = useState(null);
  const [sessionRecap, setSessionRecap] = useState(null);
  const [posPoste, setPosPoste] = useState(null);
  const [posMultiPostOperational, setPosMultiPostOperational] = useState(false);
  const [posSessionRemoteClosed, setPosSessionRemoteClosed] = useState(false);
  const [posOrphanPanierDraft, setPosOrphanPanierDraft] = useState(null);
  const prevSessionCaisseIdRef = useRef(undefined);
  const prevCommercantIdRef = useRef(null);
  const panierReconciledForSessionRef = useRef(null);
  /** Bloque la persistance sessionStorage tant que le brouillon n'est pas réconcilié avec session.id. */
  const panierStorageReadyForSessionRef = useRef(null);
  /** Menu caisse (posHub=accueil) : ne jamais rouvrir la vente automatiquement. */
  const hubAccueilLandingRef = useRef(false);
  const posLocalDeviceId = getOrCreateDeviceId();
  const [posSinglePosteMode, setPosSinglePosteMode] = useState(true);
  const [posDeviceBlockedCode, setPosDeviceBlockedCode] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [tresorerieMouvements, setTresorerieMouvements] = useState([]);
  const [tresorerieType, setTresorerieType] = useState('AJOUT_MONNAIE');
  const [tresorerieMontant, setTresorerieMontant] = useState('');
  const [tresorerieMotif, setTresorerieMotif] = useState('');
  const [tresorerieBusy, setTresorerieBusy] = useState(false);
  /** L2-T3 — clôtures fiscales (lecture + déclenchement ; pas d'édition des montants) */
  const [fiscalClosures, setFiscalClosures] = useState([]);
  const [fiscalPerpetual, setFiscalPerpetual] = useState(null);
  const [fiscalClosePeriodKey, setFiscalClosePeriodKey] = useState(() => {
    try {
      return new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Paris' });
    } catch {
      return new Date().toISOString().slice(0, 10);
    }
  });
  const [fiscalCloseBusy, setFiscalCloseBusy] = useState(false);
  const [fiscalCloseMsg, setFiscalCloseMsg] = useState('');
  /** L2-T4 — archives fiscales */
  const [fiscalArchives, setFiscalArchives] = useState([]);
  const [fiscalArchiveBusy, setFiscalArchiveBusy] = useState(false);
  const [fiscalArchiveMsg, setFiscalArchiveMsg] = useState('');
  const [codePromo, setCodePromo] = useState('');
  const [promoReduction, setPromoReduction] = useState(0);
  const [promoMessage, setPromoMessage] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoValide, setPromoValide] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  const resetPaiement = () => {
    setPaymentError('');
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [catalogue, setCatalogue] = useState({ produits: [], prestations: [] });
  const [catalogueRubriques, setCatalogueRubriques] = useState([]);
  const [aEncaisser, setAEncaisser] = useState([]);
  const [panier, setPanier] = useState([]);
  const [fondCaisse, setFondCaisse] = useState('');
  const [especesComptees, setEspecesComptees] = useState('');
  const [motifEcartCaisse, setMotifEcartCaisse] = useState('');
  const [forceCloseMotif, setForceCloseMotif] = useState('');
  const [contactNom, setContactNom] = useState(() => {
    try { return sessionStorage.getItem(CAISSE_CONTACT_KEY) || ''; } catch { return ''; }
  });
  /** F2.4b1 — client fidélité identifié (≠ contact libre guest) */
  const [selectedClient, setSelectedClient] = useState(null);
  const [encaissementTarget, setEncaissementTarget] = useState(null);
  const [paymentMode, setPaymentMode] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [devise, setDevise] = useState(DEVISE_DEFAUT);
  const [searchQuery, setSearchQuery] = useState('');
  const [categorieFilter, setCategorieFilter] = useState('TOUS');
  const [sousCategorieFilter, setSousCategorieFilter] = useState('TOUS');
  const [affichageCatalogue, setAffichageCatalogue] = useState(readAffichageCatalogue);
  const [menuShowAll, setMenuShowAll] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [optionsProduct, setOptionsProduct] = useState(null);
  const [poidsProduct, setPoidsProduct] = useState(null);
  const [catalogueTab, setCatalogueTab] = useState('produits');
  const [modulePrestations, setModulePrestations] = useState(false);
  const [modulePosRestaurant, setModulePosRestaurant] = useState(false);
  const [modulePosRestaurantKitchen, setModulePosRestaurantKitchen] = useState(false);
  const [modulePosRestaurantChannels, setModulePosRestaurantChannels] = useState(false);
  const restaurantBootstrapRef = useRef(false);
  const [loadingProductId, setLoadingProductId] = useState(null);
  const [ticketsOuverts, setTicketsOuverts] = useState([]);
  const [activeTicketId, setActiveTicketId] = useState(null);
  /** L1-T4 — révision serveur du ticket actif (concurrency optimiste) */
  const [activeTicketRevision, setActiveTicketRevision] = useState(null);
  /** L1-T6 — règlements déjà confirmés sur le ticket actif */
  const [activeTicketPaiements, setActiveTicketPaiements] = useState([]);
  const [ticketConflict, setTicketConflict] = useState(null);
  /** Restaurant : modifications locales non encore persistées (panier rechargé serveur = false). */
  const [ticketPanierDirty, setTicketPanierDirty] = useState(false);
  /** Panier vendre — client / fidélité et actions secondaires repliés (priorité restaurant). */
  const [cartIdentityExpanded, setCartIdentityExpanded] = useState(false);
  const [cartActionsExpanded, setCartActionsExpanded] = useState(false);
  /** Contrat API — édition lignes / encaissement / annulation (posCapabilities). */
  const [activeTicketCapabilities, setActiveTicketCapabilities] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [ticketFideliteDisplay, setTicketFideliteDisplay] = useState(null);
  const [showTicketDetail, setShowTicketDetail] = useState(false);
  const [fermetureWarning, setFermetureWarning] = useState(null);
  const [operationalHealth, setOperationalHealth] = useState(null);
  const [operationalAlertsDismissed, setOperationalAlertsDismissed] = useState(false);
  const [closeOperationalPreview, setCloseOperationalPreview] = useState(null);
  const [restaurantCloseWarning, setRestaurantCloseWarning] = useState(null);
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);
  /** Conservée pendant retries ; renouvelée seulement après succès */
  const paymentIdempotencyKeyRef = useRef(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [annulMotif, setAnnulMotif] = useState('');
  const [showClotureConfirm, setShowClotureConfirm] = useState(false);
  const [showSavModal, setShowSavModal] = useState(false);
  const [ticketFactureLoading, setTicketFactureLoading] = useState(false);
  const [ticketAvoirLoadingId, setTicketAvoirLoadingId] = useState(null);
  const [duplicataPreview, setDuplicataPreview] = useState(null);
  /** Profil commerçant (en-tête duplicata) — GET /commercants/:id */
  const [commercantProfile, setCommercantProfile] = useState(null);
  const [savTab, setSavTab] = useState('articles');
  const [savLineQty, setSavLineQty] = useState({});
  const [savMontantLibre, setSavMontantLibre] = useState('');
  const [savMotif, setSavMotif] = useState('');
  const [savMethode, setSavMethode] = useState('ESPECES');
  const [savLoading, setSavLoading] = useState(false);

  useEffect(() => {
    if (!readPosHubAccueilFromSearchParams(searchParams)) return;
    hubAccueilLandingRef.current = true;
    setView(MODES.accueil);
    const next = new URLSearchParams(searchParams);
    next.delete('posHub');
    next.delete('posServiceSessionId');
    next.delete('posTableId');
    next.delete('returnTo');
    next.delete('tableLabel');
    next.delete('ticketId');
    next.delete('newAddition');
    next.delete('posChannel');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!commercantId) return;
    const prev = prevCommercantIdRef.current;
    if (prev != null && prev !== commercantId) {
      try {
        resetCaisseLocalStateForCommercantSwitch(sessionStorage, prev, commercantId);
      } catch {
        /* ignore */
      }
      prevSessionCaisseIdRef.current = undefined;
      panierReconciledForSessionRef.current = null;
      panierStorageReadyForSessionRef.current = null;
      setPanier([]);
      setActiveTicketId(null);
      setActiveTicketRevision(null);
      setActiveTicketPaiements([]);
      setPosOrphanPanierDraft(null);
      setSelectedClient(null);
      setContactNom('');
      setEncaissementTarget(null);
      setPaymentMode(null);
    }
    try {
      const ticketKey = resolveCaisseTicketStorageKey(commercantId);
      const storedTicket = sessionStorage.getItem(ticketKey);
      if (prev == null || prev !== commercantId) {
        setActiveTicketId(storedTicket || null);
      }
    } catch {
      /* ignore */
    }
    prevCommercantIdRef.current = commercantId;
  }, [commercantId]);

  useEffect(() => {
    if (loading || !commercantId) return;
    if (!session?.id) {
      panierStorageReadyForSessionRef.current = null;
      if (prevSessionCaisseIdRef.current != null) {
        prevSessionCaisseIdRef.current = null;
        panierReconciledForSessionRef.current = null;
      }
      return;
    }

    const prev = prevSessionCaisseIdRef.current;
    const mustReconcile =
      panierReconciledForSessionRef.current !== session.id ||
      (prev !== undefined && prev !== session.id);

    if (!mustReconcile && prev === session.id) {
      panierStorageReadyForSessionRef.current = session.id;
      return;
    }

    panierStorageReadyForSessionRef.current = null;

    const result = reconcilePanierDraftForSession({
      storage: sessionStorage,
      commercantId,
      sessionCaisseId: session.id,
      previousSessionCaisseId: prev,
    });

    prevSessionCaisseIdRef.current = session.id;
    panierReconciledForSessionRef.current = session.id;
    setPosOrphanPanierDraft(result.orphan);

    if (hubAccueilLandingRef.current) {
      hubAccueilLandingRef.current = false;
      setView(MODES.accueil);
      const hubDraft = readPanierDraft(sessionStorage, commercantId);
      if (!isRestaurantPanierDraft(hubDraft) && result.activeLines.length > 0) {
        setPanier(result.activeLines);
      } else {
        setPanier([]);
      }
    } else if (result.activeLines.length > 0) {
      setPanier(result.activeLines);
      const storedDraft = readPanierDraft(sessionStorage, commercantId);
      const mayAutoOpenVendre =
        !isRestaurantPanierDraft(storedDraft) &&
        !readPosHubAccueilFromSearchParams(searchParams);
      if (mayAutoOpenVendre && caisseViewRef.current !== MODES.vendre) {
        setView(MODES.vendre);
      }
    } else {
      setPanier([]);
      if (result.orphan && caisseViewRef.current === MODES.vendre) {
        setView(MODES.accueil);
      }
      panierStorageReadyForSessionRef.current = null;
      return;
    }

    panierStorageReadyForSessionRef.current = session.id;
  }, [session?.id, commercantId, loading, searchParams]);

  useEffect(() => {
    if (!session?.id || !commercantId) return;
    if (panierStorageReadyForSessionRef.current === session.id) return;
    if (panierReconciledForSessionRef.current !== session.id) return;
    if (panier.length > 0) return;
    panierStorageReadyForSessionRef.current = session.id;
  }, [panier, session?.id, commercantId]);

  useEffect(() => {
    if (!session?.id || !commercantId) return;
    if (panierStorageReadyForSessionRef.current !== session.id) return;
    try {
      const stored = readPanierDraft(sessionStorage, commercantId);
      const orphan = readOrphanPanierDraft(sessionStorage, commercantId);
      if (
        panier.length > 0 &&
        orphan?.lines?.length > 0 &&
        (!stored.lines?.length || stored.sessionCaisseId !== session.id)
      ) {
        return;
      }
      writePanierDraft(sessionStorage, {
        sessionCaisseId: session.id,
        commercantId,
        lines: panier,
        ticketId: activeTicketId,
        saleContext: posServiceSessionId ? 'restaurant' : 'retail',
        restaurantServiceSessionId: posServiceSessionId || null,
      });
    } catch {
      /* ignore */
    }
  }, [panier, session?.id, commercantId, activeTicketId, posServiceSessionId]);

  useEffect(() => {
    if (!commercantId) return;
    try {
      const ticketKey = resolveCaisseTicketStorageKey(commercantId);
      if (activeTicketId) sessionStorage.setItem(ticketKey, activeTicketId);
      else sessionStorage.removeItem(ticketKey);
    } catch { /* */ }
  }, [activeTicketId, commercantId]);

  useEffect(() => {
    try {
      if (contactNom) sessionStorage.setItem(CAISSE_CONTACT_KEY, contactNom);
      else sessionStorage.removeItem(CAISSE_CONTACT_KEY);
    } catch { /* */ }
  }, [contactNom]);

  const showToast = useCallback((message, type = 'success') => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    if (selectedProduct && isVenteAuPoids(selectedProduct)) {
      setPoidsProduct(selectedProduct);
      setSelectedProduct(null);
    }
  }, [selectedProduct]);

  const fmt = useCallback((amount) => formatMoney(amount, devise), [devise]);

  const totalPanier = useMemo(
    () => panier.reduce((s, l) => s + l.prixUnitaire * l.quantite, 0),
    [panier]
  );

  const fidelite = useCaisseFidelite({
    active: view === MODES.vendre && !!session,
    commercantId,
    clientUserId: selectedClient?.id || null,
    sousTotal: totalPanier,
    reductionPromo: Number(promoReduction) || 0,
    ticketId: activeTicketId,
    expectedPosRevision: activeTicketRevision,
    onPosRevisionUpdated: setActiveTicketRevision,
  });

  const panierQtyMap = useMemo(() => {
    const m = {};
    panier.forEach((l) => {
      m[l.produitId] = (m[l.produitId] || 0) + l.quantite;
    });
    return m;
  }, [panier]);

  const posStockDisplayByProduitId = useMemo(() => {
    const m = new Map();
    (catalogue.produits || []).forEach((p) => {
      if (p.id && p.posStockDisplay) m.set(p.id, p.posStockDisplay);
    });
    return m;
  }, [catalogue.produits]);

  const categories = useMemo(() => {
    const ordered = buildCategoryFilterChips(catalogue.produits, catalogueRubriques);
    return ['TOUS', ...ordered];
  }, [catalogue.produits, catalogueRubriques]);

  const produitCountMaps = useMemo(
    () => buildProduitCountMaps(catalogue.produits),
    [catalogue.produits]
  );

  const rubriqueCoverImages = useMemo(
    () => buildRubriqueCoverImages(catalogue.produits),
    [catalogue.produits]
  );

  const categoryMenuItems = useMemo(
    () => categories.filter((cat) => cat !== 'TOUS'),
    [categories]
  );

  const sousCategories = useMemo(() => {
    if (categorieFilter === 'TOUS') return [];
    const cat = catalogueRubriques.find((c) => c.nom === categorieFilter);
    const fromRegistry = (cat?.sousCategories || [])
      .filter((s) => s.actif !== false)
      .map((s) => s.nom);
    if (fromRegistry.length) return fromRegistry;

    const seen = new Map();
    catalogue.produits.forEach((p) => {
      if (rubriqueCountKey(p.categorie) !== rubriqueCountKey(categorieFilter)) return;
      const sub = normalizeRubriqueNom(p.sousCategorie);
      if (sub && !seen.has(rubriqueCountKey(sub))) seen.set(rubriqueCountKey(sub), sub);
    });
    return [...seen.values()].sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
  }, [categorieFilter, catalogueRubriques, catalogue.produits]);

  useEffect(() => {
    setSousCategorieFilter('TOUS');
  }, [categorieFilter]);

  const produitsFiltres = useMemo(() => {
    const q = searchQuery.trim();
    const filtered = catalogue.produits.filter((p) => {
      if (
        categorieFilter !== 'TOUS' &&
        rubriqueCountKey(p.categorie) !== rubriqueCountKey(categorieFilter)
      ) {
        return false;
      }
      if (
        sousCategorieFilter !== 'TOUS' &&
        sousCategorieFilter !== '__ALL__' &&
        rubriqueCountKey(p.sousCategorie) !== rubriqueCountKey(sousCategorieFilter)
      ) {
        return false;
      }
      if (!q) return true;
      return (
        matchSearchText(p.nom, q) ||
        matchSearchText(p.categorie, q) ||
        matchSearchText(p.sousCategorie, q) ||
        matchSearchText(p.description, q)
      );
    });
    return sortProduitsForPos(filtered, catalogueRubriques);
  }, [catalogue.produits, catalogueRubriques, searchQuery, categorieFilter, sousCategorieFilter]);

  const isMenuMode = affichageCatalogue === AFFICHAGE_CATALOGUE.menu;
  const hasSearch = Boolean(searchQuery.trim());
  const showMenuCategories =
    isMenuMode &&
    catalogueTab === 'produits' &&
    !hasSearch &&
    categorieFilter === 'TOUS' &&
    !menuShowAll;
  const showMenuSubcategories =
    isMenuMode &&
    catalogueTab === 'produits' &&
    !hasSearch &&
    categorieFilter !== 'TOUS' &&
    sousCategories.length > 0 &&
    sousCategorieFilter === 'TOUS';

  const setAffichageCataloguePersisted = useCallback((mode) => {
    setAffichageCatalogue(mode);
    try {
      localStorage.setItem(CAISSE_AFFICHAGE_KEY, mode);
    } catch { /* */ }
    if (mode === AFFICHAGE_CATALOGUE.menu) {
      setCategorieFilter('TOUS');
      setSousCategorieFilter('TOUS');
      setMenuShowAll(false);
    }
  }, []);

  const menuNavigateBack = useCallback(() => {
    if (menuShowAll) {
      setMenuShowAll(false);
      return;
    }
    if (sousCategorieFilter !== 'TOUS' && sousCategorieFilter !== '__ALL__') {
      setSousCategorieFilter('TOUS');
      return;
    }
    if (sousCategorieFilter === '__ALL__') {
      setSousCategorieFilter('TOUS');
      return;
    }
    setCategorieFilter('TOUS');
  }, [menuShowAll, sousCategorieFilter]);

  const menuBreadcrumb = useMemo(() => {
    if (!isMenuMode || showMenuCategories || hasSearch) return null;
    if (menuShowAll) return 'Tous les produits';
    const parts = [];
    if (categorieFilter !== 'TOUS') parts.push(categorieFilter);
    if (sousCategorieFilter !== 'TOUS' && sousCategorieFilter !== '__ALL__') {
      parts.push(sousCategorieFilter);
    }
    return parts.length ? parts.join(' › ') : null;
  }, [
    isMenuMode,
    showMenuCategories,
    hasSearch,
    menuShowAll,
    categorieFilter,
    sousCategorieFilter,
  ]);

  const prestationsFiltrees = useMemo(() => {
    const q = searchQuery.trim();
    return (catalogue.prestations || []).filter((p) => {
      if (!q) return true;
      return matchSearchText(p.nom, q);
    });
  }, [catalogue.prestations, searchQuery]);

  const aEncaisserCount = aEncaisser.filter((i) => i.actions?.includes('encaisser')).length;

  const loadOperationalHealth = useCallback(async () => {
    if (!commercantId) return null;
    try {
      const health = await apiService.getCaisseOperationalHealth(commercantId);
      setOperationalHealth(health);
      return health;
    } catch {
      return null;
    }
  }, [commercantId]);

  useEffect(() => {
    sessionOpenIdRef.current = session?.id ?? null;
  }, [session?.id]);

  useEffect(() => {
    caisseViewRef.current = view;
  }, [view]);

  const loadSession = useCallback(async () => {
    if (!commercantId) {
      setError('Aucun commerce sélectionné.');
      setLoading(false);
      return null;
    }
    try {
      const hadOpenSession = Boolean(sessionOpenIdRef.current);
      const wasSelling = caisseViewRef.current === MODES.vendre;
      const data = await apiService.getCaisseSession(commercantId);
      setSession(data.session || null);
      if (data.session?.id) {
        setPosSessionRemoteClosed(false);
      } else if (hadOpenSession && wasSelling) {
        setPosSessionRemoteClosed(true);
      }
      setSessionStats(data.stats || null);
      setSessionRecap(data.recap || null);
      setPosPoste(data.poste || null);
      setPosMultiPostOperational(Boolean(data.multiPostOperational));
      setPosSinglePosteMode(data.singlePosteMode !== false);
      setPosDeviceBlockedCode(null);
      if (data.devise) setDevise(data.devise);
      setError('');
      loadOperationalHealth();
      return data;
    } catch (e) {
      const code = e.code || e.data?.code;
      if (code === 'DEVICE_NOT_ENROLLED' || code === 'POS_DEVICE_REQUIRED') {
        setPosDeviceBlockedCode(code);
      }
      if (e.message?.includes('MODULE_CAISSE') || e.message?.includes('403')) {
        setError(
          'Le module Caisse n\'est pas activé. Cochez « Caisse (POS) » dans Paramètres ou Modules activés.'
        );
      } else if (code !== 'DEVICE_NOT_ENROLLED' && code !== 'POS_DEVICE_REQUIRED') {
        setError(e.message || 'Erreur chargement caisse');
      } else {
        setError('');
      }
      return null;
    }
  }, [commercantId]);

  const syncPosSessionFromServer = useCallback(() => {
    loadSession();
    loadOperationalHealth();
  }, [loadSession, loadOperationalHealth]);

  const handlePosDeviceRevoked = useCallback(() => {
    setPosDeviceBlockedCode('DEVICE_NOT_ENROLLED');
    setPosPoste(null);
    setSession(null);
    setSessionStats(null);
    setSessionRecap(null);
    setPosSessionRemoteClosed(false);
    setError('');
    loadSession().catch(() => null);
  }, [loadSession]);

  usePosCaisseRealtime({
    commercantId,
    posteCaisseId: posPoste?.id,
    enabled: Boolean(commercantId && posPoste?.id),
    onSessionSync: syncPosSessionFromServer,
    onSessionClosedRemotely: () => setPosSessionRemoteClosed(true),
    onDeviceRevoked: handlePosDeviceRevoked,
    onEnrollmentChanged: (payload) => {
      if (
        payload?.action === 'REVOKE' &&
        payload?.deviceId &&
        payload.deviceId === posLocalDeviceId
      ) {
        handlePosDeviceRevoked();
      }
    },
  });

  const loadAEncaisser = useCallback(async () => {
    if (!commercantId) return;
    try {
      const data = await apiService.getCaisseAEncaisser(commercantId);
      setAEncaisser(data.items || []);
      if (data.devise) setDevise(data.devise);
    } catch (e) {
      console.error(e);
    }
  }, [commercantId]);

  const loadTresorerie = useCallback(async () => {
    if (!commercantId || !session?.id) return;
    try {
      const data = await apiService.getCaisseTresorerieHistorique(commercantId, session.id);
      setTresorerieMouvements(data.mouvements || []);
    } catch (e) {
      console.error(e);
      showToast(e.message || 'Impossible de charger la trésorerie', 'error');
    }
  }, [commercantId, session?.id, showToast]);

  const submitTresorerieMouvement = async () => {
    const montant = Number(String(tresorerieMontant).replace(',', '.'));
    if (!Number.isFinite(montant) || montant <= 0) {
      showToast('Montant invalide', 'error');
      return;
    }
    if ((tresorerieMotif || '').trim().length < 3) {
      showToast('Motif requis (3 caractères minimum)', 'error');
      return;
    }
    setTresorerieBusy(true);
    try {
      const idempotencyKey =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `tres-${Date.now()}`;
      const data = await apiService.postCaisseTresorerieMouvement(
        { type: tresorerieType, montant, motif: tresorerieMotif.trim() },
        commercantId,
        idempotencyKey
      );
      if (data.recap) setSessionRecap(data.recap);
      setTresorerieMontant('');
      setTresorerieMotif('');
      await loadTresorerie();
      await loadSession();
      showToast('Mouvement enregistré', 'success');
    } catch (e) {
      showToast(e.message || 'Échec du mouvement', 'error');
    } finally {
      setTresorerieBusy(false);
    }
  };

  const contrepasserTresorerie = async (mouvementId) => {
    const motif = window.prompt('Motif de contrepassation (min. 3 caractères) :');
    if (!motif || motif.trim().length < 3) return;
    setTresorerieBusy(true);
    try {
      const data = await apiService.postCaisseTresorerieContrepasser(
        mouvementId,
        { motif: motif.trim() },
        commercantId
      );
      if (data.recap) setSessionRecap(data.recap);
      await loadTresorerie();
      await loadSession();
      showToast('Contrepassation enregistrée', 'success');
    } catch (e) {
      showToast(e.message || 'Contrepassation impossible', 'error');
    } finally {
      setTresorerieBusy(false);
    }
  };

  const loadHistorique = useCallback(async () => {
    if (!commercantId) return;
    try {
      const data = await apiService.getCaisseHistorique(commercantId);
      setHistorique(data.commandes || []);
    } catch (e) {
      console.error(e);
    }
  }, [commercantId]);

  const loadFiscalClosures = useCallback(async () => {
    if (!commercantId) return;
    try {
      setFiscalCloseMsg('');
      const data = await apiService.listFiscalClosures(commercantId);
      setFiscalClosures(data.closures || []);
      setFiscalPerpetual(data.perpetual || null);
    } catch (e) {
      setFiscalCloseMsg(e.message || 'Clôtures fiscales indisponibles (journal désactivé ?)');
      setFiscalClosures([]);
      setFiscalPerpetual(null);
    }
    try {
      const arch = await apiService.listFiscalArchives(commercantId);
      setFiscalArchives(arch.archives || []);
      setFiscalArchiveMsg('');
    } catch (e) {
      setFiscalArchives([]);
      setFiscalArchiveMsg(e.message || 'Archives fiscales indisponibles');
    }
  }, [commercantId]);

  const runGenerateFiscalArchive = useCallback(async () => {
    if (!fiscalClosePeriodKey || !/^\d{4}-\d{2}-\d{2}$/.test(fiscalClosePeriodKey)) {
      setFiscalArchiveMsg('Date YYYY-MM-DD requise pour archiver le jour');
      return;
    }
    setFiscalArchiveBusy(true);
    setFiscalArchiveMsg('');
    try {
      const data = await apiService.generateFiscalArchive(
        { periodType: 'DAILY', periodKey: fiscalClosePeriodKey, commercantId },
        { idempotencyKey: `ui-archive-daily-${commercantId}-${fiscalClosePeriodKey}` }
      );
      const a = data.archive;
      setFiscalArchiveMsg(
        `${data.replayed ? 'Archive existante' : 'Archive générée'} — ${a?.status} · complétude ${a?.completenessStatus}`
      );
      await loadFiscalClosures();
    } catch (e) {
      setFiscalArchiveMsg(e.message || 'Échec génération archive');
    } finally {
      setFiscalArchiveBusy(false);
    }
  }, [fiscalClosePeriodKey, commercantId, loadFiscalClosures]);

  const runCloseFiscalDaily = useCallback(async () => {
    if (!fiscalClosePeriodKey || !/^\d{4}-\d{2}-\d{2}$/.test(fiscalClosePeriodKey)) {
      setFiscalCloseMsg('Date YYYY-MM-DD requise');
      return;
    }
    setFiscalCloseBusy(true);
    setFiscalCloseMsg('');
    try {
      const data = await apiService.closeFiscalDaily(
        { periodKey: fiscalClosePeriodKey, commercantId },
        { idempotencyKey: `ui-daily-${commercantId}-${fiscalClosePeriodKey}` }
      );
      const c = data.closure;
      setFiscalCloseMsg(
        data.replayed
          ? `Clôture ${c?.periodKey} déjà enregistrée (idempotent)`
          : `Clôture ${c?.periodKey} FINAL — CA ${(Number(c?.revenueCents || 0) / 100).toFixed(2)} €`
      );
      await loadFiscalClosures();
    } catch (e) {
      setFiscalCloseMsg(e.message || 'Échec clôture fiscale');
    } finally {
      setFiscalCloseBusy(false);
    }
  }, [fiscalClosePeriodKey, commercantId, loadFiscalClosures]);

  const resetPromo = () => {
    setCodePromo('');
    setPromoReduction(0);
    setPromoMessage('');
    setPromoValide(false);
  };

  const handleVerifierPromo = async () => {
    if (!codePromo.trim()) return;
    setPromoLoading(true);
    try {
      const base = encaissementTarget?.montant ?? totalPanier;
      const res = await apiService.verifierPromoCaisse(
        codePromo.trim(),
        base,
        commercantId
      );
      if (res.valide) {
        setPromoReduction(res.reduction || 0);
        setPromoValide(true);
        setPromoMessage(`Réduction appliquée : −${fmt(res.reduction)}`);
        setPaymentError('');
      } else {
        setPromoReduction(0);
        setPromoValide(false);
        setPromoMessage(res.message || 'Code invalide');
      }
    } catch (e) {
      setPromoReduction(0);
      setPromoValide(false);
      setPromoMessage(e.message || 'Code invalide');
    } finally {
      setPromoLoading(false);
    }
  };

  const loadCatalogue = useCallback(async () => {
    if (!commercantId) return;
    try {
      const [data, rubTree] = await Promise.all([
        apiService.getCaisseCatalogue(commercantId),
        apiService.getCatalogueRubriques(commercantId, false).catch(() => ({ categories: [] })),
      ]);
      const produits = Array.isArray(data?.produits) ? data.produits : [];
      setCatalogue({
        produits,
        prestations: data?.prestations ?? [],
      });
      setCatalogueRubriques(rubTree?.categories || []);
      setModulePrestations(Boolean(data?.modulePrestations));
      if (produits.length === 0 && !data?.prestations?.length && data?.moduleProduits === false) {
        setError('Le module Produits n\'est pas activé pour ce commerce.');
      }
    } catch (e) {
      console.error('[Caisse] catalogue:', e);
      setError(e.message || 'Impossible de charger le catalogue');
      setCatalogue({ produits: [], prestations: [] });
      setCatalogueRubriques([]);
    }
  }, [commercantId]);

  const reprendreBrouillonPanierOrphelin = useCallback(() => {
    if (!session?.id || !commercantId || !posOrphanPanierDraft?.lines?.length) return;
    const ticketIdOrphelin = posOrphanPanierDraft.ticketId || null;
    const lines = adoptOrphanDraftIntoSession(sessionStorage, {
      sessionCaisseId: session.id,
      commercantId,
    });
    if (!lines?.length) return;
    setPosOrphanPanierDraft(null);
    setPanier(lines);
    setActiveTicketId(ticketIdOrphelin);
    setView(MODES.vendre);
    loadCatalogue();
    showToast('Brouillon de panier repris pour cette session', 'success');
  }, [session?.id, commercantId, posOrphanPanierDraft, loadCatalogue, showToast]);

  const abandonnerBrouillonPanierOrphelin = useCallback(() => {
    setConfirmModal({
      title: 'Abandonner le brouillon ?',
      message:
        'Ce panier provient d’une session précédente. L’abandon est définitif sur cet appareil.',
      variant: 'danger',
      confirmLabel: 'Abandonner le brouillon',
      onConfirm: () => {
        setConfirmModal(null);
        try {
          clearOrphanPanierDraftStorage(sessionStorage, commercantId);
        } catch {
          /* ignore */
        }
        setPosOrphanPanierDraft(null);
        showToast('Brouillon abandonné', 'success');
      },
    });
  }, [commercantId, showToast]);

  useEffect(() => {
    if (commercantId && !searchParams.get('commercantId')) {
      setSearchParams({ commercantId }, { replace: true });
    }
  }, [commercantId, searchParams, setSearchParams]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadSession();
      setLoading(false);
    })();
  }, [loadSession]);

  useEffect(() => {
    if (!commercantId) {
      setModulePosRestaurant(false);
      setModulePosRestaurantKitchen(false);
      setModulePosRestaurantChannels(false);
      setCommercantProfile(null);
      return;
    }
    (async () => {
      try {
        const com = await apiService.getCommercant(commercantId);
        setCommercantProfile(com || null);
        setModulePosRestaurant(Boolean(com?.modulePosRestaurant && com?.moduleCaisse));
        setModulePosRestaurantKitchen(
          Boolean(com?.modulePosRestaurantKitchen && com?.modulePosRestaurant && com?.moduleCaisse)
        );
        setModulePosRestaurantChannels(
          Boolean(
            com?.modulePosRestaurantChannels &&
              com?.modulePosRestaurant &&
              com?.moduleCaisse
          )
        );
      } catch {
        setCommercantProfile(null);
        setModulePosRestaurant(false);
        setModulePosRestaurantKitchen(false);
        setModulePosRestaurantChannels(false);
      }
    })();
  }, [commercantId]);

  /** Ventes canal emporter / téléphone : ouvrir la vente sans « Nouvelle vente » (qui purge posChannel). */
  useEffect(() => {
    if (loading || !session?.id || !commercantId) return;
    if (posServiceSessionId || restaurantReturn) return;
    if (!posChannelMode) return;
    if (readPosHubAccueilFromSearchParams(searchParams)) return;
    if (hubAccueilLandingRef.current) return;
    if (view !== MODES.accueil) return;
    loadCatalogue();
    setView(MODES.vendre);
  }, [
    loading,
    session?.id,
    commercantId,
    posServiceSessionId,
    restaurantReturn,
    posChannelMode,
    searchParams,
    view,
    loadCatalogue,
  ]);

  const restaurantPlanReturnUrl = useCallback(() => {
    const params = new URLSearchParams({ commercantId });
    return `/caisse/restaurant?${params.toString()}`;
  }, [commercantId]);

  const restaurantTableReturnUrl = useCallback(() => {
    const params = new URLSearchParams({ commercantId });
    if (restaurantTableIdFromUrl) params.set('focusTableId', restaurantTableIdFromUrl);
    return `/caisse/restaurant?${params.toString()}`;
  }, [commercantId, restaurantTableIdFromUrl]);

  const clearRetailCounterSaleState = useCallback(() => {
    setPanier([]);
    setActiveTicketId(null);
    setActiveTicketRevision(null);
    setActiveTicketPaiements([]);
    setTicketConflict(null);
    setTicketPanierDirty(false);
    setSelectedClient(null);
    fidelite.clearAll();
    setContactNom('');
    resetPromo();
    try {
      if (session?.id && commercantId) {
        writePanierDraft(sessionStorage, {
          sessionCaisseId: session.id,
          commercantId,
          lines: [],
          ticketId: null,
          saleContext: 'retail',
          restaurantServiceSessionId: null,
        });
      } else {
        clearPanierDraftStorage(sessionStorage, commercantId);
      }
      if (commercantId) {
        sessionStorage.removeItem(resolveCaisseTicketStorageKey(commercantId));
      }
      purgeLegacyGlobalCaisseStorage(sessionStorage);
    } catch {
      /* ignore */
    }
  }, [session?.id, commercantId, fidelite, resetPromo]);

  const resumeRetailCounterSale = useCallback(() => {
    if (!session?.id || !commercantId) return;
    const draft =
      readActiveRetailCounterDraft(sessionStorage, {
        commercantId,
        sessionCaisseId: session.id,
      }) ||
      (panier.length > 0 && !posServiceSessionId
        ? { lines: panier, ticketId: activeTicketId }
        : null);
    if (draft?.lines?.length) {
      setPanier(draft.lines);
      setActiveTicketId(draft.ticketId || null);
    }
    loadCatalogue();
    setView(MODES.vendre);
  }, [
    session?.id,
    commercantId,
    panier,
    posServiceSessionId,
    activeTicketId,
    loadCatalogue,
  ]);

  const retailCounterDraftOnAccueil = useMemo(() => {
    if (view !== MODES.accueil || !session?.id || !commercantId || posServiceSessionId) {
      return null;
    }
    const fromStorage = readActiveRetailCounterDraft(sessionStorage, {
      commercantId,
      sessionCaisseId: session.id,
    });
    if (fromStorage?.lines?.length) return fromStorage;
    if (panier.length > 0 && session?.id) {
      const stored = readPanierDraft(sessionStorage, commercantId);
      const orphan = readOrphanPanierDraft(sessionStorage, commercantId);
      const stalePanierAfterOrphanQuarantine =
        orphan?.lines?.length > 0 &&
        (!stored.lines?.length || stored.sessionCaisseId !== session.id);
      if (stalePanierAfterOrphanQuarantine) {
        return null;
      }
      const storageAlignedWithSession =
        !stored.lines?.length ||
        (stored.sessionCaisseId === session.id && !isRestaurantPanierDraft(stored));
      if (storageAlignedWithSession) {
        return {
          lines: panier,
          ticketId: activeTicketId,
          totalEstimate: estimateRetailCounterDraftTotal(panier),
        };
      }
    }
    return null;
  }, [view, session?.id, commercantId, posServiceSessionId, panier, activeTicketId]);

  const beginRetailCounterSale = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('posServiceSessionId');
    next.delete('posTableId');
    next.delete('returnTo');
    next.delete('tableLabel');
    next.delete('ticketId');
    next.delete('newAddition');
    next.delete('posChannel');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }

    const existing =
      retailCounterDraftOnAccueil ||
      (session?.id && commercantId
        ? readActiveRetailCounterDraft(sessionStorage, {
            commercantId,
            sessionCaisseId: session.id,
          })
        : null);

    if (existing?.lines?.length) {
      const n = existing.lines.length;
      const total = existing.totalEstimate ?? estimateRetailCounterDraftTotal(existing.lines);
      setConfirmModal({
        title: 'Vente comptoir en cours',
        message: `Une vente non terminée contient ${n} article${n > 1 ? 's' : ''}${
          total > 0 ? ` (environ ${total.toFixed(2).replace('.', ',')} €)` : ''
        }. Que souhaitez-vous faire ?`,
        variant: 'danger',
        confirmLabel: 'Abandonner et recommencer',
        cancelLabel: 'Reprendre la vente en cours',
        testIdCancel: 'caisse-confirm-resume-sale',
        onConfirm: () => {
          setConfirmModal(null);
          clearRetailCounterSaleState();
          loadCatalogue();
          setView(MODES.vendre);
        },
        onCancel: () => {
          setConfirmModal(null);
          resumeRetailCounterSale();
        },
      });
      return;
    }

    clearRetailCounterSaleState();
    loadCatalogue();
    setView(MODES.vendre);
  }, [
    searchParams,
    setSearchParams,
    session?.id,
    commercantId,
    retailCounterDraftOnAccueil,
    clearRetailCounterSaleState,
    resumeRetailCounterSale,
    loadCatalogue,
  ]);

  const leaveVendreView = useCallback(
    (onLeave) => {
      const unsavedRestaurant =
        posServiceSessionId &&
        panier.length > 0 &&
        (ticketPanierDirty || !activeTicketId);
      if (unsavedRestaurant) {
        setConfirmModal({
          title: 'Commande non enregistrée',
          message:
            'Des articles sont encore dans le panier. Quitter sans enregistrer la commande ?',
          variant: 'danger',
          confirmLabel: 'Quitter sans enregistrer',
          onConfirm: () => {
            setConfirmModal(null);
            setPanier([]);
            setActiveTicketId(null);
            setActiveTicketRevision(null);
            setActiveTicketPaiements([]);
            setTicketPanierDirty(false);
            try {
              clearAllLocalPanierDrafts(sessionStorage, commercantId);
              if (commercantId) {
                sessionStorage.removeItem(resolveCaisseTicketStorageKey(commercantId));
              }
              purgeLegacyGlobalCaisseStorage(sessionStorage);
            } catch {
              /* ignore */
            }
            onLeave();
          },
        });
        return;
      }
      onLeave();
    },
    [posServiceSessionId, panier.length, ticketPanierDirty, activeTicketId, commercantId]
  );

  const sendActiveCommandeToKitchen = useCallback(
    async (
      commandeId,
      { silentNothing = false, quiet = false, sendMode = null, commandeProduitIds = null } = {}
    ) => {
      if (!commandeId || !modulePosRestaurantKitchen) return null;
      const idempotencyKey = createCaisseIdempotencyKey(`rest-prep-${commandeId}`);
      try {
        const outcome = await apiService.sendKitchenCommandePrep(commandeId, {
          idempotencyKey,
          commercantId,
          sendMode,
          commandeProduitIds,
        });
        if (!quiet) {
          const msg = formatPrepSendSuccessToast(outcome);
          if (msg) {
            showToast(msg);
          } else if (!silentNothing && !outcome?.replay) {
            showToast('Aucune ligne envoyée — vérifiez la réponse serveur', 'error');
          } else if (outcome?.replay) {
            showToast(formatPrepSendSuccessToast(outcome));
          }
        }
        return outcome;
      } catch (e) {
        if (e?.code === 'PREP_NOTHING_TO_SEND') {
          if (!silentNothing && !quiet) {
            showToast(formatPrepNothingToSendToast(e), 'error');
          }
          throw e;
        }
        if (!quiet) {
          if (e?.code === 'PREP_PRODUCT_NO_STATION') {
            showToast(e.message || 'Produit sans poste cuisine', 'error');
          } else if (e?.code === 'MODULE_POS_RESTAURANT_KITCHEN_DISABLED') {
            showToast('Module cuisine inactif', 'error');
          } else {
            showToast(e.message || 'Envoi cuisine impossible', 'error');
          }
        }
        throw e;
      }
    },
    [commercantId, modulePosRestaurantKitchen, showToast]
  );

  const loadTicketsOuverts = useCallback(async () => {
    try {
      const data = await apiService.getTicketsOuverts(commercantId);
      setTicketsOuverts(data?.tickets || []);
    } catch { setTicketsOuverts([]); }
  }, [commercantId]);

  useEffect(() => {
    if (session && view === MODES.accueil) {
      loadAEncaisser();
      loadTicketsOuverts();
    }
  }, [session, view, loadAEncaisser, loadTicketsOuverts]);

  useEffect(() => {
    if (session && view === MODES.vendre && commercantId) {
      loadCatalogue();
    }
  }, [session, view, commercantId, loadCatalogue]);

  useEffect(() => {
    if (session && view === MODES.historique) {
      loadHistorique();
    }
    if (session && view === MODES.tresorerie) {
      loadTresorerie();
    }
  }, [session, view, loadHistorique]);

  const lineFromProduit = (produit, extra = {}) => {
    const options = extra.options || {};
    const lineKey =
      extra.poidsSouhaite != null
        ? `${produit.id}::poids::${extra.poidsSouhaite}::${extra.typeDecoupe || ''}::${buildLineKey(produit.id, options)}`
        : buildLineKey(produit.id, options);
    return {
      lineKey,
      lineType: 'produit',
      produitId: produit.id,
      nom: produit.nom,
      prixUnitaire: extra.prixUnitaire ?? produit.prixPromo ?? produit.prix,
      quantite: extra.quantite ?? 1,
      options,
      optionsLabel: extra.optionsLabel || '',
      poidsSouhaite: extra.poidsSouhaite,
      typeDecoupe: extra.typeDecoupe,
      imageUrl: getProductImageUrl(produit),
      categorie: produit.categorie,
      requiresPreparation: produit.requiresPreparation,
      preparationStationId: produit.preparationStationId,
      prepSelectedForSend: true,
    };
  };

  const lineFromPrestation = (prestation, extra = {}) => ({
    lineKey: `prestation::${prestation.id}`,
    lineType: 'prestation',
    prestationId: prestation.id,
    nom: prestation.nom,
    prixUnitaire: extra.prixUnitaire ?? prestation.prix,
    quantite: extra.quantite ?? 1,
    options: {},
    optionsLabel: '',
    imageUrl: prestation.images?.[0] || null,
    categorie: 'Prestation',
  });

  const ajouterPrestationAuPanier = (prestation, extra = {}) => {
    const line = lineFromPrestation(prestation, extra);
    setPanier((prev) => {
      const idx = prev.findIndex((p) => p.lineKey === line.lineKey);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantite: next[idx].quantite + (extra.quantite ?? 1) };
        return next;
      }
      return [...prev, line];
    });
  };

  const ajouterAuPanier = (produit, extra = {}) => {
    const line = lineFromProduit(produit, extra);
    setPanier((prev) => {
      const idx = prev.findIndex((p) => p.lineKey === line.lineKey);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          quantite: next[idx].quantite + (extra.quantite ?? 1),
        };
        return next;
      }
      return [...prev, line];
    });
    setTicketPanierDirty(true);
  };

  const modifierQuantite = (lineKey, delta) => {
    setPanier((prev) =>
      prev
        .map((l) =>
          l.lineKey === lineKey ? { ...l, quantite: Math.max(0, l.quantite + delta) } : l
        )
        .filter((l) => l.quantite > 0)
    );
    setTicketPanierDirty(true);
  };

  const chargerProduitCaisse = async (produit) => {
    const loaders = [
      () => apiService.getCaisseProduit(produit.id, commercantId),
      () => apiService.getProduitOptions(produit.id),
      () =>
        apiService.request(`/produits/${produit.id}?_=${Date.now()}`, {
          headers: { 'X-Caisse-POS': '1' },
        }),
    ];

    let merged = produit;
    for (const load of loaders) {
      try {
        const res = await load();
        const raw = res?.produit ?? res;
        if (raw?.id) {
          merged = mergeProduitAvecOptions(merged, raw);
        }
      } catch (err) {
        console.warn('[Caisse] chargement produit:', err?.message || err);
      }
    }

    return merged;
  };

  const handleProductTap = async (produit) => {
    if (produit.indisponible || produit.desactiveParIngredient) return;

    if (hasMenuOptions(produit)) {
      setOptionsProduct(produit);
    }

    setLoadingProductId(produit.id);
    try {
      const data = await chargerProduitCaisse(produit);

      setOptionsProduct(null);
      setSelectedProduct(null);

      if (isVenteAuPoids(data)) {
        setPoidsProduct(data);
        return;
      }

      if (hasMenuOptions(data)) {
        setOptionsProduct(data);
        return;
      }

      if (productNeedsCustomization(data)) {
        setSelectedProduct(data);
        return;
      }

      ajouterAuPanier(produit);
    } catch (e) {
      setError(e.message || 'Impossible de charger le produit');
      if (!hasMenuOptions(produit)) {
        ajouterAuPanier(produit);
      }
    } finally {
      setLoadingProductId(null);
    }
  };

  const barcodeScanBusyRef = useRef(false);

  const applyBarcodeLookupResult = useCallback(
    async (lookup) => {
      let produit = lookup?.produit;
      if (!produit?.id) return;
      if (lookup?.posStockDisplay && !produit.posStockDisplay) {
        produit = { ...produit, posStockDisplay: lookup.posStockDisplay };
      }
      if (lookup.addition?.options) {
        ajouterAuPanier(produit, {
          options: lookup.addition.options,
          optionsLabel: lookup.addition.optionsLabel,
          prixUnitaire: lookup.addition.prixUnitaire,
        });
        showToast(`${produit.nom} ajouté`, 'success');
        return;
      }
      setLoadingProductId(produit.id);
      try {
        const data = await chargerProduitCaisse(produit);
        if (isVenteAuPoids(data)) {
          showToast('Produit au poids — saisie manuelle requise', 'error');
          setPoidsProduct(data);
          return;
        }
        if (hasMenuOptions(data) || productNeedsCustomization(data)) {
          showToast('Produit avec options — touchez la fiche ou complétez les options', 'error');
          setOptionsProduct(data);
          return;
        }
        ajouterAuPanier(data);
        showToast(`${data.nom} ajouté`, 'success');
      } finally {
        setLoadingProductId(null);
      }
    },
    [ajouterAuPanier, chargerProduitCaisse, showToast]
  );

  const processRetailGtinLookup = useCallback(
    async (gtin) => {
      if (barcodeScanBusyRef.current || processing || !commercantId) return false;
      barcodeScanBusyRef.current = true;
      try {
        const lookup = await apiService.lookupCaisseBarcode(gtin, commercantId);
        await applyBarcodeLookupResult(lookup);
        return true;
      } catch (e) {
        showToast(messageForBarcodeLookupError(e), 'error');
        return false;
      } finally {
        barcodeScanBusyRef.current = false;
      }
    },
    [applyBarcodeLookupResult, commercantId, processing, showToast]
  );

  const handleRetailGtinScan = useCallback(
    (gtin) => {
      void processRetailGtinLookup(gtin);
    },
    [processRetailGtinLookup]
  );

  const handleOuvrirSession = async () => {
    setProcessing(true);
    try {
      const data = await apiService.ouvrirCaisseSession(Number(fondCaisse) || 0, commercantId);
      setSession(data.session);
      setView(MODES.accueil);
    } catch (e) {
      setError(e.message || 'Impossible d\'ouvrir la caisse');
    } finally {
      setProcessing(false);
    }
  };

  /** L2-T2 — émission serveur d'addition (snapshot + journal). Pas une prévisualisation locale. */
  const emettreAddition = async () => {
    if (!activeTicketId || activeTicketRevision == null) {
      showToast('Ouvrez ou reprenez un ticket avant d\'émettre une addition', 'error');
      return;
    }
    setProcessing(true);
    try {
      const res = await apiService.emitTicketAddition(
        activeTicketId,
        { expectedPosRevision: activeTicketRevision, commercantId },
        {
          idempotencyKey: `ui-addition-${activeTicketId}-${activeTicketRevision}`,
          commercantId,
        }
      );
      if (res?.ticket?.posRevision != null) {
        setActiveTicketRevision(Number(res.ticket.posRevision));
      }
      showToast(
        res?.document?.numero
          ? `Addition émise ${res.document.numero}`
          : 'Addition émise'
      );
    } catch (e) {
      if (e?.code === 'FISCAL_JOURNAL_DISABLED' || e?.status === 503) {
        showToast('Émission fiscale désactivée sur cet environnement', 'error');
      } else if (e?.code === 'TICKET_REVISION_CONFLICT') {
        showToast('Ticket modifié ailleurs — actualisez puis réessayez', 'error');
      } else {
        showToast(e.message || 'Émission addition impossible', 'error');
      }
    } finally {
      setProcessing(false);
    }
  };

  const persistRestaurantPanier = async ({ navigateBackAfterCreate = false } = {}) => {
    /** Après persistance réussie : panier local vidé ; retour plan si validation explicite. */
    const finalizeRestaurantPersist = async ({ commandeId, revision, returnToTable }) => {
      setPanier([]);
      setTicketConflict(null);
      setTicketPanierDirty(false);
      await loadTicketsOuverts();
      await loadSession();
      if (returnToTable && restaurantReturn) {
        setActiveTicketId(null);
        setActiveTicketRevision(null);
        setActiveTicketPaiements([]);
        setContactNom('');
        setSelectedClient(null);
        fidelite.clearAll();
        resetPromo();
        navigate(restaurantTableReturnUrl());
      } else if (commandeId) {
        setActiveTicketId(commandeId);
        if (revision != null) {
          setActiveTicketRevision(revision);
        }
      }
      return commandeId ? { commandeId, revision } : null;
    };

    if (panier.length === 0) {
      if (activeTicketId && navigateBackAfterCreate && restaurantReturn) {
        return finalizeRestaurantPersist({
          commandeId: activeTicketId,
          revision: activeTicketRevision,
          returnToTable: true,
        });
      }
      if (activeTicketId) {
        return { commandeId: activeTicketId, revision: activeTicketRevision };
      }
      return null;
    }

    const fideliteFields = buildTicketFideliteFields({
      clientUserId: selectedClient?.id || null,
      confirmedRewardId: fidelite.confirmedRewardId,
    });
    const payload = {
      commercantId,
      produits: panier
        .filter((l) => l.lineType !== 'prestation')
        .map(serializeLigneProduitPourVente),
      prestations: panier
        .filter((l) => l.lineType === 'prestation')
        .map((l) => ({ prestationId: l.prestationId, quantite: l.quantite, prixUnitaire: l.prixUnitaire })),
      contactNom: contactNom || undefined,
      contactTelephone: selectedClient?.telephone || undefined,
      encaisserImmediatement: false,
      ...fideliteFields,
    };

    if (activeTicketId) {
      const syncPayload = {
        commercantId,
        ...buildModifierTicketPayload(
          panier,
          serializeLigneProduitPourVente,
          activeTicketRevision
        ),
      };
      if (!syncPayload.produits.length) {
        showToast('Ajoutez au moins un produit', 'error');
        return null;
      }
      if (syncPayload.expectedPosRevision == null) {
        showToast('Reprenez l\'addition avant d\'enregistrer', 'error');
        return null;
      }
      const syncRes = await apiService.modifierTicket(activeTicketId, syncPayload);
      const serverTicket = syncRes?.ticket;
      if (!serverTicket?.id) {
        throw new Error('Enregistrement refusé — ticket serveur absent');
      }
      let revision = activeTicketRevision;
      if (serverTicket.posRevision != null) {
        revision = Number(serverTicket.posRevision);
      }
      return finalizeRestaurantPersist({
        commandeId: serverTicket.id,
        revision,
        returnToTable: navigateBackAfterCreate,
      });
    }

    const idempotencyKey = createCaisseIdempotencyKey('rest-addition-save');
    const res = await apiService.createRestaurantServiceAddition(
      posServiceSessionId,
      payload,
      { idempotencyKey }
    );
    const ticket = res?.ticket || res?.commande;
    const commandeId = ticket?.id || null;
    let revision = activeTicketRevision;
    if (ticket?.posRevision != null) {
      revision = Number(ticket.posRevision);
    }
    return finalizeRestaurantPersist({
      commandeId,
      revision,
      returnToTable: navigateBackAfterCreate,
    });
  };

  const enregistrerCommandeRestaurant = async () => {
    if (panier.length === 0) return;
    setProcessing(true);
    try {
      const wasExistingAddition = Boolean(activeTicketId);
      const saved = await persistRestaurantPanier({ navigateBackAfterCreate: true });
      if (!saved?.commandeId) return;
      showToast(
        wasExistingAddition
          ? 'Commande validée sur l\'addition'
          : 'Addition validée pour le service'
      );
    } catch (e) {
      if (isTicketRevisionConflict(e)) {
        handleTicketRevisionConflict(e, { context: 'enregistrement' });
      } else if (e?.code === 'TICKET_HAS_PAYMENTS' || e?.status === 409) {
        showToast(
          e.message ||
            'Cette addition a déjà un paiement — créez une nouvelle addition depuis le plan de salle',
          'error'
        );
      } else {
        setError(e.message || 'Enregistrement impossible');
      }
    } finally {
      setProcessing(false);
    }
  };

  const prepSendSelectionCount = useMemo(
    () => countPanierLinesForPrepSend(panier, { selectedOnly: true }),
    [panier]
  );
  const prepSendAllPendingCount = useMemo(
    () => countPanierLinesForPrepSend(panier, { allPending: true }),
    [panier]
  );

  const panierArticleCount = useMemo(
    () => panier.reduce((sum, l) => sum + l.quantite, 0),
    [panier]
  );

  const cartIdentitySummary = useMemo(() => {
    if (selectedClient) {
      const label = [selectedClient.prenom, selectedClient.nom].filter(Boolean).join(' ');
      return label || selectedClient.email || 'Client identifié';
    }
    if (contactNom.trim()) return contactNom.trim();
    if (fidelite.confirmedRewardId || fidelite.selectedRewardId) return 'Récompense fidélité en cours';
    if (fidelite.showPanel && fidelite.solde != null) return `Fidélité · ${fidelite.solde} pts`;
    return 'Client / fidélité (optionnel)';
  }, [
    selectedClient,
    contactNom,
    fidelite.confirmedRewardId,
    fidelite.selectedRewardId,
    fidelite.showPanel,
    fidelite.solde,
  ]);

  const handleStartEncaissementPanier = useCallback(() => {
    const payGate = resolveCaisseImmediatePayGate({
      confirmedRewardId: fidelite.confirmedRewardId,
      activeTicketId,
    });
    if (payGate.action === 'block') {
      showToast(payGate.message, 'error');
      return;
    }
    const rewardGate = resolveCaisseConfirmedRewardPayGate({
      confirmedRewardId: fidelite.confirmedRewardId,
      quote: fidelite.quote,
      quoteLoading: fidelite.quoteLoading,
      quoteError: fidelite.quoteError,
    });
    if (rewardGate.action === 'block') {
      showToast(rewardGate.message, 'error');
      return;
    }
    resetPaiement();
    resetPromo();
    setPaymentMode('vente');
  }, [
    activeTicketId,
    fidelite.confirmedRewardId,
    fidelite.quote,
    fidelite.quoteError,
    fidelite.quoteLoading,
    resetPaiement,
    resetPromo,
    showToast,
  ]);

  useEffect(() => {
    if (posServiceSessionId) {
      setCartIdentityExpanded(false);
      setCartActionsExpanded(false);
    }
  }, [posServiceSessionId]);

  const togglePanierPrepSelection = useCallback((lineKey) => {
    setPanier((prev) =>
      prev.map((l) => {
        if (l.lineKey !== lineKey) return l;
        if (!panierLineNeedsKitchenSend(l) || panierLinePrepSent(l)) return l;
        const selected = l.prepSelectedForSend !== false;
        return { ...l, prepSelectedForSend: !selected };
      })
    );
    setTicketPanierDirty(true);
  }, []);

  const resolveSelectionIdsAfterPersist = (ticket) =>
    (ticket?.produits || [])
      .filter((p) => {
        if (p.prepReleaseIntent === 'DEFER') return false;
        const sent = p.posPrepLine && p.posPrepLine.status !== 'CANCELLED';
        if (sent) return false;
        if (p.produit?.requiresPreparation === false) return false;
        if (!p.produit?.preparationStationId) return false;
        return true;
      })
      .map((p) => p.id);

  const runRestaurantPrepSend = async ({ sendMode }) => {
    if (panier.length === 0 && !activeTicketId) {
      showToast('Composez ou reprenez une commande', 'error');
      return;
    }
    setProcessing(true);
    const hadPanier = panier.length > 0;
    try {
      const saved = await persistRestaurantPanier({ navigateBackAfterCreate: false });
      const commandeId = saved?.commandeId || activeTicketId;
      if (!commandeId) return;

      let commandeProduitIds = null;
      if (sendMode === 'SELECTION') {
        const detail = await apiService.getTicketDetail(commandeId, commercantId);
        commandeProduitIds = resolveSelectionIdsAfterPersist(detail?.ticket);
        if (!commandeProduitIds.length) {
          showToast('Aucune ligne sélectionnée à envoyer', 'error');
          return;
        }
      }

      const outcome = await sendActiveCommandeToKitchen(commandeId, {
        silentNothing: true,
        quiet: true,
        sendMode,
        commandeProduitIds,
      });
      const prepMsg = formatPrepSendSuccessToast(outcome);
      if (prepMsg) {
        showToast(
          hadPanier ? `Commande enregistrée — ${prepMsg.charAt(0).toLowerCase()}${prepMsg.slice(1)}` : prepMsg
        );
      } else {
        showToast('Enregistrement OK mais aucune ligne envoyée — vérifiez la cuisine', 'error');
        return;
      }
      if (restaurantReturn) {
        setPanier([]);
        setActiveTicketId(null);
        setActiveTicketRevision(null);
        setActiveTicketPaiements([]);
        setTicketPanierDirty(false);
        navigate(restaurantPlanReturnUrl(), { state: { restaurantPlanOnly: true } });
      } else if (commandeId) {
        try {
          const detail = await apiService.getTicketDetail(commandeId, commercantId);
          if (detail?.ticket) appliquerTicketServeur(detail.ticket);
        } catch {
          /* ignore */
        }
      }
    } catch (e) {
      if (isTicketRevisionConflict(e)) {
        handleTicketRevisionConflict(e, { context: 'enregistrement' });
      } else if (e?.code === 'TICKET_HAS_PAYMENTS' || e?.status === 409) {
        showToast(e.message || 'Enregistrement impossible', 'error');
      } else if (e?.code === 'PREP_NOTHING_TO_SEND') {
        const nothingMsg = formatPrepNothingToSendToast(e);
        showToast(
          hadPanier
            ? `Commande enregistrée — ${nothingMsg.charAt(0).toLowerCase()}${nothingMsg.slice(1)}`
            : nothingMsg,
          'error'
        );
      } else {
        showToast(e.message || 'Envoi en préparation impossible', 'error');
      }
    } finally {
      setProcessing(false);
    }
  };

  const envoyerSelectionPreparationRestaurant = () => {
    if (prepSendSelectionCount === 0) {
      showToast('Sélectionnez au moins un produit à envoyer', 'error');
      return;
    }
    return runRestaurantPrepSend({ sendMode: 'SELECTION' });
  };

  const envoyerToutPreparationRestaurant = () => {
    if (prepSendAllPendingCount === 0) {
      showToast('Aucun produit en attente d\'envoi cuisine', 'error');
      return;
    }
    return runRestaurantPrepSend({ sendMode: 'ALL_REMAINING' });
  };

  const mettreEnAttente = async () => {
    if (panier.length === 0) return;
    if (posServiceSessionId) {
      await enregistrerCommandeRestaurant();
      return;
    }
    setProcessing(true);
    try {
      const fideliteFields = buildTicketFideliteFields({
        clientUserId: selectedClient?.id || null,
        confirmedRewardId: fidelite.confirmedRewardId,
      });
      const payload = {
        commercantId,
        produits: panier
          .filter((l) => l.lineType !== 'prestation')
          .map(serializeLigneProduitPourVente),
        prestations: panier
          .filter((l) => l.lineType === 'prestation')
          .map((l) => ({ prestationId: l.prestationId, quantite: l.quantite, prixUnitaire: l.prixUnitaire })),
        contactNom: contactNom || undefined,
        contactTelephone: selectedClient?.telephone || undefined,
        encaisserImmediatement: false,
        ...(posChannelMode ? { modeRetrait: posChannelMode } : {}),
        ...fideliteFields,
      };
      await apiService.creerTicket(payload);
      setActiveTicketId(null);
      setActiveTicketRevision(null);
      setActiveTicketPaiements([]);
      setPanier([]);
      setTicketConflict(null);
      setContactNom('');
      setSelectedClient(null);
      fidelite.clearAll();
      resetPromo();
      await loadTicketsOuverts();
      await loadSession();
      const toastMsg = posChannelMode
        ? `Commande ${posChannelMode === 'telephone' ? 'téléphone' : 'à emporter'} enregistrée`
        : fideliteFields.rewardId
          ? 'Ticket mis en attente (intention récompense enregistrée)'
          : 'Ticket mis en attente';
      showToast(toastMsg);
      setView(MODES.accueil);
    } catch (e) {
      setError(e.message || 'Erreur mise en attente');
    } finally {
      setProcessing(false);
    }
  };

  const filterPaiementsPositifs = useCallback(
    (paiements) =>
      (paiements || []).filter(
        (p) =>
          p.statutPaiement === 'VALIDE' &&
          Number(p.montant) > 0 &&
          p.typePaiement !== 'REMBOURSEMENT'
      ),
    []
  );

  const appliquerTicketServeur = useCallback(
    (ticket, posCapabilities = null) => {
      const lignes = (ticket.produits || []).map((lp) => mapTicketProduitToPanierLigne(lp));
      setPanier(lignes);
      setActiveTicketId(ticket.id);
      setActiveTicketRevision(
        ticket.posRevision != null ? Number(ticket.posRevision) : 1
      );
      setActiveTicketPaiements(filterPaiementsPositifs(ticket.paiements));
      setTicketPanierDirty(false);
      setActiveTicketCapabilities(posCapabilities || null);
    },
    [filterPaiementsPositifs]
  );

  /** Reprise après reload : sessionStorage garde panier/ticketId, pas les règlements ni la révision. */
  useEffect(() => {
    if (!session || !commercantId || !activeTicketId || view !== MODES.vendre) return;
    if (activeTicketRevision != null && activeTicketPaiements.length > 0) return;

    let cancelled = false;
    (async () => {
      try {
        const detail = await apiService.getTicketDetail(activeTicketId, commercantId);
        if (cancelled) return;
        const t = detail?.ticket;
        if (!t) {
          setActiveTicketId(null);
          setActiveTicketPaiements([]);
          setActiveTicketRevision(null);
          try {
            sessionStorage.removeItem(resolveCaisseTicketStorageKey(commercantId));
          } catch {
            /* ignore */
          }
          return;
        }
        if (t.posRevision != null) {
          setActiveTicketRevision(Number(t.posRevision));
        }
        setActiveTicketPaiements(filterPaiementsPositifs(t.paiements));
        if (detail?.posCapabilities) {
          setActiveTicketCapabilities(detail.posCapabilities);
        }
      } catch (e) {
        console.error('[Caisse] Hydratation ticket après reload:', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    session,
    commercantId,
    activeTicketId,
    view,
    activeTicketRevision,
    activeTicketPaiements.length,
    filterPaiementsPositifs,
  ]);

  const handleTicketRevisionConflict = useCallback(
    (err, { context = 'enregistrement' } = {}) => {
      const ui = resolveTicketRevisionConflictUi(err);
      setTicketConflict({
        ...ui,
        context,
        localPanierSnapshot: panier,
      });
      setPaymentError(ui.message);
      showToast(ui.message, 'error');
    },
    [panier, showToast]
  );

  const reprendreTicket = async (ticket) => {
    try {
      clearPanierDraftStorage(sessionStorage, commercantId);
      sessionStorage.removeItem(resolveCaisseTicketStorageKey(commercantId));
    } catch {
      /* ignore */
    }
    setPanier([]);
    setActiveTicketId(ticket.id);
    // L1-T1 : panier complet depuis le détail serveur (listes tickets souvent sans lignes).
    try {
      const detail = await apiService.getTicketDetail(ticket.id, commercantId);
      const t = detail?.ticket || ticket;
      if (t?.statut === 'ANNULEE') {
        showToast('Cette addition est annulée — reprise impossible', 'error');
        setActiveTicketId(null);
        setPanier([]);
        return;
      }
      appliquerTicketServeur(t, detail?.posCapabilities || null);
      if (t?.posRevision != null) {
        setActiveTicketRevision(Number(t.posRevision));
      }
      if (Array.isArray(t?.paiements)) {
        setActiveTicketPaiements(filterPaiementsPositifs(t.paiements));
      }
      const intentRewardId = detail?.rewardIdIntent || null;
      const user = t.user || null;
      const userId = t.userId || user?.id || null;
      const guestTechnical = user && isCaisseGuestClient(user);
      if (userId && user && !guestTechnical) {
        setSelectedClient({
          id: userId,
          nom: user.nom,
          prenom: user.prenom,
          email: user.email,
          telephone: user.telephone,
        });
        setContactNom(
          [user.prenom, user.nom].filter(Boolean).join(' ') || t.contactNom || ''
        );
      } else if (userId && !guestTechnical) {
        setSelectedClient({ id: userId, nom: null, prenom: null, email: null, telephone: null });
        setContactNom(t.contactNom || '');
      } else {
        setSelectedClient(null);
        setContactNom(t.contactNom || '');
        fidelite.clearAll();
      }
      // hydrate après set client (catalogue charge via effect)
      if (intentRewardId) {
        // léger délai pour laisser le clientUserId se propager au hook
        setTimeout(() => fidelite.hydrateIntent(intentRewardId), 0);
      } else {
        fidelite.resetSelection();
      }
    } catch {
      if (Array.isArray(ticket?.produits) && ticket.produits.length > 0) {
        appliquerTicketServeur(ticket);
      } else {
        setActiveTicketId(ticket.id);
        setPanier([]);
      }
      setContactNom(ticket.contactNom || '');
    } finally {
      setView(MODES.vendre);
      loadCatalogue();
    }
  };

  const reprendreTicketOuvert = async (ticket) => {
    const rs = resolveCaisseOpenTicketRestaurantService(ticket);
    if (rs?.serviceSessionId && rs?.tableId) {
      const params = new URLSearchParams({
        commercantId,
        posServiceSessionId: rs.serviceSessionId,
        posTableId: rs.tableId,
        returnTo: 'restaurant',
        ticketId: ticket.id,
      });
      if (rs.tableLabel) params.set('tableLabel', rs.tableLabel);
      navigate(`/caisse?${params.toString()}`);
      return;
    }
    await reprendreTicket(ticket);
  };

  useEffect(() => {
    restaurantBootstrapRef.current = false;
  }, [restaurantTicketIdFromUrl, posServiceSessionId, restaurantNewAddition]);

  useEffect(() => {
    if (!session || !commercantId || restaurantBootstrapRef.current) return;
    if (!posServiceSessionId && !restaurantTicketIdFromUrl) return;

    restaurantBootstrapRef.current = true;
    (async () => {
      if (restaurantTicketIdFromUrl) {
        try {
          const data = await apiService.getTicketDetail(restaurantTicketIdFromUrl, commercantId);
          const ticket = data?.ticket;
          if (ticket) await reprendreTicket(ticket);
        } catch (e) {
          setError(e.message || 'Impossible de reprendre l\'addition');
        }
        return;
      }
      if (posServiceSessionId && restaurantNewAddition) {
        setActiveTicketId(null);
        setActiveTicketRevision(null);
        setActiveTicketPaiements([]);
        setTicketConflict(null);
        setPanier([]);
        setTicketPanierDirty(false);
        try {
          clearPanierDraftStorage(sessionStorage, commercantId);
          clearOrphanPanierDraftStorage(sessionStorage, commercantId);
          sessionStorage.removeItem(resolveCaisseTicketStorageKey(commercantId));
          purgeLegacyGlobalCaisseStorage(sessionStorage);
        } catch {
          /* ignore */
        }
        setSelectedClient(null);
        fidelite.clearAll();
        setContactNom('');
        setView(MODES.vendre);
        loadCatalogue();
      }
    })();
  }, [
    session,
    commercantId,
    posServiceSessionId,
    restaurantTicketIdFromUrl,
    restaurantNewAddition,
    loadCatalogue,
    reprendreTicket,
  ]);

  const annulerTicketOuvert = (ticketId) => {
    setConfirmModal({
      title: 'Annuler ce ticket ?',
      message: 'Cette action est irréversible. Le ticket sera supprimé.',
      variant: 'danger',
      confirmLabel: 'Annuler le ticket',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await apiService.annulerTicket(ticketId);
          await loadTicketsOuverts();
          await loadSession();
          if (activeTicketId === ticketId) {
            setPanier([]);
            setActiveTicketId(null);
            setActiveTicketRevision(null);
            setActiveTicketPaiements([]);
            setTicketConflict(null);
          }
          showToast('Ticket annulé');
        } catch (e) {
          showToast(e.message || 'Erreur annulation', 'error');
        }
      },
    });
  };

  const downloadTicketFacture = async () => {
    if (!ticketDetail?.id || ticketFactureLoading) return;
    if (!isFactureTelechargeable(ticketDetail)) {
      showToast('Aucune facture disponible pour ce ticket', 'info');
      return;
    }
    setTicketFactureLoading(true);
    try {
      await apiService.downloadCommandeFacture(ticketDetail.id);
      showToast('Facture téléchargée', 'success');
    } catch (e) {
      showToast(e.message || 'Facture indisponible', 'error');
    } finally {
      setTicketFactureLoading(false);
    }
  };

  const downloadTicketAvoir = async (avoirId) => {
    if (!ticketDetail?.id || !avoirId) return;
    setTicketAvoirLoadingId(avoirId);
    try {
      await apiService.downloadCommandeAvoir(ticketDetail.id, avoirId);
      showToast('Avoir téléchargé', 'success');
    } catch (e) {
      showToast(e.message || 'Avoir indisponible', 'error');
    } finally {
      setTicketAvoirLoadingId(null);
    }
  };

  const voirDetailTicket = async (ticketId) => {
    try {
      const data = await apiService.getTicketDetail(ticketId, commercantId);
      const ticket = data?.ticket || null;
      setTicketDetail(ticket);
      setTicketFideliteDisplay(
        data?.fideliteDisplay ||
          (ticket
            ? buildCaisseFideliteDisplay({
                commande: ticket,
                rewardIdIntent: data?.rewardIdIntent || null,
                consommation: ticket.recompenseConsommations?.[0] || null,
              })
            : null)
      );
      setShowTicketDetail(true);
    } catch (e) {
      setError(e.message || 'Erreur chargement détail');
    }
  };

  const annulerTicketPaye = (ticketId) => {
    setAnnulMotif('');
    setConfirmModal({
      title: 'Annuler ce ticket payé ?',
      message: 'INPUT_MOTIF',
      variant: 'danger',
      confirmLabel: 'Confirmer l\'annulation',
      onConfirm: async () => {
        const motif = annulMotif || 'Annulation POS';
        setConfirmModal(null);
        try {
          await apiService.annulerTicketPaye(ticketId, motif);
          setShowTicketDetail(false);
          setTicketDetail(null);
          await loadSession();
          await loadTicketsOuverts();
          showToast('Ticket annulé');
        } catch (e) {
          showToast(e.message || 'Erreur annulation', 'error');
        }
      },
    });
  };

  const ouvrirSavModal = () => {
    setSavTab('articles');
    setSavLineQty({});
    setSavMontantLibre('');
    setSavMotif('');
    setSavMethode('ESPECES');
    setShowSavModal(true);
  };

  const openMesCommandesFromUnifiedStripe = useCallback(
    (href) => {
      if (!href || typeof href !== 'string') return;
      if (!isU2Ux1StripeMesCommandesFromUnifiedEnabled()) return;
      const url = href.startsWith('/') ? `${window.location.origin}${href}` : href;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    []
  );

  const navigateToWebEncaisseRefundFromUnified = useCallback(
    async (commandeId) => {
      if (!commandeId) return;
      const loadGen = ++webEncaisseHandoffLoadGenRef.current;
      const shopAtStart = commercantId;
      setWebEncaisseUnifiedHandoff({ returnTo: 'unified', commandeId });
      setShowSavModal(false);
      setShowTicketDetail(false);
      try {
        const data = await apiService.getTicketDetail(commandeId, shopAtStart);
        const check = validateWebEncaisseUnifiedHandoffTicketLoad({
          loadGen,
          activeGen: webEncaisseHandoffLoadGenRef.current,
          shopAtStart,
          shopNow: commercantId,
          commandeId,
          ticket: data?.ticket || null,
        });
        if (!check.ok) {
          if (loadGen === webEncaisseHandoffLoadGenRef.current) {
            setWebEncaisseUnifiedHandoff(null);
          }
          if (check.reason === 'shop_changed') {
            showToast('Boutique changée pendant le chargement.', 'error');
          } else if (check.reason === 'ticket_missing' || check.reason === 'ticket_mismatch') {
            showToast('Commande introuvable ou incohérente', 'error');
          }
          return;
        }
        const ticket = data.ticket;
        setTicketDetail(ticket);
        setTicketFideliteDisplay(
          data?.fideliteDisplay ||
            buildCaisseFideliteDisplay({
              commande: ticket,
              rewardIdIntent: data?.rewardIdIntent || null,
              consommation: ticket.recompenseConsommations?.[0] || null,
            })
        );
        setShowTicketDetail(true);
        ouvrirSavModal();
      } catch (e) {
        if (loadGen !== webEncaisseHandoffLoadGenRef.current) return;
        setWebEncaisseUnifiedHandoff(null);
        setShowTicketDetail(false);
        setShowSavModal(false);
        showToast(e.message || 'Erreur chargement commande', 'error');
      }
    },
    [commercantId, showToast]
  );

  const handleRemboursement = async () => {
    if (savLoading) return;
    setSavLoading(true);
    try {
      let payload;
      if (savTab === 'complet') {
        payload = { type: 'COMPLET', motif: savMotif, methodePaiement: savMethode };
      } else if (savTab === 'articles') {
        const activeLines = Object.entries(savLineQty).filter(([, q]) => q > 0);
        if (activeLines.length === 0) {
          showToast('Sélectionnez au moins un article', 'error');
          setSavLoading(false);
          return;
        }
        const ligneQuantites = Object.fromEntries(activeLines);
        payload = { type: 'ARTICLES', ligneIds: activeLines.map(([id]) => id), ligneQuantites, motif: savMotif, methodePaiement: savMethode };
      } else {
        const montant = parseFloat(savMontantLibre);
        if (!montant || montant <= 0) {
          showToast('Montant invalide', 'error');
          setSavLoading(false);
          return;
        }
        payload = { type: 'LIBRE', montant, motif: savMotif, methodePaiement: savMethode };
      }
      const exec = await executeCaisseSavRefund({
        ticketDetail,
        payload,
        commercantId,
        apiService,
        createIdempotencyKey: createCaisseIdempotencyKey,
        storage: sessionStorage,
      });
      if (exec.blocked) {
        showToast(exec.message || 'Remboursement impossible depuis la caisse pour cette commande.', 'error');
        setSavLoading(false);
        return;
      }
      const { result } = exec;
      showToast(`Remboursé ${fmt(result.montantRembourse)} — solde restant ${fmt(result.soldeRestant)}`);
      if (result.avoirCreated && result.avoir?.numeroAvoir) {
        showToast(`Avoir ${result.avoir.numeroAvoir} émis`, 'info');
      }
      setShowSavModal(false);
      const refreshed = await apiService.getTicketDetail(ticketDetail.id, commercantId);
      setTicketDetail(refreshed?.ticket || refreshed);
      setTicketFideliteDisplay(refreshed?.fideliteDisplay || null);
      await loadSession();
      if (webEncaisseUnifiedHandoff?.returnTo === 'unified') {
        setShowTicketDetail(false);
        setWebEncaisseUnifiedHandoff(null);
      }
    } catch (e) {
      showToast(e.message || 'Erreur remboursement', 'error');
    } finally {
      setSavLoading(false);
    }
  };

  const dismissTicketDetailOverlay = () => {
    webEncaisseHandoffLoadGenRef.current += 1;
    setShowTicketDetail(false);
    setShowSavModal(false);
    setTicketFideliteDisplay(null);
    if (webEncaisseUnifiedHandoff?.returnTo === 'unified') {
      setWebEncaisseUnifiedHandoff(null);
    }
  };

  const imprimerDuplicata = (ticket, displayOverride = null) => {
    const display =
      displayOverride ||
      ticketFideliteDisplay ||
      buildCaisseFideliteDisplay({
        commande: ticket,
        rewardIdIntent: null,
        consommation: ticket?.recompenseConsommations?.[0] || null,
      });
    setDuplicataPreview({ ticket, display });
  };

  const handleFermerSession = async (force = false) => {
    if (especesComptees === '' || Number.isNaN(Number(especesComptees))) {
      setError('Indiquez le montant des espèces comptées dans le tiroir.');
      return;
    }
    const counted = round2(especesComptees);
    const ecart = round2(counted - round2(especesEnCaisseAttendues));
    const motif = motifEcartCaisse.trim();
    if (Math.abs(ecart) > 0.01 && motif.length < 3) {
      setError('Un motif est obligatoire lorsque l’écart de caisse est non nul.');
      return;
    }
    if (force && fermetureWarning?.count > 0) {
      const fm = forceCloseMotif.trim();
      if (fm.length < 3) {
        setError('Un motif est obligatoire pour annuler les tickets et fermer.');
        return;
      }
    }

    setProcessing(true);
    setError('');
    try {
      const res = await apiService.fermerCaisseSession({
        especesComptees: counted,
        confirmCashCount: true,
        motifEcartCaisse: motif || undefined,
        commercantId,
        forceClose: force,
        forceCloseMotif: force ? forceCloseMotif.trim() : undefined,
      });
      if (res?.code === 'TICKETS_OUVERTS') {
        setFermetureWarning(res);
        setRestaurantCloseWarning(null);
        setProcessing(false);
        return;
      }
      if (res?.code === 'RESTAURANT_SERVICE_CLOSE_BLOCKED') {
        setRestaurantCloseWarning(res);
        setFermetureWarning(null);
        setProcessing(false);
        return;
      }
      setSession(null);
      setSessionStats(null);
      setFermetureWarning(null);
      setView(MODES.accueil);
      setEspecesComptees('');
      setMotifEcartCaisse('');
      setForceCloseMotif('');
    } catch (e) {
      const code = e.data?.error?.code || e.data?.code;
      if (code === 'TICKETS_OUVERTS') {
        setFermetureWarning(e.data?.error ? { ...e.data.error, ...e.data } : e.data);
        setRestaurantCloseWarning(null);
      } else if (code === 'RESTAURANT_SERVICE_CLOSE_BLOCKED') {
        setRestaurantCloseWarning(e.data?.error ? { ...e.data.error, ...e.data } : e.data);
        setFermetureWarning(null);
      } else {
        setError(e.message || 'Erreur clôture');
      }
    } finally {
      setProcessing(false);
    }
  };

  const validerVente = async (check) => {
    if (panier.length === 0 || !check?.ok) return;

    const codeSaisi = codePromo.trim();
    if (codeSaisi && !promoValide) {
      setPaymentError(
        'Code promo non validé. Cliquez sur « OK » ou videz le champ pour continuer sans promo.'
      );
      return;
    }

    // F2.4b2b — payNow et ticket + reward autorisés (apply serveur)
    const payGate = resolveCaisseImmediatePayGate({
      confirmedRewardId: fidelite.confirmedRewardId,
      activeTicketId,
    });
    if (payGate.action === 'block') {
      setPaymentError(payGate.message);
      return;
    }
    // F2.5a3 — pas d’encaissement silencieux si reward confirmé mais quote morte
    const rewardGate = resolveCaisseConfirmedRewardPayGate({
      confirmedRewardId: fidelite.confirmedRewardId,
      quote: fidelite.quote,
      quoteLoading: fidelite.quoteLoading,
      quoteError: fidelite.quoteError,
    });
    if (rewardGate.action === 'block') {
      setPaymentError(rewardGate.message);
      return;
    }

    setPaymentError('');
    setProcessing(true);
    if (!paymentIdempotencyKeyRef.current) {
      paymentIdempotencyKeyRef.current = createCaisseIdempotencyKey();
    }
    const idempotencyKey = paymentIdempotencyKeyRef.current;
    try {
      let res;
      if (activeTicketId) {
        // L1-T1 / POS-L0-D1 : persister le panier modifié AVANT encaissement
        // L1-T4 : expectedPosRevision après sync pour bloquer encaissement obsolète
        // L1-T6 : si règlements déjà confirmés → pas de modifierTicket (TICKET_HAS_PAYMENTS)
        let syncSucceeded = false;
        let serverTotal = null;
        let syncErrorMessage = '';
        let expectedForPay = null;
        const ticketDejaPartiel = (activeTicketPaiements || []).length > 0;

        if (
          ticketPanierDirty &&
          activeTicketCapabilities &&
          activeTicketCapabilities.canEditLines === false
        ) {
          setPaymentError(
            activeTicketCapabilities.blockReason ||
              'Cette addition ne peut plus être modifiée. Actualisez depuis le plan de salle ou encaissez le solde affiché.'
          );
          setProcessing(false);
          return;
        }

        const mustSync = shouldSyncTicketBeforePay({
          ticketPanierDirty,
          posCapabilities: activeTicketCapabilities,
          ticketDejaPartiel,
        });

        if (ticketDejaPartiel || !mustSync) {
          if (activeTicketCapabilities?.canPay === false) {
            setPaymentError(
              activeTicketCapabilities?.blockReason ||
                'Encaissement indisponible pour cette addition.'
            );
            setProcessing(false);
            return;
          }
          if (activeTicketRevision == null) {
            setPaymentError(
              'Révision du ticket manquante. Reprenez le ticket avant d’encaisser.'
            );
            setProcessing(false);
            return;
          }
          expectedForPay = Number(activeTicketRevision);
          syncSucceeded = true;
          serverTotal = Number(paymentTotalAddition);
        } else {
          const syncPayload = {
            commercantId,
            ...buildModifierTicketPayload(
              panier,
              serializeLigneProduitPourVente,
              activeTicketRevision
            ),
          };
          if (!syncPayload.produits.length) {
            setPaymentError('Le ticket doit contenir au moins un produit.');
            setProcessing(false);
            return;
          }
          if (syncPayload.expectedPosRevision == null) {
            setPaymentError(
              'Révision du ticket manquante. Reprenez le ticket avant d’encaisser.'
            );
            setProcessing(false);
            return;
          }
          try {
            const syncRes = await apiService.modifierTicket(activeTicketId, syncPayload);
            serverTotal = Number(syncRes?.ticket?.total);
            syncSucceeded = Number.isFinite(serverTotal);
            if (syncRes?.ticket?.posRevision != null) {
              expectedForPay = Number(syncRes.ticket.posRevision);
              setActiveTicketRevision(expectedForPay);
            }
          } catch (syncErr) {
            syncSucceeded = false;
            syncErrorMessage =
              syncErr?.data?.error?.message ||
              syncErr?.data?.message ||
              syncErr?.message ||
              '';
            if (isTicketRevisionConflict(syncErr)) {
              handleTicketRevisionConflict(syncErr, { context: 'enregistrement' });
              setProcessing(false);
              return;
            }
            if (
              syncErr?.data?.code === 'TICKET_HAS_PAYMENTS' ||
              syncErr?.data?.error?.code === 'TICKET_HAS_PAYMENTS'
            ) {
              setPaymentError(
                syncErr?.data?.error?.message ||
                  syncErr?.data?.message ||
                  'Ticket déjà partiellement réglé — reprisez-le pour encaisser le solde.'
              );
              setProcessing(false);
              return;
            }
          }
          const syncGate = resolveTicketSyncBeforePayGate({
            syncSucceeded,
            serverTotal,
            localTotal: totalPanier,
            syncErrorMessage,
          });
          if (syncGate.action === 'block') {
            setPaymentError(syncGate.message);
            setProcessing(false);
            return;
          }
        }
        if (expectedForPay == null) {
          setPaymentError(
            'Révision serveur indisponible après synchronisation. Paiement bloqué.'
          );
          setProcessing(false);
          return;
        }

        const allowPartial = Boolean(check.isPartial || check.allowPartial);

        try {
          res = await apiService.encaisserCaisse(
            {
              commercantId,
              type: 'COMMANDE',
              id: activeTicketId,
              methodePaiement:
                check.methodePaiement === 'MIXTE' ? 'ESPECES' : check.methodePaiement,
              montantRecu: check.montantRecu ?? undefined,
              paiements: check.paiements,
              codePromo:
                !ticketDejaPartiel && promoValide && codeSaisi ? codeSaisi : undefined,
              expectedPosRevision: expectedForPay,
              allowPartial,
            },
            { idempotencyKey }
          );
        } catch (payErr) {
          if (isTicketRevisionConflict(payErr)) {
            handleTicketRevisionConflict(payErr, { context: 'encaissement' });
            setProcessing(false);
            return;
          }
          throw payErr;
        }
      } else {
        const fideliteFields = buildImmediateVenteFideliteFields({
          clientUserId: selectedClient?.id || null,
          confirmedRewardId: fidelite.confirmedRewardId,
        });
        res = await apiService.creerVenteCaisse(
          {
            commercantId,
            produits: panier
              .filter((l) => l.lineType !== 'prestation')
              .map(serializeLigneProduitPourVente),
            prestations: panier
              .filter((l) => l.lineType === 'prestation')
              .map((l) => ({
                prestationId: l.prestationId,
                quantite: l.quantite,
                prixUnitaire: l.prixUnitaire,
              })),
            contactNom: contactNom || undefined,
            codePromo: promoValide && codeSaisi ? codeSaisi : undefined,
            encaisserImmediatement: true,
            methodePaiement:
              check.methodePaiement === 'MIXTE' ? 'ESPECES' : check.methodePaiement,
            montantRecu: check.montantRecu != null ? Number(check.montantRecu) : undefined,
            paiements: check.paiements,
            ...fideliteFields,
          },
          { idempotencyKey }
        );
      }
      paymentIdempotencyKeyRef.current = null;

      // L1-T6 — paiement partiel : conserver le ticket, actualiser solde / révision
      if (res?.partial && !res?.settled && activeTicketId) {
        if (res.commande?.posRevision != null) {
          setActiveTicketRevision(Number(res.commande.posRevision));
        }
        if (Array.isArray(res.commande?.paiements)) {
          setActiveTicketPaiements(
            res.commande.paiements.filter(
              (p) =>
                p.statutPaiement === 'VALIDE' &&
                Number(p.montant) > 0 &&
                p.typePaiement !== 'REMBOURSEMENT'
            )
          );
        } else {
          // Recharger le détail pour historique
          try {
            const detail = await apiService.getTicketDetail(activeTicketId, commercantId);
            const t = detail?.ticket;
            if (t?.posRevision != null) setActiveTicketRevision(Number(t.posRevision));
            if (Array.isArray(t?.paiements)) {
              setActiveTicketPaiements(
                t.paiements.filter(
                  (p) =>
                    p.statutPaiement === 'VALIDE' &&
                    Number(p.montant) > 0 &&
                    p.typePaiement !== 'REMBOURSEMENT'
                )
              );
            }
          } catch {
            /* ignore */
          }
        }
        resetPaiement();
        setPaymentMode(null);
        resetPromo();
        await loadSession();
        await loadTicketsOuverts();
        const reste = res.soldeRestant != null ? fmt(res.soldeRestant) : '—';
        showToast(`Règlement partiel enregistré · Reste à payer : ${reste}`);
        setView(MODES.vendre);
      } else {
        setPanier([]);
        setActiveTicketId(null);
        setActiveTicketRevision(null);
        setActiveTicketPaiements([]);
        setTicketConflict(null);
        setContactNom('');
        setSelectedClient(null);
        fidelite.clearAll();
        resetPaiement();
        setPaymentMode(null);
        resetPromo();
        await loadSession();
        await loadTicketsOuverts();
        let msg = 'Vente enregistrée';
        if (res.rendu != null && res.rendu > 0) msg += ` · Rendu : ${fmt(res.rendu)}`;
        if (res.reduction > 0) msg += ` · Réduction : ${fmt(res.reduction)}`;
        if (res.reductionAvantage > 0) msg += ` · Fidélité : ${fmt(res.reductionAvantage)}`;
        showToast(msg);
        setView(MODES.accueil);
      }
    } catch (e) {
      const detail = e.data?.details?.[0]?.message || e.data?.message;
      const msg = detail || e.message || 'Erreur lors de la vente';
      setPaymentError(msg);
      // Soft-skip interdit : conserver panier / client / intention si erreur catalogue
      if (!isRewardCatalogueVenteError(e) && !isRewardCatalogueVenteError({ code: e.data?.code })) {
        // autres erreurs : panier aussi conservé (pas de clear)
      }
    } finally {
      setProcessing(false);
    }
  };

  const confirmerEncaissement = async (check) => {
    if (!encaissementTarget || !check?.ok) return;

    setPaymentError('');
    setProcessing(true);
    if (!paymentIdempotencyKeyRef.current) {
      paymentIdempotencyKeyRef.current = createCaisseIdempotencyKey();
    }
    const idempotencyKey = paymentIdempotencyKeyRef.current;
    try {
      const res = await apiService.encaisserCaisse(
        {
          commercantId,
          type: encaissementTarget.type,
          id: encaissementTarget.id,
          methodePaiement:
            check.methodePaiement === 'MIXTE' ? 'ESPECES' : check.methodePaiement,
          montantRecu: check.montantRecu ?? undefined,
          paiements: check.paiements,
        },
        encaissementTarget.type === 'COMMANDE' ? { idempotencyKey } : undefined
      );
      paymentIdempotencyKeyRef.current = null;
      setEncaissementTarget(null);
      resetPaiement();
      await loadAEncaisser();
      await loadSession();
      let msg = 'Encaissé';
      if (res.rendu != null && res.rendu > 0) msg += ` · Rendu : ${fmt(res.rendu)}`;
      showToast(msg);
    } catch (e) {
      const fin = e.data?.encaissementHint || e.data?.error?.details;
      const detail = e.data?.details?.[0]?.message || e.data?.error?.message || e.data?.message;
      let msg = detail || e.message || 'Erreur encaissement';
      if (fin?.montantDu != null && (e.data?.error?.code === 'TROP_PERCU' || e.data?.error?.code === 'PAIEMENT_INCOMPLET')) {
        msg = `${msg} — Montant exigible serveur : ${fmt(Number(fin.montantDu))}`;
      }
      setPaymentError(msg);
    } finally {
      setProcessing(false);
    }
  };

  const paymentOpen = !!encaissementTarget || paymentMode === 'vente';

  const barcodeScannerEnabled =
    Boolean(session) &&
    view === MODES.vendre &&
    catalogueTab === 'produits' &&
    !optionsProduct &&
    !poidsProduct &&
    !selectedProduct &&
    !paymentOpen;

  useEffect(() => {
    if (barcodeScannerEnabled) {
      document.body.dataset.posBarcodeScan = '1';
    } else {
      delete document.body.dataset.posBarcodeScan;
    }
    return () => {
      delete document.body.dataset.posBarcodeScan;
    };
  }, [barcodeScannerEnabled]);

  usePosBarcodeScanner({
    enabled: barcodeScannerEnabled,
    onRetailGtin: (gtin) => {
      void handleRetailGtinScan(gtin);
    },
  });

  const clotureTiroir = useMemo(
    () => buildClotureTiroirBreakdown(sessionRecap),
    [sessionRecap]
  );

  if (loading) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center gap-3">
        <RefreshCw className="h-10 w-10 animate-spin text-emerald-600" />
        <p className="text-slate-500 text-sm">Chargement de la caisse…</p>
      </div>
    );
  }

  if (!commercantId) {
    return (
      <div className="max-w-lg mx-auto mt-12 p-10 bg-white rounded-3xl shadow-xl text-center border border-slate-100">
        <Banknote className="h-14 w-14 mx-auto text-slate-300 mb-4" />
        <h2 className="text-xl font-bold text-slate-900">Caisse (POS)</h2>
        <p className="text-slate-600 mt-2 text-sm leading-relaxed">
          Commerce non identifié. Ouvrez la caisse depuis la fiche commerçant (bouton « Ouvrir la
          caisse ») ou vérifiez que votre compte est bien lié à un commerce.
        </p>
        <Link
          to="/commercants"
          className="inline-flex mt-6 items-center gap-2 text-emerald-600 font-semibold hover:underline"
        >
          Voir les commerçants →
        </Link>
      </div>
    );
  }

  if (error && !session && error.includes('module')) {
    return (
      <div className="max-w-lg mx-auto mt-12 p-10 bg-white rounded-3xl shadow-xl text-center">
        <Banknote className="h-14 w-14 mx-auto text-amber-500 mb-4" />
        <h2 className="text-xl font-bold">Module non activé</h2>
        <p className="text-slate-600 mt-2 text-sm">{error}</p>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="pos-caisse-session-shell w-full" data-testid="caisse-open-session-view">
        <PosCaisseSharedHeader commercant={posClock.commercant} />
        {posDeviceBlockedCode && (
          <div className="mb-4 space-y-0">
            <PosPosteSessionBar
              poste={posPoste}
              session={null}
              multiPostOperational={posMultiPostOperational}
              singlePosteMode={posSinglePosteMode}
              deviceBlockedCode={posDeviceBlockedCode}
              commercantId={commercantId}
              currentDeviceId={posLocalDeviceId}
              currentDeviceLabel={getDeviceFriendlyLabel(posLocalDeviceId)}
            />
            {posSessionRemoteClosed ? (
              <p
                data-testid="pos-session-remote-closed"
                className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3"
              >
                La session a été clôturée sur un autre appareil du même poste. Les opérations de
                vente sont bloquées jusqu&apos;à une nouvelle ouverture.
              </p>
            ) : null}
            <PosCaisseDeviceEnrollment
              commercantId={commercantId}
              onEnrolled={async () => {
                await loadSession();
              }}
            />
          </div>
        )}
        {!posDeviceBlockedCode ? (
          <>
            <div className="mb-4">
              <PosPosteSessionBar
                poste={posPoste}
                session={null}
                multiPostOperational={posMultiPostOperational}
                singlePosteMode={posSinglePosteMode}
                deviceBlockedCode={null}
                commercantId={commercantId}
                currentDeviceId={posLocalDeviceId}
                currentDeviceLabel={getDeviceFriendlyLabel(posLocalDeviceId)}
              />
              {posSessionRemoteClosed ? (
                <p
                  data-testid="pos-session-remote-closed"
                  className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3"
                >
                  La session a été clôturée sur un autre appareil du même poste. Les opérations de
                  vente sont bloquées jusqu&apos;à une nouvelle ouverture.
                </p>
              ) : null}
            </div>
            <div className="pos-caisse-session-form-card bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
            <div className="bg-gradient-to-br from-emerald-600 to-teal-700 px-6 sm:px-8 py-8 sm:py-10 text-center text-white">
              <Banknote className="h-16 w-16 mx-auto mb-4 opacity-90" />
              <h1 className="text-2xl font-bold">Ouvrir la caisse</h1>
              <p className="text-emerald-100 mt-2 text-sm">Démarrez votre session de vente</p>
            </div>
            <div className="p-6 sm:p-8">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Fond de caisse ({devise})
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="0"
                value={fondCaisse}
                onChange={(e) => setFondCaisse(e.target.value)}
                onFocus={(e) => e.target.select()}
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-xl focus:border-emerald-500 outline-none"
              />
              {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
              <button
                type="button"
                data-testid="caisse-open-session"
                onClick={handleOuvrirSession}
                disabled={processing}
                className="w-full mt-6 py-4 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 disabled:opacity-50 shadow-lg shadow-emerald-600/30"
              >
                Ouvrir la caisse
              </button>
            </div>
          </div>
          </>
        ) : null}
      </div>
    );
  }

  const rewardReductionEstimee =
    fidelite.confirmedRewardId &&
    fidelite.quote?.applicable
      ? Number(fidelite.quote.reductionAvantage) || 0
      : 0;
  const dejaRegleTicket = Math.round(
    (activeTicketPaiements || []).reduce((s, p) => s + (Number(p.montant) || 0), 0) * 100
  ) / 100;
  const paymentTotalAddition =
    encaissementTarget?.totalCommande ??
    encaissementTarget?.montant ??
    (paymentMode === 'vente'
      ? Math.max(
          0,
          Math.round(
            (totalPanier - ((Number(promoReduction) || 0) + rewardReductionEstimee)) * 100
          ) / 100
        )
      : 0);
  // L1-T6 — le modal reçoit le solde restant ; total addition affiché séparément
  const paymentMontant = encaissementTarget
    ? (encaissementTarget.montantDu ?? encaissementTarget.montant)
    : Math.max(0, Math.round((paymentTotalAddition - dejaRegleTicket) * 100) / 100);
  const paymentTitle = encaissementTarget ? 'Encaisser' : 'Encaisser la vente';
  const paymentSubtitle = encaissementTarget?.label || `${panier.length} article(s)`;
  const paymentContextNote =
    encaissementTarget?.crossPostEligible && encaissementTarget?.ticketOriginePoste?.label
      ? `Encaissement sur ce poste — ticket ouvert au ${encaissementTarget.ticketOriginePoste.label}`
      : encaissementTarget?.sessionCaisseId &&
          session?.id &&
          encaissementTarget.sessionCaisseId !== session.id
        ? 'Encaissement sur ce poste — ticket rattaché à une autre session'
        : '';
  // Montant modal = solde net déjà calculé → pas de double réduction UI
  const paymentReductionAffichee = encaissementTarget
    ? Number(encaissementTarget.reduction) || 0
    : 0;
  const paymentReductionTotale = 0;
  const paymentFraisLivraison = encaissementTarget
    ? Number(encaissementTarget.fraisLivraison) || 0
    : 0;
  const paymentFraisExpedition = encaissementTarget
    ? Number(encaissementTarget.fraisExpedition) || 0
    : 0;

  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const fondRecap = clotureTiroir.fond;
  const especesEnCaisseAttendues = clotureTiroir.especesAttendues;
  const ventesEspecesAffichees = clotureTiroir.ventesEspecesKnown ? clotureTiroir.ventesEspeces : null;
  const remboursementsEspecesCloture = clotureTiroir.remboursementsEspeces;
  const tresorerieNetCloture = clotureTiroir.tresorerieNet;
  const ecartPreview =
    especesComptees !== ''
      ? round2(round2(especesComptees) - round2(especesEnCaisseAttendues))
      : null;
  const ecartAffiche =
    ecartPreview != null && Math.abs(ecartPreview) < 0.005 ? 0 : ecartPreview;

  const showTicketCapabilityBanner = Boolean(
    activeTicketId &&
      activeTicketCapabilities &&
      activeTicketCapabilities.canEditLines === false &&
      activeTicketCapabilities.blockReason
  );

  const caissePageRootClass = posImmersiveActive
    ? `pos-caisse-immersive-root min-w-0 max-w-full w-full flex flex-col min-h-0 overflow-x-clip ${
        view === MODES.vendre ? 'flex-1 min-h-0 overflow-hidden' : ''
      }`
    : `pos-caisse-page-root min-w-0 max-w-full w-full flex flex-col flex-1 min-h-0 ${
        view === MODES.vendre ? 'overflow-hidden' : ''
      }`;

  return (
    <div className={caissePageRootClass} data-testid="caisse-page-root" data-caisse-view={view}>
      <PosCaisseSharedHeader commercant={posClock.commercant} />
      {!(posImmersiveActive && view === MODES.vendre) && (
        <div className="px-3 sm:px-4 min-w-0 max-w-full">
          <PosOperationalAlertsBanner
            health={operationalHealth}
            commercantId={commercantId}
            dismissed={operationalAlertsDismissed}
            onDismiss={() => setOperationalAlertsDismissed(true)}
          />
          {showTicketCapabilityBanner && (
            <div
              className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950"
              data-testid="caisse-ticket-capability-banner"
              role="status"
            >
              {activeTicketCapabilities.blockReason}
            </div>
          )}
        </div>
      )}
      <CaisseToast toasts={toasts} onDismiss={dismissToast} />

      <CaisseDuplicataPreviewModal
        open={!!duplicataPreview}
        ticket={duplicataPreview?.ticket}
        display={duplicataPreview?.display}
        formatMoney={fmt}
        commercant={commercantProfile}
        onClose={() => setDuplicataPreview(null)}
      />

      <CaisseConfirmModal
        open={!!confirmModal}
        title={confirmModal?.title || ''}
        confirmLabel={confirmModal?.confirmLabel}
        cancelLabel={confirmModal?.cancelLabel || 'Retour'}
        variant={confirmModal?.variant}
        processing={processing}
        testIdCancel={confirmModal?.testIdCancel}
        onCancel={() => {
          if (confirmModal?.onCancel) confirmModal.onCancel();
          else setConfirmModal(null);
        }}
        onConfirm={() => confirmModal?.onConfirm?.()}
      >
        {confirmModal?.message === 'INPUT_MOTIF' ? (
          <div>
            <p className="text-sm text-slate-600 mb-3">
              Le ticket sera annulé et les paiements marqués comme remboursés.
            </p>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Motif d&apos;annulation
            </label>
            <input
              type="text"
              value={annulMotif}
              onChange={(e) => setAnnulMotif(e.target.value)}
              placeholder="Ex: erreur de saisie, demande client..."
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-red-400 outline-none"
              autoFocus
            />
          </div>
        ) : (
          <p className="text-sm text-slate-600">{confirmModal?.message}</p>
        )}
      </CaisseConfirmModal>

      <CaisseConfirmModal
        open={!!ticketConflict}
        title="Addition modifiée ailleurs"
        confirmLabel="Actualiser avec la version serveur"
        cancelLabel="Garder mes modifications locales"
        variant="danger"
        processing={false}
        onCancel={() => {
          setTicketConflict(null);
        }}
        onConfirm={() => {
          const serverTicket = ticketConflict?.ticket;
          if (serverTicket) {
            appliquerTicketServeur(serverTicket);
            setPaymentMode(null);
            setPaymentError('');
            showToast('Ticket actualisé avec la version serveur', 'info');
          }
          setTicketConflict(null);
        }}
      >
        <p className="text-sm text-slate-700">
          {ticketConflict?.message ||
            'Cette addition a été modifiée sur un autre terminal. Actualisez-la avant d’enregistrer ou d’encaisser.'}
        </p>
        <p className="text-xs text-slate-500 mt-2">
          Vos modifications locales non enregistrées sont conservées tant que vous ne
          choisissez pas d’actualiser.
        </p>
      </CaisseConfirmModal>

      {showClotureConfirm && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/40 p-4" onClick={() => setShowClotureConfirm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="bg-slate-800 px-6 py-5 rounded-t-2xl">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Confirmer la fermeture
              </h3>
            </div>
            <div className="px-6 py-5 space-y-3">
              <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Ventes initiées (ce poste)</span>
                  <span className="font-bold text-slate-900">
                    {fmt(sessionRecap?.ventesInitiees?.montant ?? sessionRecap?.totalVentes ?? 0)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Encaissements reçus (ce poste)</span>
                  <span className="font-bold text-slate-900">
                    {fmt(sessionRecap?.encaissementsRecus?.montant ?? sessionRecap?.totalEncaissements ?? 0)}
                  </span>
                </div>
                {Math.abs(sessionRecap?.crossPostEcart ?? 0) >= 0.01 && (
                  <div className="flex justify-between text-xs text-violet-800">
                    <span>Écart inter-postes (initié − encaissé ici)</span>
                    <span className="font-semibold">{fmt(sessionRecap.crossPostEcart)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Nombre de ventes initiées</span>
                  <span className="font-bold">{sessionRecap?.ventesInitiees?.count ?? sessionRecap?.ventesCount ?? 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Espèces attendues</span>
                  <span className="font-bold">{fmt(especesEnCaisseAttendues)}</span>
                </div>
                {ecartAffiche != null && !Number.isNaN(ecartAffiche) && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Écart caisse</span>
                    <span className={`font-bold ${ecartAffiche === 0 ? 'text-emerald-600' : ecartAffiche > 0 ? 'text-amber-600' : 'text-red-600'}`}>
                      {ecartAffiche >= 0 ? '+' : ''}{fmt(ecartAffiche)}
                    </span>
                  </div>
                )}
              </div>
              {sessionRecap?.remboursements?.count > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 space-y-2">
                  <h4 className="text-sm font-semibold text-orange-700 flex items-center gap-1.5">
                    <RotateCcw className="h-4 w-4" /> Remboursements
                  </h4>
                  <div className="flex justify-between text-sm">
                    <span className="text-orange-600">Nombre</span>
                    <span className="font-bold text-orange-800">{sessionRecap.remboursements.count}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-orange-600">Total remboursé</span>
                    <span className="font-bold text-orange-800">-{fmt(sessionRecap.remboursements.total)}</span>
                  </div>
                  {sessionRecap.remboursements.especes > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-orange-600">dont espèces</span>
                      <span className="font-bold">-{fmt(sessionRecap.remboursements.especes)}</span>
                    </div>
                  )}
                  {sessionRecap.remboursements.carte > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-orange-600">dont carte</span>
                      <span className="font-bold">-{fmt(sessionRecap.remboursements.carte)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm border-t border-orange-200 pt-2">
                    <span className="text-orange-700 font-semibold">CA net</span>
                    <span className="font-bold text-orange-900">{fmt(sessionRecap.totalNet ?? sessionRecap.totalVentes)}</span>
                  </div>
                </div>
              )}
              <p className="text-sm text-red-600 font-medium">
                Cette action est définitive. Vous ne pourrez plus accéder à cette session.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex gap-3">
              <button
                type="button"
                onClick={() => setShowClotureConfirm(false)}
                className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200"
              >
                Retour
              </button>
              <button
                type="button"
                data-testid="caisse-cloture-confirmer"
                onClick={() => {
                  setShowClotureConfirm(false);
                  handleFermerSession(false);
                }}
                disabled={processing}
                className="flex-1 py-3 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50"
              >
                Fermer définitivement
              </button>
            </div>
          </div>
        </div>
      )}

      <CaissePaymentModal
        open={paymentOpen}
        title={paymentTitle}
        subtitle={paymentSubtitle}
        contextNote={paymentContextNote}
        montant={paymentMontant}
        sousTotalArticles={
          encaissementTarget?.sousTotal != null ? Number(encaissementTarget.sousTotal) : null
        }
        reduction={paymentReductionTotale}
        reductionAffichee={paymentReductionAffichee}
        totalAddition={paymentTotalAddition || null}
        dejaRegle={
          encaissementTarget
            ? Number(encaissementTarget.dejaEncaisse) || 0
            : dejaRegleTicket
        }
        fraisLivraison={paymentFraisLivraison}
        fraisExpedition={paymentFraisExpedition}
        historiquePaiements={
          encaissementTarget?.historiquePaiements?.length
            ? encaissementTarget.historiquePaiements
            : activeTicketPaiements
        }
        onRefreshFromServer={
          encaissementTarget && commercantId
            ? async () => {
                try {
                  const data = await apiService.getCaisseAEncaisser(commercantId);
                  const items = data.items || [];
                  setAEncaisser(items);
                  const fresh = items.find(
                    (i) => i.id === encaissementTarget.id && i.type === encaissementTarget.type
                  );
                  if (fresh) {
                    setEncaissementTarget(fresh);
                    setPaymentError('');
                    showToast('Montants actualisés depuis le serveur', 'info');
                  } else {
                    setEncaissementTarget(null);
                    setPaymentMode(null);
                    showToast('Cette commande n’est plus à encaisser', 'info');
                  }
                } catch (err) {
                  showToast(err.message || 'Actualisation impossible', 'error');
                }
              }
            : undefined
        }
        fmt={fmt}
        showCodePromo={paymentMode === 'vente' && dejaRegleTicket <= 0}
        codePromo={codePromo}
        setCodePromo={(value) => {
          setCodePromo(value);
          setPromoValide(false);
          setPromoReduction(0);
          setPromoMessage('');
        }}
        onVerifierPromo={
          paymentMode === 'vente' && dejaRegleTicket <= 0 ? handleVerifierPromo : undefined
        }
        promoMessage={promoMessage}
        promoLoading={promoLoading}
        error={paymentError}
        onClose={() => {
          setEncaissementTarget(null);
          setPaymentMode(null);
          resetPaiement();
          resetPromo();
        }}
        onConfirm={(check) => {
          if (encaissementTarget) confirmerEncaissement(check);
          else validerVente(check);
        }}
        processing={processing}
      />

      <CaisseRecompenseConfirmDialog
        open={fidelite.confirmOpen}
        clientLabel={fidelite.client ? [fidelite.client.prenom, fidelite.client.nom].filter(Boolean).join(' ') : null}
        reward={fidelite.pendingReward}
        quote={fidelite.quote}
        message={fidelite.confirmMessage}
        totalApres={
          fidelite.confirmOpen && fidelite.quote?.applicable
            ? estimateCaissePayableTotal({
                sousTotal: totalPanier,
                reductionPromo: Number(promoReduction) || 0,
                reductionAvantage: Number(fidelite.quote.reductionAvantage) || 0,
              })
            : null
        }
        onConfirm={fidelite.acceptConfirm}
        onCancel={fidelite.cancelConfirm}
        busy={fidelite.quoteLoading}
      />

      <CaisseProductModal
        produit={selectedProduct}
        fmt={fmt}
        quantitePanier={selectedProduct ? panierQtyMap[selectedProduct.id] || 0 : 0}
        onClose={() => setSelectedProduct(null)}
        onAdd={() => {
          if (!selectedProduct) return;
          if (hasMenuOptions(selectedProduct) || productNeedsCustomization(selectedProduct)) {
            handleProductTap(selectedProduct);
          } else {
            ajouterAuPanier(selectedProduct);
            setSelectedProduct(null);
          }
        }}
        onSetQuantite={(delta) => {
          if (!selectedProduct) return;
          const lines = panier.filter((l) => l.produitId === selectedProduct.id);
          if (lines.length === 0 && delta > 0) {
            handleProductTap(selectedProduct);
            return;
          }
          if (lines.length === 1) modifierQuantite(lines[0].lineKey, delta);
        }}
      />

      <CaisseOptionsModal
        produit={optionsProduct}
        fmt={fmt}
        onClose={() => setOptionsProduct(null)}
        onAddToPanier={({ produit, quantite, prixUnitaire, options, optionsLabel }) => {
          ajouterAuPanier(produit, { quantite, prixUnitaire, options, optionsLabel });
        }}
      />

      {poidsProduct && (
        <CaissePoidsModal
          key={poidsProduct.id}
          produit={poidsProduct}
          fmt={fmt}
          onClose={() => setPoidsProduct(null)}
          onConfirm={({ poidsSouhaite, typeDecoupe, quantite, prixUnitaire }) => {
            ajouterAuPanier(poidsProduct, {
              poidsSouhaite,
              typeDecoupe,
              quantite,
              prixUnitaire,
            });
            setPoidsProduct(null);
          }}
        />
      )}

      {view === MODES.vendre && (
        <div
          data-testid="caisse-vendre-shell"
          className={`pos-caisse-vendre-shell bg-slate-50 min-h-0 min-w-0 w-full max-w-full flex-1 flex flex-col self-stretch ${
            posImmersiveActive ? 'pos-caisse-vendre-shell--immersive' : ''
          }`}
        >
          <div className="pos-caisse-main-grid min-h-0 min-w-0 flex-1 w-full max-w-full">
          <div className="pos-caisse-catalog-pane flex flex-col min-w-0 min-h-0 border-r border-slate-200">
            {posImmersiveActive && (
              <div className="shrink-0 px-3 sm:px-4 pt-2 min-w-0 max-w-full border-b border-slate-100 bg-white">
                <PosOperationalAlertsBanner
                  health={operationalHealth}
                  commercantId={commercantId}
                  dismissed={operationalAlertsDismissed}
                  onDismiss={() => setOperationalAlertsDismissed(true)}
                />
                {showTicketCapabilityBanner && (
                  <div
                    className="mt-2 mb-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950"
                    data-testid="caisse-ticket-capability-banner"
                    role="status"
                  >
                    {activeTicketCapabilities.blockReason}
                  </div>
                )}
              </div>
            )}
            <header className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-slate-200 px-3 sm:px-4 py-3 min-w-0">
              <div className="pos-caisse-vendre-header mb-3 min-w-0">
                <div className="pos-caisse-vendre-header-main flex items-start gap-2 min-w-0">
                  <button
                    type="button"
                    data-testid="caisse-vendre-back"
                    onClick={() => {
                      leaveVendreView(() => {
                        if (restaurantReturn && posServiceSessionId) {
                          navigate(restaurantTableReturnUrl());
                          return;
                        }
                        setView(MODES.accueil);
                        setSearchQuery('');
                      });
                    }}
                    className="pos-touch-target shrink-0 p-2 rounded-xl hover:bg-slate-100 text-slate-600"
                    aria-label={posServiceSessionId ? 'Retour à la table' : 'Retour'}
                    title={posServiceSessionId ? 'Retour à la table' : 'Retour'}
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <div className="flex-1 min-w-0">
                    <h2
                      className="text-base sm:text-lg font-bold text-slate-900 break-words leading-snug"
                      data-testid="caisse-vendre-title"
                    >
                      {posServiceSessionId
                        ? 'Addition restaurant'
                        : posChannelMode
                          ? posChannelMode === 'telephone'
                            ? 'Commande téléphone'
                            : 'À emporter'
                          : 'Nouvelle vente'}
                    </h2>
                    {posServiceSessionId && (
                      <p className="text-xs text-emerald-700 font-medium" data-testid="caisse-restaurant-context">
                        {restaurantTableLabel || '—'} · Service en cours
                      </p>
                    )}
                    {!posServiceSessionId && posChannelMode && (
                      <p
                        className="text-xs text-orange-700 font-medium"
                        data-testid="caisse-channel-context"
                      >
                        Canal POS · {posChannelMode === 'telephone' ? 'téléphone' : 'comptoir emporter'}
                      </p>
                    )}
                    <p className="text-xs text-slate-500">
                      {catalogueTab === 'produits'
                        ? `${produitsFiltres.length} produits`
                        : `${prestationsFiltrees.length} prestations`}
                    </p>
                  </div>
                </div>
                {catalogueTab === 'produits' && (
                  <div
                    className="pos-caisse-vendre-header-catalog-mode flex rounded-xl border border-slate-200 p-0.5 bg-slate-50 shrink-0 w-fit max-w-full mt-2 sm:mt-0"
                    title="Mode d'affichage du catalogue"
                  >
                    <button
                      type="button"
                      onClick={() => setAffichageCataloguePersisted(AFFICHAGE_CATALOGUE.compact)}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition ${
                        affichageCatalogue === AFFICHAGE_CATALOGUE.compact
                          ? 'bg-white text-emerald-700 shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <List className="h-3.5 w-3.5" />
                      Compact
                    </button>
                    <button
                      type="button"
                      onClick={() => setAffichageCataloguePersisted(AFFICHAGE_CATALOGUE.menu)}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition ${
                        affichageCatalogue === AFFICHAGE_CATALOGUE.menu
                          ? 'bg-white text-emerald-700 shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <LayoutGrid className="h-3.5 w-3.5" />
                      Menu
                    </button>
                  </div>
                )}
              </div>
            </header>
            <div className="pos-caisse-catalog-toolbar shrink-0 px-3 sm:px-4 pb-3 border-b border-slate-100 bg-white min-w-0">
              <PosOfflinePanel
                commercantId={commercantId}
                catalogue={catalogue}
                demoProduitId={catalogue.produits?.[0]?.id}
                demoProduitNom={catalogue.produits?.[0]?.nom}
              />
              {(modulePrestations && catalogue.prestations?.length > 0) && (
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setCatalogueTab('produits')}
                    className={`flex-1 py-2 rounded-xl text-sm font-semibold ${
                      catalogueTab === 'produits'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    Produits
                  </button>
                  <button
                    type="button"
                    onClick={() => setCatalogueTab('prestations')}
                    className={`flex-1 py-2 rounded-xl text-sm font-semibold ${
                      catalogueTab === 'prestations'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    Prestations
                  </button>
                </div>
              )}
              {catalogueTab === 'produits' && (
                <>
                  <PosBarcodeManualEntry
                    disabled={!barcodeScannerEnabled || processing}
                    onSubmitGtin={processRetailGtinLookup}
                    onValidationError={(msg) => showToast(msg, 'error')}
                  />
                  <p
                    className="text-xs text-slate-500 mt-2 mb-2"
                    data-testid="pos-barcode-scan-hint"
                  >
                    Douchette USB/Bluetooth : scannez hors recherche nom — ou saisissez le code ci-dessus.
                  </p>
                </>
              )}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="search"
                  data-testid="caisse-catalogue-search"
                  placeholder={
                    catalogueTab === 'prestations' ? 'Rechercher une prestation…' : 'Rechercher un produit…'
                  }
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-emerald-500 outline-none text-sm"
                />
              </div>
              {catalogueTab === 'produits' && affichageCatalogue === AFFICHAGE_CATALOGUE.compact && categories.length > 1 && (
                <div className="flex gap-2 mt-3 overflow-x-auto pb-1 scrollbar-hide">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setCategorieFilter(cat)}
                      className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                        categorieFilter === cat
                          ? 'bg-emerald-600 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {cat === 'TOUS' ? 'Tous' : cat}
                    </button>
                  ))}
                </div>
              )}
              {catalogueTab === 'produits' && affichageCatalogue === AFFICHAGE_CATALOGUE.compact && sousCategories.length > 0 && (
                <div className="flex gap-2 mt-2 overflow-x-auto pb-1 scrollbar-hide">
                  <button
                    type="button"
                    onClick={() => setSousCategorieFilter('TOUS')}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition ${
                      sousCategorieFilter === 'TOUS'
                        ? 'bg-slate-700 text-white'
                        : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    Toute la catégorie
                  </button>
                  {sousCategories.map((sub) => (
                    <button
                      key={sub}
                      type="button"
                      onClick={() => setSousCategorieFilter(sub)}
                      className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition ${
                        sousCategorieFilter === sub
                          ? 'bg-slate-700 text-white'
                          : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      {sub}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="pos-caisse-catalog-scroll flex-1 overflow-y-auto overscroll-contain p-4">
              {catalogueTab === 'prestations' ? (
                prestationsFiltrees.length === 0 ? (
                  <p className="text-center text-slate-500 py-16">Aucune prestation</p>
                ) : (
                  <div className="pos-caisse-product-grid">
                    {prestationsFiltrees.map((pr) => (
                      <button
                        key={pr.id}
                        type="button"
                        onClick={() => ajouterPrestationAuPanier(pr)}
                        className="text-left bg-white rounded-2xl border-2 border-slate-100 p-4 hover:border-emerald-400 hover:shadow-lg transition"
                      >
                        <p className="font-semibold text-slate-900 line-clamp-2">{pr.nom}</p>
                        {pr.dureeMinutes && (
                          <p className="text-xs text-slate-500 mt-1">{pr.dureeMinutes} min</p>
                        )}
                        <p className="text-lg font-bold text-emerald-600 mt-2">{fmt(pr.prix)}</p>
                      </button>
                    ))}
                  </div>
                )
              ) : showMenuCategories ? (
                <div>
                  <p className="text-xs text-slate-500 mb-2">Rubriques</p>
                  <div className="pos-caisse-product-grid">
                    <MenuNavTile
                      label="Tous les produits"
                      count={catalogue.produits.length}
                      previewImages={rubriqueCoverImages.previewImages}
                      paletteIndex={0}
                      onClick={() => setMenuShowAll(true)}
                    />
                    {categoryMenuItems.map((cat, index) => (
                      <MenuNavTile
                        key={cat}
                        label={cat}
                        count={produitCountMaps.byCategorie.get(rubriqueCountKey(cat)) || 0}
                        imageUrl={rubriqueCoverImages.byCategorie.get(rubriqueCountKey(cat))}
                        paletteIndex={index + 1}
                        onClick={() => {
                          setMenuShowAll(false);
                          setCategorieFilter(cat);
                        }}
                      />
                    ))}
                  </div>
                </div>
              ) : showMenuSubcategories ? (
                <div>
                  <button
                    type="button"
                    onClick={menuNavigateBack}
                    className="inline-flex items-center gap-2 mb-4 px-3 py-2 rounded-xl text-sm font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {categorieFilter}
                  </button>
                  <p className="text-xs text-slate-500 mb-2">Sous-rubriques</p>
                  <div className="pos-caisse-product-grid">
                    <MenuNavTile
                      label="Toute la catégorie"
                      count={
                        produitCountMaps.byCategorie.get(rubriqueCountKey(categorieFilter)) || 0
                      }
                      imageUrl={rubriqueCoverImages.byCategorie.get(rubriqueCountKey(categorieFilter))}
                      paletteIndex={Math.max(0, categoryMenuItems.indexOf(categorieFilter)) + 1}
                      onClick={() => setSousCategorieFilter('__ALL__')}
                    />
                    {sousCategories.map((sub, index) => (
                      <MenuNavTile
                        key={sub}
                        label={sub}
                        count={
                          produitCountMaps.bySousCategorie.get(
                            `${rubriqueCountKey(categorieFilter)}::${rubriqueCountKey(sub)}`
                          ) || 0
                        }
                        imageUrl={rubriqueCoverImages.bySousCategorie.get(
                          `${rubriqueCountKey(categorieFilter)}::${rubriqueCountKey(sub)}`
                        )}
                        paletteIndex={index + 2}
                        onClick={() => setSousCategorieFilter(sub)}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {menuBreadcrumb && (
                    <button
                      type="button"
                      onClick={menuNavigateBack}
                      className="inline-flex items-center gap-2 mb-4 px-3 py-2 rounded-xl text-sm font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      {menuBreadcrumb}
                    </button>
                  )}
                  {produitsFiltres.length === 0 ? (
                    <p className="text-center text-slate-500 py-16">Aucun produit trouvé</p>
                  ) : (
                    <div className="pos-caisse-product-grid">
                      {produitsFiltres.map((p) => (
                        <ProductTile
                          key={p.id}
                          produit={p}
                          fmt={fmt}
                          qty={panierQtyMap[p.id] || 0}
                          onTap={handleProductTap}
                          onInfo={handleProductTap}
                          loading={loadingProductId === p.id}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <aside
            className="pos-caisse-cart-pane min-h-0 bg-white flex flex-col shadow-xl"
            data-testid="caisse-cart-pane"
            data-cart-empty={panier.length === 0 ? '1' : '0'}
          >
            <div
              className={`pos-caisse-cart-pane-header shrink-0 px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white ${
                cartIdentityExpanded ? '' : 'pos-caisse-cart-pane-header--collapsed'
              }`}
            >
              <h3 className="font-bold text-lg flex items-center gap-2 text-slate-900">
                <ShoppingCart className="h-5 w-5 text-emerald-600" />
                Panier
                {panier.length > 0 && (
                  <span className="ml-auto text-sm font-normal text-slate-500">
                    {panierArticleCount} art.
                  </span>
                )}
              </h3>
              <button
                type="button"
                data-testid="caisse-cart-identity-toggle"
                aria-expanded={cartIdentityExpanded}
                onClick={() => setCartIdentityExpanded((v) => !v)}
                className="pos-touch-target mt-2 w-full flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50"
              >
                <span className="truncate font-medium">{cartIdentitySummary}</span>
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-slate-500 transition-transform ${
                    cartIdentityExpanded ? 'rotate-180' : ''
                  }`}
                  aria-hidden
                />
              </button>
              {cartIdentityExpanded ? (
                <div
                  className="pos-caisse-cart-identity-details mt-3 space-y-3"
                  data-testid="caisse-cart-identity-details"
                >
                  <input
                    type="text"
                    placeholder="Nom libre (optionnel, sans fidélité)"
                    value={contactNom}
                    onChange={(e) => setContactNom(e.target.value)}
                    className="pos-caisse-cart-contact-nom w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:border-emerald-500 outline-none"
                  />
                  <div className="pos-caisse-cart-client-block">
                    <CaisseClientPicker
                      commercantId={commercantId}
                      selectedClient={selectedClient}
                      disabled={processing}
                      onSelect={(c) => {
                        setSelectedClient(c);
                        const label = [c.prenom, c.nom].filter(Boolean).join(' ');
                        if (label && !contactNom) setContactNom(label);
                      }}
                      onClear={() => {
                        setSelectedClient(null);
                        fidelite.clearAll();
                      }}
                    />
                  </div>
                  {fidelite.showPanel ? (
                    <CaisseRecompensePanel
                      solde={fidelite.solde}
                      recompenses={fidelite.recompenses}
                      loading={fidelite.catalogueLoading}
                      error={fidelite.catalogueError}
                      selectedRewardId={fidelite.selectedRewardId}
                      confirmedRewardId={fidelite.confirmedRewardId}
                      quote={fidelite.quote}
                      quoteLoading={fidelite.quoteLoading}
                      quoteError={fidelite.quoteError}
                      notice={fidelite.notice}
                      onKeepPoints={fidelite.selectKeepPoints}
                      onSelectReward={fidelite.requestSelectReward}
                      onDismissNotice={fidelite.dismissNotice}
                      disabled={processing}
                    />
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="pos-caisse-cart-scroll flex-1 overflow-y-auto overscroll-contain p-3 space-y-2">
              {panier.length === 0 ? (
                <div className="text-center py-16 px-4">
                  <ShoppingCart className="h-12 w-12 mx-auto text-slate-200 mb-3" />
                  <p className="text-slate-400 text-sm">Panier vide</p>
                  <p className="text-slate-400 text-xs mt-1">Un clic sur un produit pour l&apos;ajouter</p>
                </div>
              ) : (
                panier.map((l) => {
                  const kitchenLine = Boolean(posServiceSessionId && modulePosRestaurantKitchen);
                  const needsPrep = kitchenLine && panierLineNeedsKitchenSend(l);
                  const sentPrep = kitchenLine && panierLinePrepSent(l);
                  const selectedPrep = needsPrep && isPanierLineSelectedForPrepSend(l);
                  const rowInteractive = needsPrep && !sentPrep;
                  return (
                  <div
                    key={l.lineKey}
                    role={rowInteractive ? 'button' : undefined}
                    tabIndex={rowInteractive ? 0 : undefined}
                    onClick={
                      rowInteractive
                        ? () => togglePanierPrepSelection(l.lineKey)
                        : undefined
                    }
                    onKeyDown={
                      rowInteractive
                        ? (ev) => {
                            if (ev.key === 'Enter' || ev.key === ' ') {
                              ev.preventDefault();
                              togglePanierPrepSelection(l.lineKey);
                            }
                          }
                        : undefined
                    }
                    data-testid={
                      needsPrep ? `caisse-panier-prep-line-${l.lineKey}` : undefined
                    }
                    className={`flex gap-3 p-2 rounded-xl border transition-colors ${
                      sentPrep
                        ? 'bg-slate-100 border-slate-200 opacity-90'
                        : selectedPrep
                          ? 'bg-emerald-50 border-emerald-300 ring-1 ring-emerald-200'
                          : needsPrep
                            ? 'bg-white border-slate-200 border-dashed'
                            : 'bg-slate-50 border-slate-100'
                    } ${rowInteractive ? 'cursor-pointer' : ''}`}
                  >
                    <div className="w-14 h-14 rounded-lg bg-slate-200 overflow-hidden shrink-0">
                      {l.imageUrl ? (
                        <img src={l.imageUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">
                          —
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col">
                      <div className="flex items-start justify-between gap-3">
                        <p className="font-semibold text-sm text-slate-900 leading-tight min-w-0 pr-1">
                          {l.nom}
                          {needsPrep && !sentPrep && (
                            <span
                              className={`ml-2 text-[10px] font-bold uppercase tracking-wide ${
                                selectedPrep ? 'text-emerald-700' : 'text-amber-700'
                              }`}
                            >
                              {selectedPrep ? 'Envoi' : 'À suivre'}
                            </span>
                          )}
                          {sentPrep && (
                            <span className="ml-2 text-[10px] font-bold uppercase text-slate-500">
                              Envoyé
                            </span>
                          )}
                        </p>
                        <p className="text-emerald-600 font-bold text-sm shrink-0 tabular-nums text-right">
                          {fmt(l.prixUnitaire * l.quantite)}
                        </p>
                      </div>
                      <CaissePanierOptions options={l.options} />
                      {l.produitId && posStockDisplayByProduitId.get(l.produitId) ? (
                        <PosRetailStockHint
                          display={posStockDisplayByProduitId.get(l.produitId)}
                          className="!mt-0.5"
                        />
                      ) : null}
                      {l.poidsSouhaite ? (
                        <p className="text-xs text-slate-500 mt-0.5">
                          {l.poidsSouhaite} g{l.typeDecoupe ? ` · ${l.typeDecoupe}` : ''}
                        </p>
                      ) : null}
                      <div className="flex items-center gap-1 mt-1.5">
                        <button
                          type="button"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            modifierQuantite(l.lineKey, -1);
                          }}
                          className="p-1 rounded-md bg-white border border-slate-200"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-6 text-center text-sm font-bold">{l.quantite}</span>
                        <button
                          type="button"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            modifierQuantite(l.lineKey, 1);
                          }}
                          className="p-1 rounded-md bg-white border border-slate-200"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          data-testid="caisse-panier-supprimer-ligne"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            modifierQuantite(l.lineKey, -999);
                          }}
                          className="ml-auto p-1 text-red-500 hover:bg-red-50 rounded"
                          aria-label="Supprimer la ligne"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  );
                })
              )}
            </div>

            <div
              className="pos-caisse-cart-footer shrink-0 p-3 sm:p-4 border-t border-slate-200 bg-white"
              data-testid="caisse-cart-footer"
            >
              {activeTicketId && (
                <p className="text-xs text-orange-600 font-semibold mb-2 flex items-center gap-1">
                  <PauseCircle className="h-3.5 w-3.5" />
                  Ticket en cours — repris
                </p>
              )}
              <div className="pos-caisse-cart-footer-compact">
                <div className="flex justify-between items-start gap-3 mb-2">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-slate-500">
                      Total · {panierArticleCount} art.
                    </p>
                    {posServiceSessionId &&
                      modulePosRestaurantKitchen &&
                      prepSendAllPendingCount > 0 && (
                        <p
                          className="text-[11px] text-amber-800 font-semibold mt-0.5"
                          data-testid="caisse-cart-prep-pending"
                        >
                          {prepSendAllPendingCount} ligne(s) à envoyer cuisine
                        </p>
                      )}
                  </div>
                  <span
                    className="text-xl sm:text-2xl font-bold text-slate-900 tabular-nums shrink-0"
                    data-testid="caisse-cart-total"
                  >
                    {fmt(totalPanier)}
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  {posServiceSessionId && modulePosRestaurantKitchen && (
                    <button
                      type="button"
                      disabled={
                        processing ||
                        prepSendSelectionCount === 0 ||
                        (panier.length === 0 && !activeTicketId)
                      }
                      className="w-full py-3 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 disabled:opacity-40 flex items-center justify-center gap-2 pos-touch-target"
                      data-testid="caisse-send-kitchen-selection"
                      onClick={envoyerSelectionPreparationRestaurant}
                    >
                      <ChefHat className="h-5 w-5" />
                      Envoyer la sélection ({prepSendSelectionCount})
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleStartEncaissementPanier}
                    disabled={panier.length === 0}
                    data-testid="caisse-encaisser-panier"
                    className={`w-full py-3 font-semibold rounded-xl disabled:opacity-40 pos-touch-target flex items-center justify-center gap-2 ${
                      posServiceSessionId
                        ? 'border-2 border-emerald-600 text-emerald-800 hover:bg-emerald-50'
                        : 'bg-emerald-600 text-white font-bold hover:bg-emerald-700 shadow-lg shadow-emerald-600/25 text-lg'
                    }`}
                  >
                    Encaisser
                  </button>
                  {(posServiceSessionId ||
                    !activeTicketId ||
                    activeTicketId ||
                    (activeTicketId && posChannelMode && modulePosRestaurantKitchen)) && (
                    <button
                      type="button"
                      data-testid="caisse-cart-actions-toggle"
                      aria-expanded={cartActionsExpanded}
                      onClick={() => setCartActionsExpanded((v) => !v)}
                      className="pos-touch-target w-full py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-700 font-semibold text-sm flex items-center justify-center gap-2 hover:bg-slate-100"
                    >
                      Autres actions
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${cartActionsExpanded ? 'rotate-180' : ''}`}
                        aria-hidden
                      />
                    </button>
                  )}
                </div>
              </div>
              {cartActionsExpanded ? (
                <div
                  className="pos-caisse-cart-footer-expanded mt-2 pt-2 border-t border-slate-100 space-y-2"
                  data-testid="caisse-cart-actions-expanded"
                >
                  {posServiceSessionId ? (
                    <>
                      <button
                        type="button"
                        onClick={enregistrerCommandeRestaurant}
                        disabled={panier.length === 0 || processing}
                        data-testid="caisse-mettre-en-attente"
                        className="w-full py-3 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 disabled:opacity-40 flex items-center justify-center gap-2 pos-touch-target"
                      >
                        <PauseCircle className="h-5 w-5" />
                        Valider la commande
                      </button>
                      <p className="text-xs text-slate-500 text-center px-1">
                        Enregistrer sans envoyer en cuisine.
                      </p>
                      {modulePosRestaurantKitchen && (
                        <>
                          <button
                            type="button"
                            disabled={
                              processing ||
                              prepSendAllPendingCount === 0 ||
                              (panier.length === 0 && !activeTicketId)
                            }
                            className="w-full py-3 bg-orange-600 text-white font-semibold rounded-xl hover:bg-orange-700 disabled:opacity-40 flex items-center justify-center gap-2 pos-touch-target"
                            data-testid="caisse-send-kitchen-all"
                            onClick={envoyerToutPreparationRestaurant}
                          >
                            <ChefHat className="h-5 w-5" />
                            Tout envoyer ({prepSendAllPendingCount})
                          </button>
                          <p className="text-xs text-slate-500 text-center px-1">
                            Touchez une ligne pour la sélectionner (vert) ou la garder à suivre.
                          </p>
                        </>
                      )}
                      <p className="text-xs text-slate-500 text-center px-1">
                        Régler l&apos;addition — distinct de la cuisine.
                      </p>
                      {activeTicketId && (
                        <button
                          type="button"
                          onClick={emettreAddition}
                          disabled={processing || panier.length === 0}
                          className="w-full py-2.5 text-sm text-slate-600 underline disabled:opacity-40 pos-touch-target"
                          data-testid="caisse-emettre-addition"
                          title="Émission fiscale addition serveur"
                        >
                          Émettre addition (fiscal)
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      {!activeTicketId && (
                        <button
                          type="button"
                          onClick={mettreEnAttente}
                          disabled={panier.length === 0 || processing}
                          data-testid="caisse-mettre-en-attente"
                          className="w-full py-3 bg-orange-500 text-white font-semibold rounded-xl hover:bg-orange-600 disabled:opacity-40 flex items-center justify-center gap-2 pos-touch-target"
                        >
                          <PauseCircle className="h-5 w-5" />
                          Mettre en attente
                        </button>
                      )}
                      {activeTicketId && (
                        <button
                          type="button"
                          onClick={emettreAddition}
                          disabled={processing || panier.length === 0}
                          className="w-full py-3 bg-slate-700 text-white font-semibold rounded-xl hover:bg-slate-800 disabled:opacity-40 flex items-center justify-center gap-2 pos-touch-target"
                          data-testid="caisse-emettre-addition"
                          title="Émet une addition serveur protégée (pas une prévisualisation)"
                        >
                          <Receipt className="h-5 w-5" />
                          Émettre addition
                        </button>
                      )}
                      {activeTicketId &&
                        posChannelMode &&
                        modulePosRestaurantKitchen && (
                          <button
                            type="button"
                            disabled={processing}
                            className="w-full py-3 bg-slate-900 text-white font-semibold rounded-xl hover:bg-slate-800 disabled:opacity-40 flex items-center justify-center gap-2 pos-touch-target"
                            data-testid="caisse-send-kitchen"
                            onClick={() => sendActiveCommandeToKitchen(activeTicketId)}
                          >
                            <ChefHat className="h-5 w-5" />
                            Envoyer en cuisine
                          </button>
                        )}
                    </>
                  )}
                </div>
              ) : null}
            </div>
          </aside>
          </div>
        </div>
      )}

      {view === MODES.encaisser && (
        <div className="pos-caisse-work-surface" data-testid="caisse-encaisser-view">
          <header className="pos-caisse-work-surface-header flex flex-wrap items-center justify-between gap-3 mb-6">
            <button
              type="button"
              onClick={() => setView(MODES.accueil)}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="h-5 w-5" /> Retour
            </button>
            <h2 className="text-xl font-bold">À encaisser</h2>
            <button type="button" onClick={loadAEncaisser} className="p-2 rounded-xl hover:bg-slate-100">
              <RefreshCw className="h-5 w-5 text-emerald-600" />
            </button>
          </header>
          {aEncaisser.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl border border-slate-100">
              <Receipt className="h-12 w-12 mx-auto text-slate-200 mb-3" />
              <p className="text-slate-500">Rien à encaisser pour le moment</p>
            </div>
          ) : (
            <div className="space-y-3">
              {aEncaisser.map((item) => {
                const Icon = TYPE_ICONS[item.type] || Receipt;
                return (
                  <div
                    key={`${item.type}-${item.id}`}
                    className="bg-white rounded-2xl border border-slate-100 p-4 flex gap-4 shadow-sm hover:shadow-md transition"
                  >
                    <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                      <Icon className="h-6 w-6 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-[10px] font-bold uppercase text-slate-400">{item.type}</span>
                      <p className="font-semibold text-slate-900">{item.label}</p>
                      <p className="text-sm text-slate-500">{item.client || '—'}</p>
                      {item.lignes?.length > 0 && (
                        <ul className="mt-2 text-xs text-slate-600 space-y-0.5">
                          {item.lignes.slice(0, 4).map((l, i) => (
                            <li key={i}>
                              {l.nom} ×{l.quantite}
                              {l.optionsText ? (
                                <span className="block text-slate-400 pl-2 whitespace-pre-line">
                                  {l.optionsText}
                                </span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      {item.montant != null && (
                        <span className="text-lg font-bold text-slate-900">{fmt(item.montant)}</span>
                      )}
                      {item.actions?.includes('encaisser') && (
                        <button
                          type="button"
                          onClick={() => {
                            setEncaissementTarget(item);
                            resetPaiement();
                          }}
                          className="px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-xl hover:bg-emerald-700"
                        >
                          Encaisser
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {view === MODES.cloture && (
        <div className="pos-caisse-session-shell w-full" data-testid="caisse-cloture-view">
          <button
            type="button"
            onClick={() => setView(MODES.accueil)}
            className="pos-touch-target flex items-center gap-2 text-slate-600 mb-4 min-h-[44px] px-2 rounded-xl hover:bg-slate-100 max-w-3xl mx-auto w-full"
          >
            <ArrowLeft className="h-5 w-5" /> Retour
          </button>
          <div className="pos-caisse-session-form-card bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
            <div className="bg-slate-800 px-6 py-5 text-white">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Lock className="h-6 w-6" /> Clôturer la caisse
              </h2>
            </div>
            <div className="p-6">
              {error && (
                <p
                  data-testid="caisse-cloture-error"
                  className="text-red-700 text-sm mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3"
                >
                  {error}
                </p>
              )}
              <div className="bg-slate-50 rounded-xl p-4 mb-4 space-y-3">
                <div>
                  <p className="text-sm text-slate-600">Chiffre d&apos;affaires (ventes encaissées)</p>
                  <p className="text-2xl font-bold text-slate-900">{fmt(sessionRecap?.totalNet ?? sessionRecap?.totalVentes ?? 0)}</p>
                  <p className="text-xs text-slate-500">{sessionRecap?.ventesCount ?? 0} vente(s){sessionRecap?.remboursements?.count > 0 ? ` · ${sessionRecap.remboursements.count} remb.` : ''}</p>
                </div>
                <div className="border-t border-slate-200 pt-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                    Répartition des ventes
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-white rounded-lg px-3 py-2">
                      <span className="text-slate-500 text-xs">Espèces (ventes)</span>
                      <p className="font-semibold">
                        {ventesEspecesAffichees != null ? fmt(ventesEspecesAffichees) : '—'}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg px-3 py-2">
                      <span className="text-slate-500 text-xs">Carte</span>
                      <p className="font-semibold">{fmt(sessionRecap?.totalCarte ?? 0)}</p>
                    </div>
                    {(sessionRecap?.totalCheque > 0 || sessionRecap?.totalVirement > 0) && (
                      <>
                        <div className="bg-white rounded-lg px-3 py-2">
                          <span className="text-slate-500 text-xs">Chèque</span>
                          <p className="font-semibold">{fmt(sessionRecap?.totalCheque ?? 0)}</p>
                        </div>
                        <div className="bg-white rounded-lg px-3 py-2">
                          <span className="text-slate-500 text-xs">Virement</span>
                          <p className="font-semibold">{fmt(sessionRecap?.totalVirement ?? 0)}</p>
                        </div>
                      </>
                    )}
                  </div>
                  {sessionRecap?.repartitionOk === false && (
                    <p className="text-xs text-amber-700 mt-2">
                      Écart de répartition : {fmt(sessionRecap?.ecartRepartition ?? 0)} — vérifiez les
                      anciennes ventes sans détail de paiement.
                    </p>
                  )}
                </div>
              </div>

              {sessionRecap?.remboursements?.count > 0 && (
                <div
                  className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4 space-y-2"
                  data-testid="caisse-cloture-remboursements"
                >
                  <h4 className="text-sm font-semibold text-orange-700 flex items-center gap-1.5">
                    <RotateCcw className="h-4 w-4" /> Remboursements (session courante)
                  </h4>
                  <div className="flex justify-between text-sm">
                    <span className="text-orange-600">Nombre</span>
                    <span className="font-bold text-orange-800">{sessionRecap.remboursements.count}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-orange-600">Total remboursé</span>
                    <span className="font-bold text-orange-800">-{fmt(sessionRecap.remboursements.total)}</span>
                  </div>
                  {sessionRecap.remboursements.especes > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-orange-600">dont espèces</span>
                      <span className="font-bold">-{fmt(sessionRecap.remboursements.especes)}</span>
                    </div>
                  )}
                  {sessionRecap.remboursements.carte > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-orange-600">dont carte</span>
                      <span className="font-bold">-{fmt(sessionRecap.remboursements.carte)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm border-t border-orange-200 pt-2">
                    <span className="text-orange-700 font-semibold">CA net (session)</span>
                    <span className="font-bold text-orange-900">
                      {fmt(sessionRecap.totalNet ?? sessionRecap.totalVentes)}
                    </span>
                  </div>
                </div>
              )}

              {closeOperationalPreview?.openRestaurantTables?.length > 0 && (
                <div
                  data-testid="caisse-cloture-restaurant-recap"
                  className="bg-sky-50 border border-sky-200 rounded-xl p-4 mb-4 text-sm text-sky-950"
                >
                  <p className="font-bold mb-2">
                    Services restaurant encore ouverts ({closeOperationalPreview.openRestaurantTables.length})
                  </p>
                  <p className="text-xs text-sky-800 mb-2">
                    La clôture de session de poste est distincte de la fermeture de table. Les tables
                    avec solde dû bloquent la clôture caisse.
                  </p>
                  <ul className="space-y-2">
                    {closeOperationalPreview.openRestaurantTables.map((s) => (
                      <li key={s.sessionId} className="flex flex-wrap items-center gap-2">
                        <span>
                          Table {s.tableLabel || '—'}
                          {s.hasFinancialBlockers ? ' · solde dû' : ' · libérable'}
                        </span>
                        <Link
                          to={
                            s.deepLinkPath
                            || `/caisse/restaurant?commercantId=${commercantId}`
                          }
                          className="font-semibold underline min-h-[44px] inline-flex items-center"
                        >
                          Ouvrir
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-900 mb-2">
                  Contrôle du tiroir espèces
                </p>
                <div className="space-y-1 text-sm text-amber-950">
                  <div className="flex justify-between">
                    <span>Fond de caisse (ouverture)</span>
                    <span className="font-medium">{fmt(fondRecap)}</span>
                  </div>
                  {ventesEspecesAffichees != null ? (
                    <div className="flex justify-between">
                      <span>+ Encaissements espèces (ventes)</span>
                      <span className="font-medium">{fmt(ventesEspecesAffichees)}</span>
                    </div>
                  ) : (
                    <p className="text-xs text-amber-800">
                      Détail ventes espèces indisponible — le total attendu inclut les remboursements.
                    </p>
                  )}
                  {remboursementsEspecesCloture > 0.001 && (
                    <div className="flex justify-between text-red-800">
                      <span>− Remboursements espèces</span>
                      <span className="font-medium">-{fmt(remboursementsEspecesCloture)}</span>
                    </div>
                  )}
                  {Math.abs(tresorerieNetCloture) > 0.001 && (
                    <div className="flex justify-between">
                      <span>{tresorerieNetCloture >= 0 ? '+ Apports trésorerie' : '− Retraits trésorerie'}</span>
                      <span className="font-medium">
                        {tresorerieNetCloture >= 0 ? '+' : ''}
                        {fmt(tresorerieNetCloture)}
                      </span>
                    </div>
                  )}
                  <div
                    className={`flex justify-between pt-2 border-t border-amber-300 font-bold text-base ${
                      especesEnCaisseAttendues < -0.001 ? 'text-red-800' : ''
                    }`}
                  >
                    <span>= Espèces théoriquement attendues</span>
                    <span data-testid="caisse-cloture-especes-attendues">{fmt(especesEnCaisseAttendues)}</span>
                  </div>
                  {especesEnCaisseAttendues < -0.001 && (
                    <p className="text-xs text-red-800 pt-1">
                      Solde théorique négatif : manque de liquidités dans le tiroir (distinct de l&apos;écart
                      au comptage).
                    </p>
                  )}
                </div>
              </div>

              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Espèces comptées dans le tiroir
              </label>
              {especesEnCaisseAttendues < -0.001 && (
                <p className="text-xs text-slate-600 mb-2">
                  Solde théorique négatif ({fmt(especesEnCaisseAttendues)}) : indiquez les espèces
                  réellement présentes (souvent 0 €). L&apos;écart sera positif ; un motif reste
                  obligatoire.
                </p>
              )}
              <input
                type="number"
                min="0"
                step="0.01"
                data-testid="caisse-cloture-especes-comptees"
                value={especesComptees}
                onChange={(e) => setEspecesComptees(e.target.value)}
                onBlur={(e) => {
                  const v = parseFloat(e.target.value);
                  if (!Number.isNaN(v)) setEspecesComptees(String(Math.round(v * 100) / 100));
                }}
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 mb-2 focus:border-red-400 outline-none"
              />
              {ecartAffiche != null && !Number.isNaN(ecartAffiche) && Math.abs(ecartAffiche) > 0.01 && (
                <>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Motif de l&apos;écart (obligatoire)
                  </label>
                  <textarea
                    data-testid="caisse-cloture-motif-ecart"
                    value={motifEcartCaisse}
                    onChange={(e) => setMotifEcartCaisse(e.target.value)}
                    rows={2}
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 mb-2 focus:border-red-400 outline-none text-sm"
                    placeholder="Ex. pièces manquantes, erreur de rendu monnaie…"
                  />
                </>
              )}
              {ecartAffiche != null && !Number.isNaN(ecartAffiche) && (
                <p
                  className={`text-sm font-semibold mb-4 ${
                    ecartAffiche === 0
                      ? 'text-emerald-700'
                      : ecartAffiche > 0
                        ? 'text-amber-700'
                        : 'text-red-600'
                  }`}
                >
                  Écart caisse : {ecartAffiche >= 0 ? '+' : ''}
                  {fmt(ecartAffiche)}
                </p>
              )}
              {restaurantCloseWarning && (
                <div
                  data-testid="caisse-cloture-restaurant-blocked"
                  className="bg-red-50 border-2 border-red-300 rounded-xl p-4 mb-4"
                >
                  <p className="text-red-800 font-bold mb-2">
                    {restaurantCloseWarning.count || restaurantCloseWarning.sessions?.length}{' '}
                    service(s) restaurant avec solde dû
                  </p>
                  <p className="text-sm text-red-700 mb-3">
                    Encaissez ou libérez les tables concernées avant de clôturer la session de
                    caisse. Aucune fermeture automatique.
                  </p>
                  <ul className="text-sm text-red-700 mb-3 space-y-2">
                    {(restaurantCloseWarning.sessions || []).map((s) => (
                      <li key={s.sessionId}>
                        Table {s.tableLabel || '—'}
                        {' · '}
                        <Link
                          to={
                            s.deepLinkPath
                            || `/caisse/restaurant?commercantId=${commercantId}`
                          }
                          className="font-semibold underline"
                        >
                          Régler sur le plan
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => setRestaurantCloseWarning(null)}
                    className="w-full py-2 bg-white border border-slate-300 text-slate-700 font-semibold rounded-lg"
                  >
                    Retour
                  </button>
                </div>
              )}
              {fermetureWarning && (
                <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 mb-4">
                  <p className="text-red-800 font-bold mb-2">
                    {fermetureWarning.count} ticket(s) ouvert(s) non encaissé(s)
                  </p>
                  <p className="text-sm text-red-700 mb-3">
                    Total en attente : {fmt(fermetureWarning.totalOuvert || 0)}
                  </p>
                  <ul className="text-sm text-red-700 mb-3 space-y-1">
                    {(fermetureWarning.ticketsOuverts || []).map((t) => (
                      <li key={t.id}>
                        {formatCaisseOpenTicketTitle(t)} — {fmt(t.total || 0)}
                      </li>
                    ))}
                  </ul>
                  <label className="block text-sm font-semibold text-red-900 mb-1">
                    Motif de la fermeture forcée
                  </label>
                  <textarea
                    data-testid="caisse-cloture-force-motif"
                    value={forceCloseMotif}
                    onChange={(e) => setForceCloseMotif(e.target.value)}
                    rows={2}
                    className="w-full border border-red-300 rounded-lg px-3 py-2 mb-3 text-sm"
                    placeholder="Pourquoi annuler les tickets ouverts ?"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setFermetureWarning(null);
                        setView(MODES.accueil);
                      }}
                      className="flex-1 py-2 bg-white border border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50"
                    >
                      Retour à la caisse
                    </button>
                    {posCanSessionForce && (
                    <button
                      type="button"
                      onClick={() => handleFermerSession(true)}
                      disabled={processing}
                      className="flex-1 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      Annuler les tickets et fermer
                    </button>
                    )}
                  </div>
                </div>
              )}
              {!fermetureWarning && !restaurantCloseWarning && (
                <button
                  type="button"
                  data-testid="caisse-cloture-continuer"
                  onClick={() => setShowClotureConfirm(true)}
                  disabled={
                    processing
                    || closeOperationalPreview?.canCloseWithoutForce === false
                  }
                  className="w-full py-4 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 disabled:opacity-50"
                >
                  Fermer la caisse
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {view === MODES.fiscalClosures && (
        <FiscalClosuresPanel
          commercantId={commercantId}
          onBack={() => setView(MODES.accueil)}
          fiscalClosures={fiscalClosures}
          fiscalPerpetual={fiscalPerpetual}
          fiscalArchives={fiscalArchives}
          fiscalClosePeriodKey={fiscalClosePeriodKey}
          setFiscalClosePeriodKey={setFiscalClosePeriodKey}
          fiscalCloseBusy={fiscalCloseBusy}
          fiscalCloseMsg={fiscalCloseMsg}
          fiscalArchiveBusy={fiscalArchiveBusy}
          fiscalArchiveMsg={fiscalArchiveMsg}
          onRefresh={loadFiscalClosures}
          onCloseDaily={runCloseFiscalDaily}
          onGenerateArchive={runGenerateFiscalArchive}
          onDownloadArchive={async (archiveId) => {
            try {
              await apiService.downloadFiscalArchive(archiveId, commercantId);
            } catch (e) {
              setFiscalArchiveMsg(e.message || 'Téléchargement impossible');
            }
          }}
        />
      )}

      {view === MODES.fiscalDocuments && (
        <FiscalDocumentsPanel
          commercantId={commercantId}
          onBack={() => setView(MODES.accueil)}
        />
      )}

      {view === MODES.tresorerie && (
        <div className="pos-caisse-work-surface" data-testid="caisse-tresorerie-view">
          <header className="pos-caisse-work-surface-header flex flex-wrap items-center justify-between gap-3 mb-6">
            <button
              type="button"
              onClick={() => setView(MODES.accueil)}
              className="pos-touch-target flex items-center gap-2 text-slate-600 hover:text-slate-900 min-h-[44px] px-2 rounded-xl hover:bg-slate-100"
            >
              <ArrowLeft className="h-5 w-5" /> Retour
            </button>
            <h2 className="text-xl font-bold text-slate-900 flex-1 min-w-[8rem]">Trésorerie tiroir</h2>
            <button type="button" onClick={loadTresorerie} className="pos-touch-target p-2 rounded-xl hover:bg-slate-100 min-h-[44px] min-w-[44px] flex items-center justify-center">
              <RefreshCw className="h-5 w-5 text-emerald-600" />
            </button>
          </header>

          <div className="bg-white rounded-2xl border border-slate-100 p-5 mb-6 shadow-sm">
            <p className="text-sm text-slate-500 mb-1">Espèces théoriques (hors CA)</p>
            <p className="text-2xl font-bold text-slate-900" data-testid="caisse-tresorerie-especes-attendues">
              {fmt(especesEnCaisseAttendues)}
            </p>
            {sessionRecap?.tresorerie?.count > 0 && (
              <p className="text-xs text-slate-500 mt-1">
                Mouvements trésorerie : {sessionRecap.tresorerie.count} · net{' '}
                {sessionRecap.tresorerie.net >= 0 ? '+' : ''}
                {fmt(sessionRecap.tresorerie.net)}
              </p>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-5 mb-6 shadow-sm space-y-4">
            <h3 className="font-semibold text-slate-800">Nouveau mouvement</h3>
            <select
              data-testid="caisse-tresorerie-type"
              className="w-full border border-slate-200 rounded-xl px-3 py-2"
              value={tresorerieType}
              onChange={(e) => setTresorerieType(e.target.value)}
            >
              <option value="AJOUT_MONNAIE">Ajout de monnaie / espèces</option>
              <option value="RETRAIT_ESPECES">Retrait espèces du tiroir</option>
              <option value="PRELEVEMENT_COFFRE">Prélèvement vers coffre</option>
              <option value="REMISE_BANQUE">Remise en banque</option>
            </select>
            <input
              data-testid="caisse-tresorerie-montant"
              type="number"
              min="0"
              step="0.01"
              placeholder="Montant"
              className="w-full border border-slate-200 rounded-xl px-3 py-2"
              value={tresorerieMontant}
              onChange={(e) => setTresorerieMontant(e.target.value)}
            />
            <input
              data-testid="caisse-tresorerie-motif"
              type="text"
              placeholder="Motif (obligatoire)"
              className="w-full border border-slate-200 rounded-xl px-3 py-2"
              value={tresorerieMotif}
              onChange={(e) => setTresorerieMotif(e.target.value)}
            />
            <button
              type="button"
              data-testid="caisse-tresorerie-submit"
              disabled={tresorerieBusy}
              onClick={submitTresorerieMouvement}
              className="w-full py-3 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50"
            >
              Enregistrer le mouvement
            </button>
          </div>

          <div className="space-y-3">
            {tresorerieMouvements.length === 0 ? (
              <p className="text-center text-slate-500 py-8">Aucun mouvement sur cette session</p>
            ) : (
              tresorerieMouvements.map((m) => (
                <div
                  key={m.id}
                  className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm"
                  data-testid="caisse-tresorerie-ligne"
                >
                  <div className="flex justify-between gap-2">
                    <div>
                      <p className="font-semibold text-slate-900">{m.type.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-slate-500">
                        {new Date(m.createdAt).toLocaleString('fr-FR')} ·{' '}
                        {[m.effectuePar?.prenom, m.effectuePar?.nom].filter(Boolean).join(' ') ||
                          m.effectuePar?.email}
                      </p>
                      <p className="text-sm text-slate-600 mt-1">{m.motif}</p>
                    </div>
                    <div className="text-right">
                      <span
                        className={`text-lg font-bold ${
                          m.montantSigned >= 0 ? 'text-emerald-700' : 'text-red-700'
                        }`}
                      >
                        {m.montantSigned >= 0 ? '+' : ''}
                        {fmt(m.montantSigned)}
                      </span>
                      {m.type !== 'CONTRE_PASSATION' && !m.contrepassePar && (
                        <button
                          type="button"
                          className="block text-xs text-violet-600 mt-2 hover:underline"
                          onClick={() => contrepasserTresorerie(m.id)}
                          disabled={tresorerieBusy}
                        >
                          Contrepasser
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {view === MODES.retoursUnifies && (
        <UnifiedReturnExchangePanel
          commercantId={commercantId}
          devise={devise}
          onBack={() => setView(MODES.accueil)}
          onOpenRetailReturn={
            posCanRemboursement
              ? () => {
                  setRetailReturnUnifiedHandoff(null);
                  setView(MODES.retourRetail);
                }
              : undefined
          }
          onNavigateToPosRetailReturn={
            posCanRemboursement
              ? (ticketId) => {
                  setRetailReturnUnifiedHandoff({ ticketId, returnTo: 'unified' });
                  setView(MODES.retourRetail);
                }
              : undefined
          }
          onNavigateToWebEncaisseRefund={
            posCanRemboursement ? navigateToWebEncaisseRefundFromUnified : undefined
          }
          onOpenMesCommandesForStripe={
            isU2Ux1StripeMesCommandesFromUnifiedEnabled()
              ? openMesCommandesFromUnifiedStripe
              : undefined
          }
          canOpenMesCommandes={canOpenMesCommandesFromCaisse}
          showToast={showToast}
        />
      )}

      {view === MODES.retourRetail && (
        <PosRetailReturnPanel
          commercantId={commercantId}
          commercantProfile={commercantProfile}
          devise={devise}
          initialTicketId={retailReturnUnifiedHandoff?.ticketId ?? null}
          prefilledFromUnified={retailReturnUnifiedHandoff?.returnTo === 'unified'}
          onBack={() => {
            if (retailReturnUnifiedHandoff?.returnTo === 'unified') {
              setRetailReturnUnifiedHandoff(null);
              setView(MODES.retoursUnifies);
              return;
            }
            setRetailReturnUnifiedHandoff(null);
            setView(MODES.accueil);
          }}
          onSuccess={() => {
            loadSession();
            if (retailReturnUnifiedHandoff?.returnTo === 'unified') {
              setRetailReturnUnifiedHandoff(null);
              setView(MODES.retoursUnifies);
            }
          }}
          showToast={showToast}
        />
      )}

      {view === MODES.historique && (
        <div className="pos-caisse-work-surface" data-testid="caisse-historique-view">
          <header className="pos-caisse-work-surface-header flex flex-wrap items-center justify-between gap-3 mb-6">
            <button
              type="button"
              onClick={() => setView(MODES.accueil)}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="h-5 w-5" /> Retour
            </button>
            <h2 className="text-xl font-bold">Historique session</h2>
            <button type="button" onClick={loadHistorique} className="p-2 rounded-xl hover:bg-slate-100">
              <RefreshCw className="h-5 w-5 text-emerald-600" />
            </button>
          </header>
          {historique.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl border border-slate-100">
              <History className="h-12 w-12 mx-auto text-slate-200 mb-3" />
              <p className="text-slate-500">Aucune vente sur cette session</p>
            </div>
          ) : (
            <div className="space-y-3">
              {historique.map((cmd) => {
                const statutLabel = cmd.statut === 'ANNULEE' ? 'Annulé' : cmd.statut === 'EN_ATTENTE' ? 'En attente' : 'Payé';
                const statutColor = cmd.statut === 'ANNULEE' ? 'bg-red-100 text-red-700' : cmd.statut === 'EN_ATTENTE' ? 'bg-orange-100 text-orange-700' : 'bg-emerald-100 text-emerald-700';
                const refunds = (cmd.paiements || []).filter((p) => p.statutPaiement === 'REMBOURSE' && p.montant < 0);
                const totalRembourse = refunds.reduce((s, p) => s + Math.abs(p.montant), 0);
                const hasRefund = totalRembourse > 0;
                const totalNet = hasRefund ? cmd.total - totalRembourse : cmd.total;
                return (
                  <button
                    key={cmd.id}
                    type="button"
                    data-testid={`caisse-historique-ticket-${cmd.id}`}
                    onClick={() => voirDetailTicket(cmd.id)}
                    className="w-full text-left bg-white rounded-2xl border border-slate-100 p-4 shadow-sm hover:border-violet-300 hover:shadow-md transition-all"
                  >
                    <div className="flex justify-between items-start gap-3">
                      <div>
                        <p className="font-semibold text-slate-900">
                          {cmd.numeroTicket || cmd.numero}
                        </p>
                        <p className="text-xs text-slate-500">
                          {new Date(cmd.dateCommande).toLocaleString('fr-FR')} ·{' '}
                          {cmd.methodePaiement || '—'}
                        </p>
                      </div>
                      <div className="text-right">
                        {hasRefund ? (
                          <>
                            <span className="text-sm text-slate-400 line-through">{fmt(cmd.total)}</span>
                            <span className="text-lg font-bold text-orange-700 ml-1">{fmt(totalNet)}</span>
                          </>
                        ) : (
                          <span className="text-lg font-bold">{fmt(cmd.total)}</span>
                        )}
                        <span className={`block text-xs font-semibold px-2 py-0.5 rounded-full mt-1 ${statutColor}`}>
                          {statutLabel}
                        </span>
                      </div>
                    </div>
                    <ul className="mt-2 text-sm text-slate-600 space-y-1">
                      {(cmd.produits || []).slice(0, 3).map((lp) => (
                        <li key={lp.id}>
                          {lp.produit?.nom} x{lp.quantite}
                        </li>
                      ))}
                      {(cmd.produits || []).length > 3 && (
                        <li className="text-xs text-slate-400">
                          +{cmd.produits.length - 3} autre(s)...
                        </li>
                      )}
                    </ul>
                    <p className="text-xs text-violet-500 mt-2 font-medium">
                      Voir le détail
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {view === MODES.accueil && (
        <div className="max-w-4xl mx-auto p-6">
          <div className="mb-4">
            <PosPosteSessionBar
              poste={posPoste}
              session={session}
              multiPostOperational={posMultiPostOperational}
              singlePosteMode={posSinglePosteMode}
              deviceBlockedCode={posDeviceBlockedCode}
              commercantId={commercantId}
              currentDeviceId={posLocalDeviceId}
              currentDeviceLabel={getDeviceFriendlyLabel(posLocalDeviceId)}
            />
            {posSessionRemoteClosed ? (
              <p
                data-testid="pos-session-remote-closed"
                className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3"
              >
                Session clôturée sur un autre appareil — rechargement en cours…
              </p>
            ) : null}
          </div>
          <div className="rounded-3xl overflow-hidden shadow-xl mb-8 bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 text-white">
            <div className="p-8 flex flex-wrap items-center justify-between gap-6">
              <div>
                <p className="text-emerald-100 text-sm font-medium uppercase tracking-wider">
                  Session active
                </p>
                <p className="text-4xl sm:text-5xl font-bold mt-2 tracking-tight">
                  {fmt(sessionStats?.totalVentes ?? 0)}
                </p>
                <p className="text-emerald-100/90 mt-2">
                  {sessionStats?.ventesCount ?? 0} vente(s) · Devise {devise}
                </p>
              </div>
              <div className="p-4 rounded-2xl bg-white/10 backdrop-blur">
                <Banknote className="h-14 w-14" />
              </div>
            </div>
          </div>

          {error && (
            <p className="text-red-600 text-sm mb-4 bg-red-50 border border-red-100 rounded-xl px-4 py-2">
              {error}
            </p>
          )}

          {posOrphanPanierDraft?.lines?.length > 0 ? (
            <div
              className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-950"
              data-testid="pos-panier-draft-recovery"
            >
              <p className="font-semibold flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                Brouillon d’une session précédente ({posOrphanPanierDraft.lines.length} article
                {posOrphanPanierDraft.lines.length > 1 ? 's' : ''})
              </p>
              <p className="mt-1 text-amber-900/90">
                Ce panier n’est pas rattaché à la session ouverte. Reprenez-le explicitement ou
                abandonnez-le — il ne sera pas encaissé silencieusement.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid="pos-panier-draft-recover"
                  onClick={reprendreBrouillonPanierOrphelin}
                  className="px-4 py-2 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700"
                >
                  Reprendre le brouillon
                </button>
                <button
                  type="button"
                  data-testid="pos-panier-draft-abandon"
                  onClick={abandonnerBrouillonPanierOrphelin}
                  className="px-4 py-2 rounded-xl border border-amber-300 bg-white font-semibold text-amber-950 hover:bg-amber-100"
                >
                  Abandonner
                </button>
              </div>
            </div>
          ) : null}

          {ticketsOuverts.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-orange-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                <PauseCircle className="h-4 w-4" />
                Tickets en attente ({ticketsOuverts.length})
              </h3>
              <div className="space-y-2">
                {ticketsOuverts.map((t) => (
                  <div
                    key={t.id}
                    className="flex items-center bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <p
                        className="font-semibold text-slate-900 truncate"
                        data-testid={`caisse-ticket-ouvert-title-${t.id}`}
                      >
                        {formatCaisseOpenTicketTitle(t)}
                        {(t.hasFideliteIntent || t.rewardIdIntent) && (
                          <span className="ml-2 inline-flex items-center rounded-full bg-violet-100 text-violet-800 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5">
                            Fidélité
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatCaisseOpenTicketSubtitle(t, fmt)}
                        {t.crossPostEligible && t.ticketOriginePoste?.label && (
                          <span className="text-violet-700">
                            {' '}
                            · ouvert au {t.ticketOriginePoste.label}
                          </span>
                        )}
                        {(t.hasFideliteIntent || t.rewardIdIntent) && (
                          <span className="text-violet-600"> · intention récompense</span>
                        )}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => voirDetailTicket(t.id)}
                      className="p-2 rounded-lg hover:bg-orange-100 text-slate-500"
                      title="Voir le détail"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => reprendreTicketOuvert(t)}
                      data-testid={`caisse-reprendre-ticket-${t.id}`}
                      className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 flex items-center gap-1"
                    >
                      <PlayCircle className="h-4 w-4" />
                      Reprendre
                    </button>
                    <button
                      type="button"
                      onClick={() => annulerTicketOuvert(t.id)}
                      className="p-2 rounded-lg hover:bg-red-100 text-red-500"
                      title="Annuler"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {retailCounterDraftOnAccueil?.lines?.length > 0 ? (
            <button
              type="button"
              onClick={resumeRetailCounterSale}
              data-testid="caisse-reprendre-vente-en-cours"
              className="mb-4 w-full text-left p-5 bg-emerald-50 rounded-2xl border-2 border-emerald-300 hover:border-emerald-500 hover:shadow-lg transition-all flex items-center gap-4"
            >
              <div className="w-11 h-11 rounded-xl bg-emerald-600 flex items-center justify-center shrink-0">
                <PlayCircle className="h-6 w-6 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-bold text-emerald-950">Reprendre la vente en cours</h3>
                <p className="text-sm text-emerald-900/80">
                  {retailCounterDraftOnAccueil.lines.length} article
                  {retailCounterDraftOnAccueil.lines.length > 1 ? 's' : ''}
                  {retailCounterDraftOnAccueil.totalEstimate > 0
                    ? ` · ${retailCounterDraftOnAccueil.totalEstimate.toFixed(2).replace('.', ',')} €`
                    : ''}
                </p>
              </div>
            </button>
          ) : null}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              type="button"
              onClick={beginRetailCounterSale}
              data-testid="caisse-nouvelle-vente"
              className="group text-left p-6 bg-white rounded-2xl border-2 border-emerald-100 hover:border-emerald-400 hover:shadow-xl transition-all"
            >
              <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center mb-4 group-hover:bg-emerald-600 transition-colors">
                <Plus className="h-7 w-7 text-emerald-700 group-hover:text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Nouvelle vente</h3>
              <p className="text-sm text-slate-500 mt-1">Catalogue avec photos et détails</p>
            </button>

            {modulePosRestaurantChannels && (
              <>
                <Link
                  to={`/caisse?commercantId=${encodeURIComponent(commercantId)}&posChannel=emporter`}
                  data-testid="caisse-channel-emporter"
                  onClick={() => {
                    loadCatalogue();
                    setView(MODES.vendre);
                  }}
                  className="group text-left p-6 bg-white rounded-2xl border-2 border-orange-100 hover:border-orange-400 hover:shadow-xl transition-all block"
                >
                  <div className="w-12 h-12 rounded-2xl bg-orange-50 flex items-center justify-center mb-4 group-hover:bg-orange-600 transition-colors">
                    <ShoppingBag className="h-7 w-7 text-orange-700 group-hover:text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">À emporter</h3>
                  <p className="text-sm text-slate-500 mt-1">Comptoir · cuisine · retrait client</p>
                </Link>
                <Link
                  to={`/caisse?commercantId=${encodeURIComponent(commercantId)}&posChannel=telephone`}
                  data-testid="caisse-channel-telephone"
                  onClick={() => {
                    loadCatalogue();
                    setView(MODES.vendre);
                  }}
                  className="group text-left p-6 bg-white rounded-2xl border-2 border-sky-100 hover:border-sky-400 hover:shadow-xl transition-all block"
                >
                  <div className="w-12 h-12 rounded-2xl bg-sky-50 flex items-center justify-center mb-4 group-hover:bg-sky-600 transition-colors">
                    <Phone className="h-7 w-7 text-sky-700 group-hover:text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">Téléphone</h3>
                  <p className="text-sm text-slate-500 mt-1">Client identifié ou invité · préparation KDS</p>
                </Link>
              </>
            )}

            {modulePosRestaurant && (
              <Link
                to={`/caisse/restaurant?commercantId=${encodeURIComponent(commercantId)}`}
                state={{ restaurantPlanOnly: true }}
                data-testid="caisse-restaurant-plan"
                className="group text-left p-6 bg-white rounded-2xl border-2 border-orange-100 hover:border-orange-400 hover:shadow-xl transition-all block"
              >
                <div className="w-12 h-12 rounded-2xl bg-orange-100 flex items-center justify-center mb-4 group-hover:bg-orange-600 transition-colors">
                  <UtensilsCrossed className="h-7 w-7 text-orange-700 group-hover:text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">Restaurant · Plan de salle</h3>
                <p className="text-sm text-slate-500 mt-1">Tables, services et additions sur place</p>
              </Link>
            )}

            {modulePosRestaurantKitchen && (
              <Link
                to={`/caisse/kitchen?commercantId=${encodeURIComponent(commercantId)}`}
                data-testid="caisse-kitchen-display"
                className="group text-left p-6 bg-white rounded-2xl border-2 border-slate-800 hover:border-orange-500 hover:shadow-xl transition-all block"
              >
                <div className="w-12 h-12 rounded-2xl bg-slate-900 flex items-center justify-center mb-4 group-hover:bg-orange-600 transition-colors">
                  <ChefHat className="h-7 w-7 text-orange-300 group-hover:text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">Cuisine / Préparation</h3>
                <p className="text-sm text-slate-500 mt-1">File d&apos;attente par poste (KDS)</p>
              </Link>
            )}

            <button
              type="button"
              data-testid="caisse-open-a-encaisser"
              onClick={() => {
                loadAEncaisser();
                setView(MODES.encaisser);
              }}
              className="group relative text-left p-6 bg-white rounded-2xl border-2 border-amber-100 hover:border-amber-400 hover:shadow-xl transition-all"
            >
              {aEncaisserCount > 0 && (
                <span className="absolute top-4 right-4 min-w-[1.5rem] h-7 px-2 flex items-center justify-center rounded-full bg-amber-500 text-white text-sm font-bold">
                  {aEncaisserCount}
                </span>
              )}
              <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center mb-4 group-hover:bg-amber-500 transition-colors">
                <Clock className="h-7 w-7 text-amber-700 group-hover:text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">À encaisser</h3>
              <p className="text-sm text-slate-500 mt-1">Commandes web, RDV, réservations</p>
            </button>

            <button
              type="button"
              data-testid="caisse-open-historique"
              onClick={() => {
                loadHistorique();
                setView(MODES.historique);
              }}
              className="group text-left p-6 bg-white rounded-2xl border-2 border-violet-100 hover:border-violet-400 hover:shadow-xl transition-all"
            >
              <div className="w-12 h-12 rounded-2xl bg-violet-100 flex items-center justify-center mb-4 group-hover:bg-violet-600 transition-colors">
                <History className="h-7 w-7 text-violet-700 group-hover:text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Historique</h3>
              <p className="text-sm text-slate-500 mt-1">Ventes de la session en cours</p>
            </button>

            {posCanRemboursement && (
            <button
              type="button"
              data-testid="caisse-retours-unifies"
              onClick={() => setView(MODES.retoursUnifies)}
              className="group text-left p-6 bg-white rounded-2xl border-2 border-violet-100 hover:border-violet-400 hover:shadow-xl transition-all"
            >
              <div className="w-12 h-12 rounded-2xl bg-violet-100 flex items-center justify-center mb-4 group-hover:bg-violet-600 transition-colors">
                <Search className="h-7 w-7 text-violet-700 group-hover:text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Retours et échanges</h3>
              <p className="text-sm text-slate-500 mt-1">POS, Click &amp; Collect et Web · consultation</p>
            </button>
            )}

            {posCanRemboursement && (
            <button
              type="button"
              data-testid="caisse-retour-retail"
              onClick={() => {
                setRetailReturnUnifiedHandoff(null);
                setView(MODES.retourRetail);
              }}
              className="group text-left p-6 bg-white rounded-2xl border-2 border-fuchsia-100 hover:border-fuchsia-400 hover:shadow-xl transition-all"
            >
              <div className="w-12 h-12 rounded-2xl bg-fuchsia-100 flex items-center justify-center mb-4 group-hover:bg-fuchsia-600 transition-colors">
                <RotateCcw className="h-7 w-7 text-fuchsia-700 group-hover:text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Retour produits</h3>
              <p className="text-sm text-slate-500 mt-1">Retail comptoir · ticket d&apos;origine · remboursement</p>
            </button>
            )}

            {posCanTresorerie && (
            <button
              type="button"
              data-testid="caisse-open-tresorerie"
              onClick={() => {
                loadSession();
                setView(MODES.tresorerie);
              }}
              className="group text-left p-6 bg-white rounded-2xl border-2 border-emerald-100 hover:border-emerald-400 hover:shadow-xl transition-all"
            >
              <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center mb-4 group-hover:bg-emerald-600 transition-colors">
                <Banknote className="h-7 w-7 text-emerald-700 group-hover:text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Trésorerie</h3>
              <p className="text-sm text-slate-500 mt-1">Monnaie, coffre, banque — hors ventes</p>
            </button>
            )}

            {posCanFiscalRead && (
            <button
              type="button"
              data-testid="caisse-open-fiscal-closures"
              onClick={() => {
                loadFiscalClosures();
                setView(MODES.fiscalClosures);
              }}
              className="group text-left p-6 bg-white rounded-2xl border-2 border-teal-100 hover:border-teal-400 hover:shadow-xl transition-all"
            >
              <div className="w-12 h-12 rounded-2xl bg-teal-100 flex items-center justify-center mb-4 group-hover:bg-teal-600 transition-colors">
                <Receipt className="h-7 w-7 text-teal-700 group-hover:text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Clôtures fiscales</h3>
              <p className="text-sm text-slate-500 mt-1">Jour / mois / année — totaux protégés</p>
            </button>
            )}

            {posCanFiscalRead && (
            <button
              type="button"
              data-testid="caisse-open-fiscal-documents"
              onClick={() => setView(MODES.fiscalDocuments)}
              className="group text-left p-6 bg-white rounded-2xl border-2 border-violet-100 hover:border-violet-400 hover:shadow-xl transition-all"
            >
              <div className="w-12 h-12 rounded-2xl bg-violet-100 flex items-center justify-center mb-4 group-hover:bg-violet-600 transition-colors">
                <FileText className="h-7 w-7 text-violet-700 group-hover:text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Additions fiscales</h3>
              <p className="text-sm text-slate-500 mt-1">Consulter, corriger ou annuler</p>
            </button>
            )}

            {posCanSession && (
            <button
              type="button"
              data-testid="caisse-open-cloture"
              onClick={async () => {
                setFermetureWarning(null);
                setMotifEcartCaisse('');
                setForceCloseMotif('');
                const data = await loadSession();
                const r = data?.recap;
                const fond = round2(r?.fondDeCaisse ?? 0);
                const attendu = round2(
                  r?.especesEnCaisse ?? r?.totalEspeces ?? fond
                );
                setEspecesComptees(suggestEspecesCompteesInput(attendu));
                setRestaurantCloseWarning(null);
                try {
                  const preview = await apiService.getCaisseSessionCloseOperationalPreview(
                    commercantId
                  );
                  setCloseOperationalPreview(preview);
                } catch {
                  setCloseOperationalPreview(null);
                }
                setView(MODES.cloture);
              }}
              className="sm:col-span-2 group text-left p-6 bg-white rounded-2xl border-2 border-slate-100 hover:border-slate-300 hover:shadow-lg transition-all flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center group-hover:bg-slate-700 transition-colors shrink-0">
                <Lock className="h-6 w-6 text-slate-600 group-hover:text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Clôturer la caisse</h3>
                <p className="text-sm text-slate-500">Fin de journée et rapport</p>
              </div>
            </button>
            )}
          </div>
        </div>
      )}

      {showTicketDetail && ticketDetail && (() => {
        const isOpen = ticketDetail.statut === 'EN_ATTENTE' && ticketDetail.statutPaiement === 'EN_ATTENTE';
        const isPaid = ticketDetail.statutPaiement === 'VALIDE' && ticketDetail.statut !== 'ANNULEE';
        const isCancelled = ticketDetail.statut === 'ANNULEE';
        const statutLabel = isCancelled ? 'Annulé' : isOpen ? 'En attente' : 'Terminé';
        const statutColor = isCancelled ? 'text-red-600' : isOpen ? 'text-orange-600' : 'text-emerald-600';
        const paiementLabel = ticketDetail.statutPaiement === 'VALIDE' ? 'Payé'
          : ticketDetail.statutPaiement === 'REMBOURSE' ? 'Remboursé'
          : ticketDetail.statutPaiement === 'EN_ATTENTE' ? 'Non payé'
          : ticketDetail.statutPaiement;

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={dismissTicketDetailOverlay}>
            <div
              className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto"
              data-testid="caisse-ticket-detail-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between z-10">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">
                    {ticketDetail.numeroTicket || ticketDetail.numero || ticketDetail.id.slice(-8)}
                  </h3>
                  <p className="text-xs text-slate-400">
                    {new Date(ticketDetail.dateCommande).toLocaleString('fr-FR')}
                  </p>
                </div>
                <button type="button" onClick={dismissTicketDetailOverlay} className="p-1.5 rounded-lg hover:bg-slate-100">
                  <X className="h-5 w-5 text-slate-500" />
                </button>
              </div>

              {isCancelled && (
                <div className="bg-red-50 border-b border-red-200 px-6 py-2 text-center">
                  <span className="text-sm font-bold text-red-700">TICKET ANNULÉ</span>
                </div>
              )}

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 rounded-xl px-4 py-3">
                    <p className="text-xs text-slate-500">Statut</p>
                    <p className={`font-semibold ${statutColor}`}>{statutLabel}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl px-4 py-3">
                    <p className="text-xs text-slate-500">Paiement</p>
                    <p className="font-semibold text-slate-900">{paiementLabel}</p>
                  </div>
                  {ticketDetail.methodePaiement && ticketDetail.statutPaiement !== 'EN_ATTENTE' && (
                    <div className="bg-slate-50 rounded-xl px-4 py-3">
                      <p className="text-xs text-slate-500">Méthode</p>
                      <p className="font-semibold text-slate-900">{ticketDetail.methodePaiement}</p>
                    </div>
                  )}
                  {ticketDetail.user && (
                    <div className="bg-slate-50 rounded-xl px-4 py-3">
                      <p className="text-xs text-slate-500">Client</p>
                      <p className="font-semibold text-slate-900 truncate">{ticketDetail.user.prenom} {ticketDetail.user.nom}</p>
                    </div>
                  )}
                </div>

                {ticketFideliteDisplay?.phase && (
                  <div
                    className={`rounded-xl px-4 py-3 border ${
                      ticketFideliteDisplay.phase === 'intent'
                        ? 'bg-violet-50 border-violet-200'
                        : 'bg-emerald-50 border-emerald-200'
                    }`}
                  >
                    <p className="text-xs font-semibold uppercase tracking-wider mb-1 text-slate-600">
                      {ticketFideliteDisplay.phase === 'intent'
                        ? 'Intention fidélité'
                        : 'Récompense appliquée'}
                    </p>
                    <ul className="space-y-0.5">
                      {(ticketFideliteDisplay.lines || []).map((line) => (
                        <li
                          key={line}
                          className={`text-sm ${
                            ticketFideliteDisplay.phase === 'intent'
                              ? 'text-violet-900'
                              : 'text-emerald-900'
                          }`}
                        >
                          {line}
                        </li>
                      ))}
                    </ul>
                    {ticketFideliteDisplay.phase === 'intent' && (
                      <p className="text-xs text-violet-700 mt-2">
                        Pour remplacer ou retirer : reprendre le ticket, puis choisir une autre récompense ou « Conserver les points ».
                      </p>
                    )}
                  </div>
                )}

                {(() => {
                  const encaissements = (ticketDetail.paiements || []).filter((p) => p.montant >= 0);
                  const remboursements = (ticketDetail.paiements || []).filter((p) => p.montant < 0);
                  const totalRembourse = remboursements.reduce((s, p) => s + Math.abs(p.montant), 0);
                  const totalNet = ticketDetail.total - totalRembourse;
                  const refundedByLine = aggregateRefundedQtyByLine(ticketDetail.paiements, ticketDetail.produits);
                  const hasRefunds = remboursements.length > 0;
                  const soldeRemboursable = ticketDetail.total - totalRembourse;

                  return (
                    <>
                      <div className="border-t border-slate-100 pt-4">
                        <h4 className="text-sm font-semibold text-slate-700 mb-3">Articles ({(ticketDetail.produits || []).length})</h4>
                        <div className="space-y-2">
                          {(ticketDetail.produits || []).map((lp) => {
                            const lineRefund = buildHistoryLineRefundDisplay(lp, refundedByLine);
                            const strikeAll = lineRefund.isFullyRefunded;
                            return (
                              <div key={lp.id} className={`flex justify-between items-start gap-2 rounded-lg px-3 py-2 ${lineRefund.rembourses > 0 ? 'bg-orange-50 border border-orange-200' : 'bg-slate-50'}`}>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-medium ${strikeAll ? 'text-orange-400 line-through' : 'text-slate-900'}`}>{lp.produit?.nom || 'Produit'}</p>
                                  {lineRefund.isPartiallyRefunded && (
                                    <p className="text-xs text-orange-700 mt-0.5">
                                      Vendus {lineRefund.vendus} · remboursés {lineRefund.rembourses} · conservés {lineRefund.conserves}
                                    </p>
                                  )}
                                  {lineRefund.isFullyRefunded && (
                                    <span className="text-xs font-semibold text-orange-600">Entièrement remboursé</span>
                                  )}
                                  {lp.commentaire && <p className="text-xs text-slate-400 whitespace-pre-line mt-0.5">{lp.commentaire}</p>}
                                  {lp.poidsSouhaite && <p className="text-xs text-slate-400">{lp.poidsSouhaite}g{lp.typeDecoupe ? ` · ${lp.typeDecoupe}` : ''}</p>}
                                </div>
                                <div className="text-right shrink-0">
                                  <p className="text-sm text-slate-500">x{lineRefund.vendus}</p>
                                  <p className={`text-sm font-bold ${strikeAll ? 'text-orange-400 line-through' : ''}`}>{fmt(lp.prixTotal || lp.prixFinal || lp.prixUnitaire * lp.quantite)}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {encaissements.length > 0 && (
                        <div className="border-t border-slate-100 pt-4">
                          <h4 className="text-sm font-semibold text-slate-700 mb-3">Encaissements</h4>
                          <div className="space-y-1.5">
                            {encaissements.map((p) => (
                              <div key={p.id} className="flex justify-between text-sm bg-emerald-50 rounded-lg px-3 py-2">
                                <span className="text-slate-700 font-medium">{p.methodePaiement}</span>
                                <span className="font-bold text-emerald-700">{fmt(p.montant)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {hasRefunds && (
                        <div className="border-t border-orange-200 pt-4">
                          <h4 className="text-sm font-semibold text-orange-700 mb-3 flex items-center gap-1.5">
                            <RotateCcw className="h-4 w-4" />
                            Remboursements ({remboursements.length})
                          </h4>
                          <div className="space-y-1.5">
                            {remboursements.map((p) => {
                              let savMeta = null;
                              try { savMeta = typeof p.reference === 'string' ? JSON.parse(p.reference) : p.reference; } catch {}
                              const typeLabel = savMeta?.type === 'REMBOURSEMENT_COMPLET' ? 'Complet'
                                : savMeta?.type === 'REMBOURSEMENT_ARTICLES' ? 'Par articles'
                                : savMeta?.type === 'REMBOURSEMENT_LIBRE' ? 'Montant libre'
                                : 'Remboursement';
                              return (
                                <div key={p.id} className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                                  <div className="flex justify-between text-sm">
                                    <span className="text-orange-800 font-medium">{typeLabel} · {p.methodePaiement}</span>
                                    <span className="font-bold text-orange-700">-{fmt(Math.abs(p.montant))}</span>
                                  </div>
                                  {savMeta?.motif && (
                                    <p className="text-xs text-orange-600 mt-1">Motif : {savMeta.motif}</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <div className="border-t border-slate-200 pt-4 space-y-1">
                        {ticketFideliteDisplay?.phase === 'applied' &&
                          ticketFideliteDisplay.reductionEur > 0 && (
                          <div className="flex justify-between items-baseline text-sm">
                            <span className="text-emerald-700">
                              Fidélité{ticketFideliteDisplay.libelle ? ` · ${ticketFideliteDisplay.libelle}` : ''}
                              {ticketFideliteDisplay.points != null
                                ? ` (${ticketFideliteDisplay.points} pts)`
                                : ''}
                            </span>
                            <span className="font-semibold text-emerald-700">
                              −{fmt(ticketFideliteDisplay.reductionEur)}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between items-baseline">
                          <span className="text-lg font-bold text-slate-900">Total</span>
                          <span className={`text-2xl font-bold ${isCancelled ? 'text-red-500 line-through' : hasRefunds ? 'text-slate-400 line-through text-lg' : 'text-emerald-600'}`}>{fmt(ticketDetail.total)}</span>
                        </div>
                        {hasRefunds && (
                          <>
                            <div className="flex justify-between items-baseline">
                              <span className="text-sm text-orange-600">Remboursé</span>
                              <span className="text-sm font-bold text-orange-600">-{fmt(totalRembourse)}</span>
                            </div>
                            <div className="flex justify-between items-baseline">
                              <span className="text-lg font-bold text-slate-900">Total net</span>
                              <span className={`text-2xl font-bold ${totalNet <= 0 ? 'text-red-500' : 'text-emerald-600'}`}>{fmt(totalNet)}</span>
                            </div>
                          </>
                        )}
                      </div>

                      <div className="border-t border-slate-100 pt-4 space-y-2">
                        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Actions</h4>

                        <CaisseCommandeFactureAvoirActions
                          commandeId={ticketDetail.id}
                          numeroFacture={ticketDetail.numeroFacture}
                          avoirs={ticketDetail.avoirs}
                          factureAvailable={isFactureTelechargeable(ticketDetail)}
                          factureLoading={ticketFactureLoading}
                          onDownloadFacture={downloadTicketFacture}
                          onDownloadAvoir={downloadTicketAvoir}
                          avoirLoadingId={ticketAvoirLoadingId}
                          formatMoney={fmt}
                          testIdPrefix="caisse-historique-ticket"
                        />

                        {isOpen && (
                          <div className="flex gap-2">
                            <button type="button" onClick={() => { setShowTicketDetail(false); reprendreTicket(ticketDetail); }}
                              className="flex-1 py-3 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 flex items-center justify-center gap-2">
                              <PlayCircle className="h-5 w-5" />
                              Reprendre et encaisser
                            </button>
                            <button type="button" onClick={() => { annulerTicketOuvert(ticketDetail.id); setShowTicketDetail(false); }}
                              className="py-3 px-4 bg-red-100 text-red-700 font-semibold rounded-xl hover:bg-red-200 flex items-center gap-1">
                              <XCircle className="h-4 w-4" /> Annuler
                            </button>
                          </div>
                        )}

                        <div className="flex gap-2">
                          <button
                            type="button"
                            data-testid="caisse-duplicata-open"
                            onClick={() => imprimerDuplicata(ticketDetail, ticketFideliteDisplay)}
                            className="flex-1 py-2.5 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200 flex items-center justify-center gap-2"
                          >
                            <Printer className="h-4 w-4" /> Imprimer duplicata
                          </button>
                          <button type="button" onClick={() => {
                            const fidLines = (ticketFideliteDisplay?.lines || []).join('\n');
                            const text = `${ticketDetail.numeroTicket || ticketDetail.numero}\n${new Date(ticketDetail.dateCommande).toLocaleString('fr-FR')}\n${(ticketDetail.produits || []).map(lp => `${lp.produit?.nom} x${lp.quantite} ${fmt(lp.prixTotal || lp.prixUnitaire * lp.quantite)}`).join('\n')}${fidLines ? `\n${fidLines}` : ''}\nTotal: ${fmt(ticketDetail.total)}`;
                            navigator.clipboard?.writeText(text); showToast('Copié dans le presse-papier', 'info');
                          }}
                            className="py-2.5 px-4 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200 flex items-center gap-1">
                            <Copy className="h-4 w-4" />
                          </button>
                        </div>

                        {posCanRemboursement && isPaid && soldeRemboursable > 0.01 && (
                          <button type="button" onClick={ouvrirSavModal}
                            className="w-full py-3 bg-orange-50 text-orange-700 font-semibold rounded-xl hover:bg-orange-100 border border-orange-200 flex items-center justify-center gap-2">
                            <RotateCcw className="h-4 w-4" /> Rembourser
                          </button>
                        )}

                        {isPaid && (
                          <button type="button" onClick={() => annulerTicketPaye(ticketDetail.id)}
                            className="w-full py-2.5 bg-red-50 text-red-700 font-semibold rounded-xl hover:bg-red-100 border border-red-200 flex items-center justify-center gap-2">
                            <XCircle className="h-4 w-4" /> Annuler ce ticket (avec motif)
                          </button>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        );
      })()}

      {showSavModal && ticketDetail && (() => {
        const remboursementsDeja = (ticketDetail.paiements || []).filter((p) => p.montant < 0);
        const totalRembourse = remboursementsDeja.reduce((s, p) => s + Math.abs(p.montant), 0);
        const soldeRemboursable = ticketDetail.total - totalRembourse;
        const refundedByLineSav = aggregateRefundedQtyByLine(ticketDetail.paiements, ticketDetail.produits);
        const lignesDisponibles = (ticketDetail.produits || []).filter(
          (lp) => quantiteRetournablePourLigne(lp, refundedByLineSav) > 0
        );
        const selectedTotal = lignesDisponibles.reduce((s, lp) => {
          const qty = savLineQty[lp.id] || 0;
          if (qty <= 0) return s;
          const unitPrice = (lp.prixTotal || lp.prixFinal || lp.prixUnitaire * lp.quantite) / lp.quantite;
          return s + unitPrice * qty;
        }, 0);
        const allArticlesRefunded = !hasAnySavArticleReturnable(ticketDetail.produits, ticketDetail.paiements);
        const hasSelectedLines = Object.values(savLineQty).some((q) => q > 0);
        const montantPreview = savTab === 'complet' ? soldeRemboursable
          : savTab === 'articles' ? selectedTotal
          : (parseFloat(savMontantLibre) || 0);
        const isValid = savTab === 'complet' ? true
          : savTab === 'articles' ? hasSelectedLines
          : parseFloat(savMontantLibre) > 0;

        return (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={() => setShowSavModal(false)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-white border-b border-orange-200 px-6 py-4 flex items-center justify-between z-10">
                <div className="flex items-center gap-2">
                  <RotateCcw className="h-5 w-5 text-orange-600" />
                  <h3 className="text-lg font-bold text-slate-900">Remboursement</h3>
                </div>
                <button type="button" onClick={() => setShowSavModal(false)} className="p-1.5 rounded-lg hover:bg-slate-100">
                  <X className="h-5 w-5 text-slate-500" />
                </button>
              </div>

              <div className="bg-orange-50 border-b border-orange-200 px-6 py-2 flex justify-between text-sm">
                <span className="text-orange-700">Solde remboursable</span>
                <span className="font-bold text-orange-800">{fmt(soldeRemboursable)}</span>
              </div>

              <div className="px-6 pt-4">
                <div className="flex bg-slate-100 rounded-xl p-1 gap-1">
                  {[
                    { key: 'articles', label: 'Par article' },
                    { key: 'libre', label: 'Montant libre' },
                    { key: 'complet', label: 'Complet' },
                  ].map((tab) => (
                    <button key={tab.key} type="button"
                      onClick={() => setSavTab(tab.key)}
                      className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-colors ${savTab === tab.key ? 'bg-white text-orange-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-6 space-y-4">
                {savTab === 'articles' && (
                  <div className="space-y-2">
                    {allArticlesRefunded || lignesDisponibles.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-4">Tous les articles ont déjà été remboursés</p>
                    ) : lignesDisponibles.map((lp) => {
                      const qtySelected = savLineQty[lp.id] || 0;
                      const isActive = qtySelected > 0;
                      const maxReturnable = quantiteRetournablePourLigne(lp, refundedByLineSav);
                      const unitPrice = (lp.prixTotal || lp.prixFinal || lp.prixUnitaire * lp.quantite) / lp.quantite;
                      const toggleLine = () => {
                        setSavLineQty((prev) => ({
                          ...prev,
                          [lp.id]: isActive ? 0 : Math.min(1, maxReturnable),
                        }));
                      };
                      return (
                        <div
                          key={lp.id}
                          role="button"
                          tabIndex={0}
                          aria-pressed={isActive}
                          onClick={toggleLine}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleLine();
                            }
                          }}
                          className={`rounded-xl px-4 py-3 border-2 transition-colors cursor-pointer touch-manipulation min-h-[52px] ${isActive ? 'border-orange-400 bg-orange-50 ring-2 ring-orange-200' : 'border-slate-200 hover:border-orange-200'}`}
                        >
                          <div className="flex items-center gap-3 pointer-events-none">
                            <span
                              className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 ${isActive ? 'bg-orange-500 border-orange-500' : 'border-slate-300 bg-white'}`}
                              aria-hidden
                            >
                              {isActive && <CheckCircle className="h-4 w-4 text-white" />}
                            </span>
                            <div className="flex-1 min-w-0 text-left">
                              <p className="text-sm font-medium text-slate-900">{lp.produit?.nom || 'Produit'}</p>
                              <p className="text-xs text-slate-500">
                                {fmt(unitPrice)} / unité · {maxReturnable} remboursable{maxReturnable > 1 ? 's' : ''}
                              </p>
                            </div>
                            <span className="text-sm font-bold text-slate-700">{fmt(unitPrice * qtySelected)}</span>
                          </div>
                          {isActive && maxReturnable > 1 && (
                            <div
                              className="flex items-center justify-between mt-2 pt-2 border-t border-orange-200 pointer-events-auto"
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              <span className="text-xs text-orange-700">Quantité à rembourser</span>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  aria-label="Diminuer la quantité"
                                  onClick={() => setSavLineQty((prev) => ({ ...prev, [lp.id]: Math.max(1, qtySelected - 1) }))}
                                  className="w-10 h-10 rounded-lg bg-orange-200 text-orange-800 font-bold flex items-center justify-center hover:bg-orange-300 touch-manipulation"
                                >
                                  <Minus className="h-4 w-4" />
                                </button>
                                <span className="text-sm font-bold text-orange-800 min-w-[4.5rem] text-center">{qtySelected} / {maxReturnable}</span>
                                <button
                                  type="button"
                                  aria-label="Augmenter la quantité"
                                  onClick={() => setSavLineQty((prev) => ({ ...prev, [lp.id]: Math.min(maxReturnable, qtySelected + 1) }))}
                                  className="w-10 h-10 rounded-lg bg-orange-200 text-orange-800 font-bold flex items-center justify-center hover:bg-orange-300 touch-manipulation"
                                >
                                  <Plus className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {savTab === 'libre' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Montant à rembourser</label>
                    <div className="relative">
                      <input type="number" step="0.01" min="0.01" max={soldeRemboursable}
                        value={savMontantLibre} onChange={(e) => setSavMontantLibre(e.target.value)}
                        placeholder="0.00" className="w-full text-2xl font-bold text-center border-2 border-slate-200 rounded-xl px-4 py-4 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none"
                        autoFocus />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-lg text-slate-400">€</span>
                    </div>
                    {parseFloat(savMontantLibre) > soldeRemboursable && (
                      <p className="text-xs text-red-500 mt-1">Dépasse le solde remboursable ({fmt(soldeRemboursable)})</p>
                    )}
                  </div>
                )}

                {savTab === 'complet' && (
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center">
                    <p className="text-sm text-orange-700">Remboursement intégral du solde restant</p>
                    <p className="text-3xl font-bold text-orange-800 mt-2">{fmt(soldeRemboursable)}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Motif (optionnel)</label>
                  <input type="text" value={savMotif} onChange={(e) => setSavMotif(e.target.value)}
                    placeholder="Ex: Article défectueux, geste commercial..."
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Mode de remboursement</label>
                  <div className="flex gap-2">
                    {['ESPECES', 'CARTE'].map((m) => (
                      <button key={m} type="button" onClick={() => setSavMethode(m)}
                        className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition-colors ${savMethode === m ? 'border-orange-400 bg-orange-50 text-orange-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                        {m === 'ESPECES' ? 'Espèces' : 'Carte'}
                      </button>
                    ))}
                  </div>
                </div>

                {montantPreview > 0 && (
                  <div className="bg-slate-50 rounded-xl px-4 py-3 flex justify-between items-baseline">
                    <span className="text-sm text-slate-600">Montant remboursé</span>
                    <span className="text-xl font-bold text-orange-700">{fmt(montantPreview)}</span>
                  </div>
                )}
              </div>

              <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 flex gap-3">
                <button type="button" onClick={() => setShowSavModal(false)}
                  className="flex-1 py-3 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200">
                  Annuler
                </button>
                <button type="button" onClick={handleRemboursement}
                  disabled={!isValid || savLoading || montantPreview <= 0 || montantPreview > soldeRemboursable + 0.01}
                  className="flex-1 py-3 bg-orange-600 text-white font-semibold rounded-xl hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                  <RotateCcw className="h-4 w-4" />
                  {savLoading ? 'Traitement...' : 'Confirmer'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
